import React, { useEffect, useRef, useState, memo, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useChat } from '@/hooks/useChat';
import ClarifyForm from './ClarifyForm';
import { useClient } from '@/context/ClientContext';
import { useAI } from '@/context/AIContext';
import { useAuth } from '@/context/AuthContext';
import ChatInputV2, { type ChatInputAttachment } from '@/components/chat/ChatInputV2';
import ArticleMessageComponent from '@/components/chat/ArticleMessage';
import { ClarificationAckNotice } from '@/components/chat/ClarificationAckNotice';
import { ArtifactReferenceCard } from '@/components/chat/ArtifactReferenceCard';
import { MemoryPill } from '@/components/chat/MemoryPill';
import AgentHandoffAnimation from '@/components/chat/AgentHandoffAnimation';
import type { ClarificationSubmissionMeta, ClarifyType } from '@/lib/chat/clarify';
const RealTimeLearningStats = dynamic(
  () => import('./RealTimeLearningStats').then(m => m.RealTimeLearningStats),
  { ssr: false, loading: () => <div className="text-sm text-muted-foreground p-2">Loading…</div> }
);
import { MigrationTrigger } from './MigrationTrigger';
import { LearningSuggestion } from './LearningSuggestion';
import type { WritingSuggestion, PRContentType } from '@/components/tools/WritingAssistant';
const WritingAssistant = dynamic(() => import('@/components/tools/WritingAssistant'), { ssr: false });
const ContentArtifact = dynamic(() => import('./ContentArtifact'), { ssr: false, loading: () => <div className="text-sm text-muted-foreground p-3">Loading artifact…</div> });
import type { ArtifactContent } from './ContentArtifact';
import { useArtifactsBridge } from '@/context/ArtifactsContext';
import { detectArtifactContent, isSubstantialContent, detectUpdateIntent, calculateContentSimilarity } from '@/lib/artifactDetection';
import { LiquidGlassCard, LiquidGlassMessage } from '@/components/liquid-glass/LiquidGlassCard';
import { cn, sanitizeArtifactTitle, stripMarkdown } from '@/lib/utils';
import { InteractionFeedback } from '@/types';
import { toast, confirm, alert } from '@/lib/notifications';
import { ClarificationsDrawer } from './ClarificationsDrawer';
import { ReviewerCallout } from './ReviewerCallout';
import { ReviewerDetailsDrawer } from './ReviewerDetailsDrawer';
import { AgentRunCard } from '@/components/agents/AgentRunCard';
import { ArtifactGraphCompanionPanel } from '@/components/agents/ArtifactGraphCompanionPanel';
import { NeedsInputTray } from '@/components/agents/NeedsInputTray';
import { DecisionTracePanel, RunDetailsPanel } from '@/components/agents/DecisionTracePanel';
import { WhatAgentKnowsPanel } from '@/components/agents/WhatAgentKnowsPanel';
import { RuntimeToolResultPanel } from '@/components/agent-tools/RuntimeToolResultPanel';
import { PreferenceChips } from './PreferenceChips';
import { PreferenceConfirmBanner } from './PreferenceConfirmBanner';
import { PreferencesDrawer } from './PreferencesDrawer';
import { PreferenceToggle } from './PreferenceToggle';
import { LearningEntryPrompt } from './LearningEntryPrompt';
import type { ReviewerFeedback } from '@/types/reviewer';
import type { ClientPreferences } from '@/types/preferences';
import type { LearningPrompt } from '@/types/learning';
import { buildAgentRunDisplayFromMessage } from '@/lib/agentRunDisplay';
import { aggregatePendingInputItems, type PendingInputAction, type PendingInputItem } from '@/lib/agentPendingInput';
import {
  buildDecisionTraceFromMessage,
  buildRunDetailsFromMessage,
} from '@/lib/agentDecisionTrace';
import { shouldShowAgentDiagnostics } from '@/lib/agentDiagnostics';
import { buildAgentKnowledgeDisplay } from '@/lib/agentKnowledgeDisplay';
import { runToolAsAgent } from '@/lib/agent/actions';
// PHASE-02 START
import { contentManagement } from '@/lib/contentManagement';
import { MemoryReference, PolicyIssue, RunMeta } from '@/types';
// PHASE-02 END
import {
  buildPreferenceDiffPayload,
  createLearningEntry,
  syncFeedbackTextToGraph,
  syncPreferenceExtractionFeedbackToGraph,
  syncPreferenceExtractionToGraph,
} from '@/lib/learningLedger';
import { trackPreferenceMetric } from '@/lib/preferencesAnalytics';
import { listChatQuickActions, type ChatQuickAction, type ChatQuickActionKind } from '@/lib/runtimeTools/chatQuickActions';
import type { RuntimeToolArtifactType } from '@/lib/runtimeTools/manifest';
import type { RuntimeToolRunAgUiState } from '@/lib/runtimeTools/toolRuns';
import { resolveArtifactCheckpointId } from '@/lib/contentArtifacts/approvalReadiness';
import { 
  SparklesIcon, 
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  XMarkIcon,
  DocumentTextIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  LockClosedIcon,
  CloudArrowUpIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
// Removed SEO/Insights/Analytics right-panel UI
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { Popover } from '@/components/ui/Popover';
import { DropdownItem } from '@/components/ui/DropdownMenu';

const SHOW_AGENT_DIAGNOSTICS = shouldShowAgentDiagnostics();

const HANDOFF_STATUS_MESSAGES = new Set([
  '',
  'ℹ️ Clarifications requested — please use the form below.',
  'Queued for processing…',
  'Agent run in progress…',
  'Working on your request…',
  'Working on your request...',
  'Working on your clarifications…',
  'Continuing with placeholders…',
]);

type HandoffMessageCandidate = {
  id?: string;
  sender?: string;
  content?: string;
  text?: string;
  isLoading?: boolean;
  agentHandoffData?: {
    selectedAgent: React.ComponentProps<typeof AgentHandoffAnimation>['selectedAgent'];
    phase: React.ComponentProps<typeof AgentHandoffAnimation>['phase'];
    processingSteps: string[];
    statusText?: string;
    lastHandoff?: React.ComponentProps<typeof AgentHandoffAnimation>['lastHandoff'];
  };
  meta?: {
    runId?: string;
    pendingStatus?: string;
  };
};

function getMessageRunKey(message: HandoffMessageCandidate | null | undefined): string {
  const metaRunId = typeof message?.meta?.runId === 'string' ? message.meta.runId.trim() : '';
  if (metaRunId) return metaRunId;
  const messageId = typeof message?.id === 'string' ? message.id.trim() : '';
  return messageId || '';
}

function isActiveHandoffMessage(message: HandoffMessageCandidate | null | undefined): boolean {
  if (!message || message.sender !== 'ai' || !message.agentHandoffData) return false;
  const pendingStatus = typeof message?.meta?.pendingStatus === 'string'
    ? message.meta.pendingStatus
    : '';
  const text = String(message.content || message.text || '').trim();
  return (
    message.isLoading === true
    || pendingStatus === 'queued'
    || pendingStatus === 'in_progress'
    || pendingStatus === 'needs_clarification'
    || text === 'ℹ️ Clarifications requested — please use the form below.'
  );
}

function isHandoffStatusOnlyMessage(message: HandoffMessageCandidate | null | undefined): boolean {
  const text = String(message?.content || message?.text || '').trim();
  return HANDOFF_STATUS_MESSAGES.has(text);
}

interface ChatWindowProps {
  messages?: any[];
  loading?: boolean;
  disableRightSidebar?: boolean;
  renderMode?: 'default' | 'article';
  fullWidth?: boolean; // stretch content to available width (preview)
  handoffArtifactId?: string;
  handoffActive?: boolean;
  onHandoffConsumed?: () => void;
}

// Chat message skeleton loader
const MessageSkeleton = memo(() => (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex gap-3 animate-pulse">
        <div className="w-8 h-8 bg-muted rounded-full"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="space-y-1">
            <div className="h-3 bg-muted rounded"></div>
            <div className="h-3 bg-muted rounded w-3/4"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
));

MessageSkeleton.displayName = 'MessageSkeleton';

// Empty state imported from standalone component
import { EmptyState } from '@/components/chat/EmptyState';

type ClarificationHistoryProps = {
  message: {
    id?: string;
    clarification?: ClarificationSubmissionMeta;
    timestamp?: any;
    messageKind?: string;
  };
};

function isRequestFailedStatus(error: unknown, status: number): boolean {
  return error instanceof Error && error.message === `request_failed_${status}`;
}

const ClarificationHistoryBlock = memo(({ message }: ClarificationHistoryProps) => {
  const details = message.clarification;
  const [open, setOpen] = useState(false);
  if (!details) return null;
  const answeredCount = details.answers?.filter((ans) => ans && ans.trim() && ans.trim() !== '[placeholder]').length || 0;
  const totalQuestions = details.questions?.length || 0;
  const summary = details.skipped
    ? 'Skipped by user'
    : `${answeredCount}/${totalQuestions} answered`;
  const timestamp = (() => {
    try {
      if (!message.timestamp) return '';
      const date = message.timestamp.toDate ? message.timestamp.toDate() : new Date(message.timestamp);
      if (Number.isNaN(date?.getTime())) return '';
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  })();
  return (
    <div className="rounded-2xl border border-info/40 bg-info/10 p-4 shadow-inner shadow-info/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-foreground">Clarification</p>
          <p className="text-sm text-foreground">
            {details.type || 'draft'} · {summary}{timestamp ? ` · ${timestamp}` : ''}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="text-xs px-3 py-1" onClick={() => setOpen((prev) => !prev)}>
          {open ? 'Hide details' : 'View details'}
        </Button>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          {details.questions.map((question, idx) => {
            const answer = (details.answers && details.answers[idx]) || '';
            const isPlaceholder = !answer || answer.trim() === '' || answer.trim() === '[placeholder]';
            return (
              <div key={`${details.requestId}-${idx}`} className="rounded-lg bg-background/40 border border-border/30 p-3">
                <p className="text-xs font-medium text-foreground">{question}</p>
                <p className={cn('mt-2 text-sm whitespace-pre-wrap', isPlaceholder ? 'text-muted-foreground italic' : 'text-foreground')}>
                  {isPlaceholder ? 'Placeholder accepted' : answer}
                </p>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-3">
        {details.skipped ? 'Clarification skipped via follow-up form.' : 'Clarification sent via follow-up form.'}
      </p>
    </div>
  );
});
ClarificationHistoryBlock.displayName = 'ClarificationHistoryBlock';

const ClarificationResumeBanner = ({
  agentLabel,
  pendingCount,
  onResume,
  onDismiss,
}: {
  agentLabel?: string | null;
  pendingCount: number;
  onResume: () => void;
  onDismiss: () => void;
}) => (
  <div className="rounded-2xl border border-warning/40 bg-warning/15 p-4 flex flex-wrap items-center justify-between gap-3">
    <div>
      <p className="text-sm font-medium text-foreground">Resume pending clarification</p>
      <p className="text-xs text-muted-foreground">
        {agentLabel || 'Writer'} still needs details · {pendingCount} pending
      </p>
    </div>
    <div className="flex gap-2">
      <Button variant="ghost" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
      <Button size="sm" className="bg-warning text-warning-foreground hover:bg-warning/90" onClick={onResume}>
        Resume
      </Button>
    </div>
  </div>
);
ClarificationResumeBanner.displayName = 'ClarificationResumeBanner';

type PreferenceField = 'spokesperson' | 'mediaContact' | 'tone' | 'styleNotes' | 'locale' | null;
type PreferenceSuggestion = {
  updates: Partial<ClientPreferences>;
  source: string;
  rawConfidence?: number;
  calibratedConfidence?: number;
  runId?: string;
  messageId?: string;
  keys?: string[];
  diffChars?: number;
  explanation?: string;
};

type SourceDocumentAttachment = {
  id: string;
  title: string;
  type?: string;
  url?: string;
  status?: string;
};

type ServerContentArtifactRecord = {
  id: string;
  clientId?: string;
  title?: string;
  content?: string;
  type?: string;
  status?: string;
  reviewState?: string;
  updatedAt?: string | null;
  createdAt?: string | null;
  version?: number;
  sourceConversationId?: string | null;
  artifactKey?: string;
  lineageId?: string;
  checkpointId?: string | null;
  runId?: string | null;
  approvalEnvelopes?: ArtifactContent['approvalEnvelopes'];
  citationPackV1?: ArtifactContent['citationPackV1'];
  pressRelease?: ArtifactContent['pressRelease'];
  reviewerFeedback?: ArtifactContent['reviewerFeedback'];
  review?: Record<string, unknown> | null;
  metadata?: Record<string, unknown>;
  policyIssues?: Array<Record<string, unknown>>;
};

type ContentArtifactsListResponse = {
  ok?: boolean;
  artifacts?: ServerContentArtifactRecord[];
};

type ContentArtifactResponse = {
  ok?: boolean;
  artifact?: ServerContentArtifactRecord;
};

type ClientDocumentsResponse = {
  ok?: boolean;
  documents?: unknown[];
};

type ArtifactFeedbackAction = 'approve' | 'request_changes' | 'discuss' | 'open' | 'free_text';

type ArtifactFeedbackLinkage = {
  artifactId: string;
  lineageId?: string;
  runId?: string;
  promptMessageId?: string;
};

type ClarifySubmissionPendingState = {
  requestId: string;
  startedAt: number;
  timedOut?: boolean;
};

const CLARIFY_PENDING_HEALTH_TIMEOUT_MS = 15000;

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function parseDate(value: unknown): Date {
  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

function mapArtifactType(rawType: unknown): ArtifactContent['type'] {
  const normalized = normalizeString(rawType).toLowerCase();
  if (normalized === 'press_release') return 'press_release';
  if (normalized === 'media_pitch') return 'media_pitch';
  if (normalized === 'social_media') return 'social_media';
  if (normalized === 'email') return 'email';
  if (normalized === 'statement') return 'statement';
  if (normalized.includes('press')) return 'press_release';
  if (normalized.includes('pitch')) return 'media_pitch';
  if (normalized.includes('social')) return 'social_media';
  if (normalized.includes('email')) return 'email';
  if (normalized.includes('statement')) return 'statement';
  return 'content';
}

function mapServerArtifactToArtifactContent(record: ServerContentArtifactRecord): ArtifactContent {
  const updatedAt = parseDate(record.updatedAt || record.createdAt || Date.now());
  const metadata = asRecord(record.metadata);
  const review = asRecord(record.review);
  const provenance = asRecord(metadata.provenance);
  const supervisor = asRecord(provenance.supervisor);
  const runId = normalizeString(record.runId) || normalizeString(metadata.runId) || normalizeString(supervisor.runId);
  const checkpointId = resolveArtifactCheckpointId({
    checkpointId: record.checkpointId || metadata.checkpointId,
    runId,
    requestId: metadata.requestId,
    metadata,
    review,
    data: record,
    pressRelease: record.pressRelease || metadata.pressReleaseV1 || metadata.pressRelease,
  });
  const normalizedReviewState = normalizeString(record.reviewState).toLowerCase();
  const normalizedStatus = normalizeString(record.status).toLowerCase();
  const approvalStatus =
    normalizedReviewState === 'approved' || normalizedStatus === 'approved' || normalizedStatus === 'published'
      ? 'approved'
      : normalizedReviewState === 'changes_requested'
        ? 'needs_revision'
        : normalizedReviewState === 'rejected'
          ? 'rejected'
          : normalizedReviewState === 'pending'
            ? 'pending'
            : undefined;
  return {
    id: record.id,
    ...(normalizeString(record.clientId) ? { clientId: normalizeString(record.clientId) } : {}),
    type: mapArtifactType(record.type),
    title: normalizeString(record.title) || 'Artifact',
    content: typeof record.content === 'string' ? record.content : '',
    agentType: 'ai',
    timestamp: updatedAt,
    version: Number.isFinite(Number(record.version)) ? Number(record.version) : 1,
    history: [],
    lastUpdated: updatedAt,
    isSavedToLibrary: true,
    savedToLibraryAt: updatedAt,
    ...(runId ? { runId } : {}),
    ...(checkpointId ? { checkpointId } : {}),
    ...(normalizeString(record.artifactKey) ? { artifactKey: normalizeString(record.artifactKey) } : {}),
    ...(normalizeString(record.lineageId) ? { lineageId: normalizeString(record.lineageId) } : {}),
    ...(normalizeString(record.sourceConversationId) ? { sourceConversationId: normalizeString(record.sourceConversationId) } : {}),
    ...(approvalStatus ? { approvalStatus } : {}),
    ...(Array.isArray(record.policyIssues) ? { policyIssues: record.policyIssues as any } : {}),
    ...(record.citationPackV1 ? { citationPackV1: record.citationPackV1 } : metadata.citationPackV1 ? { citationPackV1: metadata.citationPackV1 as ArtifactContent['citationPackV1'] } : {}),
    ...(Array.isArray(record.approvalEnvelopes) ? { approvalEnvelopes: record.approvalEnvelopes } : Array.isArray(metadata.approvalEnvelopes) ? { approvalEnvelopes: metadata.approvalEnvelopes as ArtifactContent['approvalEnvelopes'] } : {}),
    ...(record.pressRelease ? { pressRelease: record.pressRelease } : metadata.pressReleaseV1 ? { pressRelease: metadata.pressReleaseV1 as ArtifactContent['pressRelease'] } : metadata.pressRelease ? { pressRelease: metadata.pressRelease as ArtifactContent['pressRelease'] } : {}),
    ...(record.reviewerFeedback ? { reviewerFeedback: record.reviewerFeedback } : metadata.reviewerFeedback ? { reviewerFeedback: metadata.reviewerFeedback as ArtifactContent['reviewerFeedback'] } : review.reviewerFeedback ? { reviewerFeedback: review.reviewerFeedback as ArtifactContent['reviewerFeedback'] } : {}),
    ...(metadata.format ? { format: metadata.format as ArtifactContent['format'] } : {}),
    ...(metadata.rag ? { rag: metadata.rag as ArtifactContent['rag'] } : {}),
    ...(metadata.researcher ? { researcher: metadata.researcher as ArtifactContent['researcher'] } : {}),
    ...(Array.isArray(metadata.targets) ? { targets: metadata.targets as ArtifactContent['targets'] } : {}),
    ...(Array.isArray(metadata.sourcesEnriched) ? { sourcesEnriched: metadata.sourcesEnriched as ArtifactContent['sourcesEnriched'] } : {}),
    ...(Array.isArray(metadata.qaIssues) ? { qaIssues: metadata.qaIssues as ArtifactContent['qaIssues'] } : {}),
    ...(Array.isArray(metadata.validationIssues) ? { validationIssues: metadata.validationIssues as string[] } : {}),
    ...(metadata.consistency ? { consistency: metadata.consistency as ArtifactContent['consistency'] } : {}),
    ...(metadata.autoFix ? { autoFix: metadata.autoFix as ArtifactContent['autoFix'] } : {}),
    ...(metadata.pillars ? { pillars: metadata.pillars as ArtifactContent['pillars'] } : {}),
    ...(metadata.knowledgePack ? { knowledgePack: metadata.knowledgePack as ArtifactContent['knowledgePack'] } : {}),
    ...(metadata.supervisor ? { supervisorMeta: metadata.supervisor as ArtifactContent['supervisorMeta'] } : {}),
  };
}

function mapClientDocumentToAttachment(raw: unknown): SourceDocumentAttachment | null {
  const doc = asRecord(raw);
  const id = normalizeString(doc.id) || normalizeString(doc.ragDocId);
  if (!id) return null;
  const title = normalizeString(doc.title) || 'Document';
  const type = normalizeString(doc.type) || undefined;
  const url = normalizeString(doc.url) || undefined;
  const status = normalizeString(doc.status) || undefined;
  return {
    id,
    title,
    ...(type ? { type } : {}),
    ...(url ? { url } : {}),
    ...(status ? { status } : {}),
  };
}

function hashFeedbackText(input: string): string {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0).toString(16);
}

function toMessageMillis(value: unknown): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (value && typeof value === 'object' && typeof (value as any).toDate === 'function') {
    try {
      const date = (value as any).toDate();
      return date instanceof Date ? date.getTime() : Date.now();
    } catch {
      return Date.now();
    }
  }
  return Date.now();
}

function parseArtifactFeedbackLinkage(message: any): ArtifactFeedbackLinkage | null {
  const raw = message?.meta?.artifactFeedback;
  if (!raw || typeof raw !== 'object') return null;
  const artifactId = typeof raw.artifactId === 'string' ? raw.artifactId.trim() : '';
  if (!artifactId) return null;
  return {
    artifactId,
    ...(typeof raw.lineageId === 'string' && raw.lineageId.trim() ? { lineageId: raw.lineageId.trim() } : {}),
    ...(typeof raw.runId === 'string' && raw.runId.trim() ? { runId: raw.runId.trim() } : {}),
    ...(typeof raw.promptMessageId === 'string' && raw.promptMessageId.trim() ? { promptMessageId: raw.promptMessageId.trim() } : {}),
  };
}

function buildArtifactFromReferenceMessage(
  message: any,
  context: { clientId?: string; conversationId?: string } = {},
): ArtifactContent | null {
  const ref = message?.artifactReference;
  const artifactId = typeof ref?.artifactId === 'string' ? ref.artifactId.trim() : '';
  if (!message?.isArtifactReference || !artifactId) return null;

  const timestamp = new Date(toMessageMillis(ref.createdAt || message.timestamp));
  const meta = asRecord(message.meta);
  const provenance = asRecord(meta.provenance);
  const runtimeTool = asRecord(meta.runtimeTool);
  const runtimeToolState = asRecord(runtimeTool.state);
  const runtimeToolPayload = asRecord(runtimeToolState.resultPayload);
  const artifactExtras = asRecord(message.artifact);
  const pressRelease = asRecord(artifactExtras.pressRelease || meta.pressReleaseV1 || meta.pressRelease);
  const checkpointId = resolveArtifactCheckpointId({
    checkpointId: meta.checkpointId || runtimeToolState.checkpointId || runtimeToolPayload.checkpointId,
    runId: meta.runId || provenance.runId || runtimeToolState.runId || runtimeToolPayload.runId,
    requestId: meta.requestId || runtimeToolState.requestId,
    metadata: meta,
    data: artifactExtras,
    pressRelease,
  });
  const format = asRecord(artifactExtras.format);
  const rag = asRecord(artifactExtras.rag);
  const researcher = asRecord(artifactExtras.researcher);
  const content = typeof message.artifactContent === 'string'
    ? message.artifactContent
    : typeof message.content === 'string'
      ? message.content
      : '';

  return {
    id: artifactId,
    ...(context.clientId ? { clientId: context.clientId } : {}),
    type: mapArtifactType(ref.artifactType),
    title: sanitizeArtifactTitle(
      typeof ref.artifactTitle === 'string' && ref.artifactTitle.trim()
        ? ref.artifactTitle
        : 'Artifact',
      'Artifact',
    ),
    content,
    agentType: 'ai',
    timestamp,
    version: 1,
    history: [],
    lastUpdated: timestamp,
    ...(context.conversationId ? { sourceConversationId: context.conversationId } : {}),
    ...(normalizeString(meta.runId) ? { runId: normalizeString(meta.runId) } : normalizeString(provenance.runId) ? { runId: normalizeString(provenance.runId) } : normalizeString(runtimeToolState.runId) ? { runId: normalizeString(runtimeToolState.runId) } : normalizeString(runtimeToolPayload.runId) ? { runId: normalizeString(runtimeToolPayload.runId) } : {}),
    ...(checkpointId ? { checkpointId } : {}),
    ...(normalizeString(meta.artifactKey) ? { artifactKey: normalizeString(meta.artifactKey) } : normalizeString(runtimeToolState.artifactKey) ? { artifactKey: normalizeString(runtimeToolState.artifactKey) } : normalizeString(runtimeToolPayload.artifactKey) ? { artifactKey: normalizeString(runtimeToolPayload.artifactKey) } : {}),
    ...(normalizeString(meta.lineageId) ? { lineageId: normalizeString(meta.lineageId) } : normalizeString(runtimeToolState.lineageId) ? { lineageId: normalizeString(runtimeToolState.lineageId) } : normalizeString(runtimeToolPayload.lineageId) ? { lineageId: normalizeString(runtimeToolPayload.lineageId) } : {}),
    ...(meta.citationPackV1 ? { citationPackV1: meta.citationPackV1 as ArtifactContent['citationPackV1'] } : {}),
    ...(Array.isArray(meta.approvalEnvelopes) ? { approvalEnvelopes: meta.approvalEnvelopes as ArtifactContent['approvalEnvelopes'] } : {}),
    ...(Object.keys(pressRelease).length ? { pressRelease: pressRelease as ArtifactContent['pressRelease'] } : {}),
    ...(artifactExtras.reviewerFeedback ? { reviewerFeedback: artifactExtras.reviewerFeedback as ArtifactContent['reviewerFeedback'] } : meta.reviewerFeedback ? { reviewerFeedback: meta.reviewerFeedback as ArtifactContent['reviewerFeedback'] } : {}),
    ...(Object.keys(format).length ? { format: format as ArtifactContent['format'] } : {}),
    ...(Object.keys(rag).length ? { rag: rag as ArtifactContent['rag'] } : {}),
    ...(Object.keys(researcher).length ? { researcher: researcher as ArtifactContent['researcher'] } : {}),
    ...(Array.isArray(artifactExtras.targets) ? { targets: artifactExtras.targets as ArtifactContent['targets'] } : {}),
    ...(Array.isArray(artifactExtras.sourcesEnriched) ? { sourcesEnriched: artifactExtras.sourcesEnriched as ArtifactContent['sourcesEnriched'] } : {}),
    ...(Array.isArray(artifactExtras.qaIssues) ? { qaIssues: artifactExtras.qaIssues as ArtifactContent['qaIssues'] } : {}),
    ...(Array.isArray(artifactExtras.validationIssues) ? { validationIssues: artifactExtras.validationIssues as string[] } : {}),
    ...(Array.isArray(artifactExtras.policyIssues) ? { policyIssues: artifactExtras.policyIssues as PolicyIssue[] } : {}),
  };
}

function findReferencedArtifactForLinkage(
  messages: any[],
  linkage: ArtifactFeedbackLinkage,
  context: { clientId?: string; conversationId?: string } = {},
): ArtifactContent | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const ref = message?.artifactReference;
    const refArtifactId = typeof ref?.artifactId === 'string' ? ref.artifactId.trim() : '';
    const meta = asRecord(message?.meta);
    const provenance = asRecord(meta.provenance);
    const runId = normalizeString(meta.runId) || normalizeString(provenance.runId);
    if (refArtifactId !== linkage.artifactId && (!linkage.runId || runId !== linkage.runId)) {
      continue;
    }
    const artifact = buildArtifactFromReferenceMessage(message, context);
    if (artifact) return artifact;
  }
  return null;
}

function toRuntimeToolArtifactType(artifact: ArtifactContent | null): RuntimeToolArtifactType | null {
  if (!artifact?.type) return null;
  if (artifact.type === 'press_release') return 'press_release';
  if (artifact.type === 'media_pitch') return 'media_pitch';
  if (artifact.type === 'social_media') return 'social_media';
  return 'content';
}

function toArtifactContentType(value: unknown): ArtifactContent['type'] | null {
  const normalized = normalizeString(value).toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('press')) return 'press_release';
  if (normalized.includes('pitch')) return 'media_pitch';
  if (normalized.includes('social')) return 'social_media';
  if (normalized.includes('email')) return 'email';
  if (normalized.includes('statement')) return 'statement';
  return 'content';
}

function getRuntimeOutputArtifactType(state: RuntimeToolRunAgUiState | null | undefined): ArtifactContent['type'] | null {
  if (!state) return null;
  const payload = state.resultPayload || {};
  return (
    toArtifactContentType(payload.outputArtifactType)
    || toArtifactContentType(state.outputArtifactType)
    || toArtifactContentType(state.artifactType)
  );
}

function isArtifactTransformPrompt(value: string): boolean {
  const normalized = value.toLowerCase();
  const artifactReferenceNoun = '(?:draft|artifact|copy|version|press release|release|content|piece|post|article|asset)';
  const hasTransformVerb =
    /\b(repurpose|reformat|adapt|convert|transform|locali[sz]e|transcreate|translate|spin out)\b/.test(normalized)
    || new RegExp(`\\bturn\\s+(?:this|it|that|this\\s+${artifactReferenceNoun}|that\\s+${artifactReferenceNoun}|the\\s+${artifactReferenceNoun})\\s+into\\b`).test(normalized)
    || new RegExp(`\\bmake\\s+(?:this|it|that|this\\s+${artifactReferenceNoun}|that\\s+${artifactReferenceNoun}|the\\s+${artifactReferenceNoun})\\s+(?:into|as)\\b`).test(normalized)
    || new RegExp(`\\bformat\\s+(?:this|it|that|this\\s+${artifactReferenceNoun}|that\\s+${artifactReferenceNoun}|the\\s+${artifactReferenceNoun})\\s+as\\b`).test(normalized);
  const hasTarget =
    /\b(linkedin|instagram|x post|tweet|thread|social|post|caption|email|arabic|english|translation|localized|localised)\b/.test(normalized)
    || /\bto\s+(?:arabic|english)\b/.test(normalized);
  return hasTransformVerb && hasTarget;
}

function referencesLatestArtifact(value: string): boolean {
  return /\b(latest|most recent|last|newest)\s+(?:draft|artifact|copy|version|one)?\b/i.test(value);
}

function filterArtifactsByReferencedSourceType(
  candidates: ArtifactContent[],
  prompt: string,
): ArtifactContent[] {
  const referencedType = inferReferencedArtifactSourceType(prompt);
  if (!referencedType) return candidates;
  const typed = candidates.filter((artifact) => artifact.type === referencedType);
  return typed.length ? typed : candidates;
}

function inferReferencedArtifactSourceType(prompt: string): ArtifactContent['type'] | null {
  const normalized = prompt.toLowerCase();
  return (
    /\bpress release\b|\brelease\b/.test(normalized)
      ? 'press_release'
      : /\bmedia pitch\b|\bpitch\b/.test(normalized)
        ? 'media_pitch'
        : /\bsocial\b|\blinkedin\b|\binstagram\b|\bpost\b|\bcaption\b|\btweet\b|\bthread\b/.test(normalized)
          ? 'social_media'
          : null
  );
}

function inferRuntimeTargetChannel(value: string): string | undefined {
  const normalized = value.toLowerCase();
  if (/\blinkedin\b/.test(normalized)) return 'linkedin';
  if (/\binstagram\b/.test(normalized)) return 'instagram';
  if (/\bx\s*post\b|\btweet\b|\btwitter\b/.test(normalized)) return 'x';
  if (/\bthread\b/.test(normalized)) return 'thread';
  if (/\bcaption\b/.test(normalized)) return 'caption';
  if (/\bemail\b/.test(normalized)) return 'email';
  if (/\bsocial\b/.test(normalized)) return 'social';
  if (/\bpost\b/.test(normalized)) return 'post';
  return undefined;
}

function inferRuntimeTargetLanguage(value: string, fallback: 'en' | 'ar'): string | undefined {
  const normalized = value.toLowerCase();
  if (/\b(arabic|to arabic|into arabic)\b/.test(normalized) || /العربية|عربي|ترجم/.test(value)) return 'ar';
  if (/\b(english|to english|into english)\b/.test(normalized) || /الإنجليزية|الانجليزية/.test(value)) return 'en';
  return /\b(translate|locali[sz]e|transcreate)\b/.test(normalized) ? fallback : undefined;
}

function formatClarificationTypeLabel(type: ClarifyType): string {
  switch (type) {
    case 'press':
      return 'press release';
    case 'pitch':
      return 'pitch';
    case 'social':
      return 'social';
    case 'research':
      return 'research';
    case 'quality_review':
      return 'quality review';
    default:
      return 'general';
  }
}

function buildPreferenceSummaryItems(preferences: ClientPreferences | null): string[] {
  if (!preferences) return [];
  const items: string[] = [];
  if (preferences.locale) items.push(`Locale: ${preferences.locale.toUpperCase()}`);
  if (preferences.tone) items.push(`Tone: ${preferences.tone}`);
  if (preferences.spokesperson?.name) items.push(`Spokesperson: ${preferences.spokesperson.name}`);
  if (preferences.mediaContact?.email || preferences.mediaContact?.name) {
    items.push(`Media contact: ${preferences.mediaContact.email || preferences.mediaContact.name}`);
  }
  if (preferences.lengthPreference) items.push(`Length: ${preferences.lengthPreference}`);
  if (preferences.structurePreference) items.push(`Structure: ${preferences.structurePreference}`);
  if (preferences.requiredTerms?.length) items.push(`${preferences.requiredTerms.length} required terms`);
  if (preferences.forbiddenTerms?.length) items.push(`${preferences.forbiddenTerms.length} forbidden terms`);
  if (preferences.styleNotes?.length) items.push(`${preferences.styleNotes.length} style notes`);
  return items;
}

function buildQuickActionComposerPrefill(input: {
  action: ChatQuickAction;
  clientName?: string;
  clientIndustry?: string;
  artifact: ArtifactContent | null;
  sourceDocuments: SourceDocumentAttachment[];
  language: 'en' | 'ar';
  strictArtifactScope: boolean;
  clarificationTypes: ClarifyType[];
  preferenceSummary: string[];
}): string {
  const { action, artifact, sourceDocuments, strictArtifactScope } = input;
  const manifest = action.manifest;
  const clientLine = input.clientName
    ? `Client: ${input.clientName}${input.clientIndustry ? ` (${input.clientIndustry})` : ''}`
    : 'Client: General mode';
  const artifactLine = artifact
    ? `Attached artifact: ${sanitizeArtifactTitle(artifact.title, 'Untitled Artifact')} (${artifact.type})`
    : 'Attached artifact: None';
  const sourceLine = sourceDocuments.length
    ? `Selected source documents: ${sourceDocuments.map((doc) => doc.title).join(', ')}`
    : 'Selected source documents: None';
  const clarificationLine = input.clarificationTypes.length
    ? `Saved clarifications: ${input.clarificationTypes.map(formatClarificationTypeLabel).join(', ')}`
    : 'Saved clarifications: None';
  const preferenceLine = input.preferenceSummary.length
    ? `Active preferences: ${input.preferenceSummary.join('; ')}`
    : 'Active preferences: None';

  const askByKind: Record<string, string> = {
    review: 'Review the attached context and return specific, prioritized improvements.',
    'fact-check': 'Fact-check the attached artifact and call out claims that need evidence.',
    repurpose: 'Repurpose the attached artifact into useful channel variants.',
    pitch: 'Create a media pitch using the selected context and client preferences.',
    crisis: 'Prepare a crisis communications response using only the available client context.',
    insights: 'Summarize themes, risks, gaps, and opportunities from this conversation context.',
  };

  return [
    `${action.label} request`,
    '',
    `Tool: ${manifest.label} (${manifest.id})`,
    `Task: ${manifest.task}`,
    `Input mode: ${manifest.inputMode}`,
    `Result kind: ${manifest.resultKind}`,
    `Language: ${input.language === 'ar' ? 'Arabic' : 'English'}`,
    clientLine,
    artifactLine,
    artifact ? `Artifact scope: ${strictArtifactScope ? 'Strict to attached artifact' : 'Use attached artifact as reference'}` : 'Artifact scope: None',
    sourceLine,
    clarificationLine,
    preferenceLine,
    '',
    askByKind[action.kind] || manifest.description,
  ].join('\n');
}

function normalizeQuickActionError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === 'string' && error.trim()) return error.trim();
  return 'Runtime tool run failed.';
}

function buildFallbackRuntimeToolState(input: {
  action: ChatQuickAction;
  clientId: string;
  sourceConversationId?: string;
  artifactType?: RuntimeToolArtifactType | null;
  sourceArtifactType?: RuntimeToolArtifactType | null;
  outputArtifactType?: RuntimeToolArtifactType | null;
  targetChannel?: string | null;
  targetLanguage?: string | null;
  artifactId?: string;
  sourceArtifactId?: string;
  parentArtifactId?: string;
  artifactKey?: string;
  lineageId?: string;
  checkpointId?: string | null;
  content?: string;
  runId?: string;
  messageId?: string;
  route?: string;
}): RuntimeToolRunAgUiState {
  const requestId = input.messageId || input.runId || `chat-action-${Date.now()}`;
  const createsArtifact =
    input.action.manifest.resultKind === 'artifact'
    || input.action.manifest.resultKind === 'artifact_update';

  return {
    requestId,
    runId: input.runId || requestId,
    toolId: input.action.toolId,
    surface: 'chat',
    stage: 'completed',
    status: 'completed',
    summary: `${input.action.manifest.label} completed.`,
    label: input.action.manifest.label,
    clientId: input.clientId,
    artifactType: input.outputArtifactType || input.artifactType || null,
    sourceArtifactType: input.sourceArtifactType || input.artifactType || null,
    outputArtifactType: input.outputArtifactType || input.artifactType || null,
    targetChannel: input.targetChannel || null,
    targetLanguage: input.targetLanguage || null,
    artifactId: input.artifactId || null,
    sourceArtifactId: input.sourceArtifactId || input.artifactId || null,
    parentArtifactId: input.parentArtifactId || input.artifactId || null,
    artifactKey: input.artifactKey || null,
    lineageId: input.lineageId || null,
    checkpointId: input.checkpointId || input.runId || requestId,
    sourceConversationId: input.sourceConversationId || null,
    resultPayload: {
      content: input.content || '',
      title: input.action.manifest.label,
      summary: `${input.action.manifest.label} completed.`,
      route: input.route,
      task: input.action.manifest.task,
      resultKind: input.action.manifest.resultKind,
      sourceArtifactId: input.sourceArtifactId || input.artifactId,
      parentArtifactId: input.parentArtifactId || input.artifactId,
      sourceArtifactType: input.sourceArtifactType || input.artifactType || undefined,
      outputArtifactType: input.outputArtifactType || input.artifactType || undefined,
      targetChannel: input.targetChannel || undefined,
      targetLanguage: input.targetLanguage || undefined,
      checkpointId: input.checkpointId || input.runId || requestId,
      runId: input.runId || requestId,
      artifactId: createsArtifact ? (input.messageId || input.artifactId) : undefined,
      artifactKey: input.artifactKey,
      lineageId: input.lineageId,
      messageId: input.messageId,
    },
    createdArtifactIds: createsArtifact && (input.messageId || input.artifactId)
      ? [input.messageId || input.artifactId || '']
      : [],
  };
}

function isFreeTextApproval(value: string): boolean {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!normalized) return false;
  const strongMatch = /^(approved|approve it|ship it|looks good|looks great|all set|all good|this is good|this looks good)$/.test(normalized);
  if (strongMatch) return true;
  const thanksMatch = /\b(thanks|thank you|thx)\b/.test(normalized)
    && /\b(approved|approve|looks good|looks great|all set|all good|works|good|great)\b/.test(normalized);
  return thanksMatch;
}

// Main component
const ChatWindow = memo(function ChatWindow(props: ChatWindowProps = {}) {
  const chatContext = useChat();
  const messagesFromContext = chatContext.messages || [];
  const messages = props.messages || messagesFromContext;
  const loading = props.loading !== undefined ? props.loading : chatContext.loading;
  const renderMode = props.renderMode || 'default';
  const fullWidth = !!props.fullWidth;
  const handoffArtifactId = props.handoffArtifactId;
  const handoffActive = !!props.handoffActive;
  const onHandoffConsumed = props.onHandoffConsumed;
  const prefersReducedMotion = useReducedMotion();
  const [isCompactViewport, setIsCompactViewport] = useState(false);
  const {
    currentConversation, 
    sendMessage, 
    createArtifactReferenceMessage, 
    replaceMessage,
    appendAssistantMessage,
    loadConversation,
    learningSuggestion,
    handleConfirmLearning,
    handleDismissLearning,
    setConversationLanguage,
    storedClarifications,
    clearClarifications,
    reviewerFeedback,
    clientPreferences,
    preferencesLoading,
    updateClientPreferences
  } = chatContext;
  const { clarifyPending, clarifyQueue } = chatContext as any;
  const [clarifyUIHidden, setClarifyUIHidden] = useState(false);
  const [clarifySubmissionPending, setClarifySubmissionPending] = useState<ClarifySubmissionPendingState | null>(null);
  const [dismissedClarifyBannerId, setDismissedClarifyBannerId] = useState<string | null>(null);
  const [composerText, setComposerText] = useState('');
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [quickActionRunningKind, setQuickActionRunningKind] = useState<ChatQuickActionKind | null>(null);
  const [quickActionError, setQuickActionError] = useState<string | null>(null);
  const [artifactFeedbackContext, setArtifactFeedbackContext] = useState<ArtifactFeedbackLinkage | null>(null);
  const [artifactFeedbackBusyPromptId, setArtifactFeedbackBusyPromptId] = useState<string | null>(null);
  const [attachedArtifact, setAttachedArtifact] = useState<ArtifactContent | null>(null);
  const [attachedSourceDocumentIds, setAttachedSourceDocumentIds] = useState<string[]>([]);
  const [attachmentDocsHydrating, setAttachmentDocsHydrating] = useState(false);
  const [artifactStrictScope, setArtifactStrictScope] = useState(true);
  const [saveAsPreference, setSaveAsPreference] = useState(false);
  const [preferencesDrawerOpen, setPreferencesDrawerOpen] = useState(false);
  const [preferencesDrawerFocus, setPreferencesDrawerFocus] = useState<PreferenceField>(null);
  const [preferenceSuggestion, setPreferenceSuggestion] = useState<PreferenceSuggestion | null>(null);
  const [learningPrompt, setLearningPrompt] = useState<LearningPrompt | null>(null);
  const learningPromptKeyRef = useRef<string | null>(null);
  const preferenceExtractionKeyRef = useRef<string | null>(null);
  const thumbFeedbackSentRef = useRef<Set<string>>(new Set());
  const feedbackTextDedupRef = useRef<Set<string>>(new Set());
  const [selectedReviewerFeedback, setSelectedReviewerFeedback] = useState<ReviewerFeedback | null>(null);
  const [isReviewerDrawerOpen, setIsReviewerDrawerOpen] = useState(false);
  const lastClarifyIdRef = useRef<string | null>(null);
  const { activeClient, fetchClientById, setActiveClient } = useClient();
  const { provideFeedback } = useAI();
  const { currentUser } = useAuth();
  // PHASE-02 START
  const { flags } = useFeatureFlags();
  // PHASE-02 END
  // Artifact state (needs to be defined before auto-save logic)
  const {
    artifacts,
    activeArtifact,
    sidebarWidth,
    addArtifact,
    updateArtifact,
    updateArtifactWithVersion,
    setActiveArtifact,
    setArtifacts,
    clearArtifacts,
    setSidebarWidth
  } = useArtifactsBridge();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [showWritingAssistant, setShowWritingAssistant] = useState(false);
  const [writingContent, setWritingContent] = useState('');
  const [writingSuggestions, setWritingSuggestions] = useState<WritingSuggestion[]>([]);
  const [updateChatContent, setUpdateChatContent] = useState<((content: string) => void) | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [showJumpToOldest, setShowJumpToOldest] = useState(false);
  const [artifactVisibleCount, setArtifactVisibleCount] = useState(50);
  const [artifactHistoryOpenSignal, setArtifactHistoryOpenSignal] = useState(0);
  const [serverArtifactHydration, setServerArtifactHydration] = useState<{
    key: string;
    status: 'idle' | 'loading' | 'complete';
  }>({ key: '', status: 'idle' });
  const [attachmentSourceDocuments, setAttachmentSourceDocuments] = useState<SourceDocumentAttachment[]>([]);
  const artifactsListRef = useRef<HTMLDivElement>(null);
  const handoffConsumedKeyRef = useRef<string | null>(null);
  const handoffFetchArtifactKeyRef = useRef<string | null>(null);
  const attachmentDocHydrationClientIdRef = useRef<string | null>(null);
  const [openedPanels, setOpenedPanels] = useState<{artifacts?: boolean; seo?: boolean; analytics?: boolean; insights?: boolean; migration?: boolean}>({});
  const clarifyLock = Boolean(clarifyPending && !clarifyUIHidden);
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [showClarificationsDrawer, setShowClarificationsDrawer] = useState(false);
  const [isClearingClarifications, setIsClearingClarifications] = useState(false);
  // PHASE-03 START
  const [lang, setLang] = useState<'en'|'ar'>(() => (currentConversation?.language as 'en' | 'ar') || flags.localeDefault || 'en');
  useEffect(() => {
    const conversationLang = (currentConversation?.language as 'en' | 'ar') || flags.localeDefault || 'en';
    setLang(prev => (prev === conversationLang ? prev : conversationLang));
  }, [currentConversation?.id, currentConversation?.language, flags.localeDefault]);
  // PHASE-03 END
  // PHASE-02 START
  const [autoSaveEnabled, setAutoSaveEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem('chat.autoSaveArtifacts') === '1';
    } catch {
      return false;
    }
  });
  const autoSaveKeysRef = useRef<Set<string>>(new Set());
  const persistedChatArtifactKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    try {
      localStorage.setItem('chat.autoSaveArtifacts', autoSaveEnabled ? '1' : '0');
    } catch {}
  }, [autoSaveEnabled]);
  // PHASE-02 END

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(max-width: 767px)');
    const update = () => setIsCompactViewport(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  const fetchAuthedJson = useCallback(async <T,>(url: string, init?: RequestInit): Promise<T> => {
    const headers = new Headers(init?.headers || {});
    if (init?.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (currentUser) {
      const token = await currentUser.getIdToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }
    const response = await fetch(url, {
      ...init,
      headers,
    });
    if (!response.ok) {
      throw new Error(`request_failed_${response.status}`);
    }
    return await response.json() as T;
  }, [currentUser]);

  const clientContextSourceDocuments = useMemo<SourceDocumentAttachment[]>(() => {
    const relevantDocuments = (activeClient as any)?.context?.relevantDocuments;
    const docsRaw = Array.isArray(relevantDocuments)
      ? (relevantDocuments as any[])
      : [];
    const seen = new Set<string>();
    const docs: SourceDocumentAttachment[] = [];
    for (const doc of docsRaw) {
      const id = typeof doc?.ragDocId === 'string' && doc.ragDocId.trim()
        ? doc.ragDocId.trim()
        : typeof doc?.id === 'string' && doc.id.trim()
          ? doc.id.trim()
          : '';
      if (!id || seen.has(id)) continue;
      seen.add(id);
      docs.push({
        id,
        title: typeof doc?.title === 'string' && doc.title.trim() ? doc.title.trim() : 'Document',
        ...(typeof doc?.type === 'string' && doc.type.trim() ? { type: doc.type.trim() } : {}),
        ...(typeof doc?.url === 'string' && doc.url.trim() ? { url: doc.url.trim() } : {}),
        ...(typeof doc?.status === 'string' && doc.status.trim() ? { status: doc.status.trim() } : {}),
      });
    }
    return docs;
  }, [activeClient?.context?.relevantDocuments]);

  const availableSourceDocuments = useMemo<SourceDocumentAttachment[]>(() => {
    const byId = new Map<string, SourceDocumentAttachment>();
    for (const doc of clientContextSourceDocuments) {
      byId.set(doc.id, doc);
    }
    for (const doc of attachmentSourceDocuments) {
      byId.set(doc.id, { ...(byId.get(doc.id) || {} as SourceDocumentAttachment), ...doc });
    }
    return Array.from(byId.values());
  }, [attachmentSourceDocuments, clientContextSourceDocuments]);

  const recentArtifactsForAttachment = useMemo(() => (
    [...artifacts]
      .sort((a, b) => {
        const aTime = a.lastUpdated?.getTime() || a.timestamp?.getTime() || 0;
        const bTime = b.lastUpdated?.getTime() || b.timestamp?.getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, 8)
  ), [artifacts]);

  const attachedSourceDocuments = useMemo(() => {
    const byId = new Map(availableSourceDocuments.map((doc) => [doc.id, doc]));
    return attachedSourceDocumentIds
      .map((docId) => byId.get(docId) || null)
      .filter((doc): doc is SourceDocumentAttachment => !!doc);
  }, [attachedSourceDocumentIds, availableSourceDocuments]);

  const inputAttachments = useMemo<ChatInputAttachment[]>(() => {
    const attachments: ChatInputAttachment[] = [];
    if (attachedArtifact) {
      attachments.push({
        id: `artifact:${attachedArtifact.id}`,
        label: `Artifact: ${sanitizeArtifactTitle(attachedArtifact.title, 'Untitled Artifact')}`,
        kind: 'artifact',
        onRemove: () => setAttachedArtifact(null),
      });
    }
    for (const doc of attachedSourceDocuments) {
      attachments.push({
        id: `doc:${doc.id}`,
        label: `Doc: ${doc.title}`,
        kind: 'document',
        onRemove: () => {
          setAttachedSourceDocumentIds((prev) => prev.filter((value) => value !== doc.id));
        },
      });
    }
    return attachments;
  }, [attachedArtifact, attachedSourceDocuments]);

  const quickActionArtifactType = useMemo(
    () => toRuntimeToolArtifactType(attachedArtifact),
    [attachedArtifact],
  );

  const chatQuickActions = useMemo(
    () => listChatQuickActions({
      hasArtifact: !!attachedArtifact,
      artifactType: quickActionArtifactType,
      hasConversation: !!currentConversation?.id,
    }),
    [attachedArtifact, currentConversation?.id, quickActionArtifactType],
  );

  const activePreferenceSummary = useMemo(
    () => buildPreferenceSummaryItems(clientPreferences),
    [clientPreferences],
  );

  const getArtifactCheckpointId = useCallback((artifact: ArtifactContent | null | undefined): string | undefined => {
    if (!artifact) return undefined;
    return resolveArtifactCheckpointId({
      checkpointId: artifact.checkpointId,
      runId: artifact.runId,
      metadata: artifact.supervisorMeta,
      pressRelease: artifact.pressRelease,
      readiness: artifact.pressRelease?.readiness,
    }) || artifact.lineageId || artifact.artifactKey || undefined;
  }, []);

  const attachmentClientId = activeClient?.id || currentConversation?.clientId || '';
  const attachmentHydrationKey = `${attachmentClientId || 'no-client'}:${currentUser?.uid || 'no-user'}`;
  const attachmentArtifactsHydrating =
    showAttachmentPicker
    && serverArtifactHydration.key === attachmentHydrationKey
    && serverArtifactHydration.status === 'loading';

  const toggleSourceDocumentAttachment = useCallback((docId: string) => {
    setAttachedSourceDocumentIds((prev) => (
      prev.includes(docId)
        ? prev.filter((value) => value !== docId)
        : [...prev, docId]
    ));
  }, []);

  useEffect(() => {
    const availableIds = new Set(availableSourceDocuments.map((doc) => doc.id));
    setAttachedSourceDocumentIds((prev) => {
      const next = prev.filter((id) => availableIds.has(id));
      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [availableSourceDocuments]);

  useEffect(() => {
    if (!attachedArtifact) return;
    if (!artifacts.some((artifact) => artifact.id === attachedArtifact.id)) {
      setAttachedArtifact(null);
    }
  }, [artifacts, attachedArtifact]);

  useEffect(() => {
    if (!attachedArtifact) {
      setArtifactStrictScope(true);
    }
  }, [attachedArtifact]);

  useEffect(() => {
    if (!artifactFeedbackContext) return;
    if (!attachedArtifact) return;
    if (attachedArtifact.id === artifactFeedbackContext.artifactId) return;
    setArtifactFeedbackContext(null);
  }, [artifactFeedbackContext, attachedArtifact]);

  useEffect(() => {
    const clientId = activeClient?.id || currentConversation?.clientId || undefined;
    const conversationId = currentConversation?.id || undefined;
    const referencedArtifacts = messages
      .map((message: any) => buildArtifactFromReferenceMessage(message, { clientId, conversationId }))
      .filter((artifact): artifact is ArtifactContent => !!artifact);

    if (!referencedArtifacts.length) return;

    setArtifacts((prev) => {
      const byId = new Map(prev.map((artifact) => [artifact.id, artifact]));
      let changed = false;

      for (const referencedArtifact of referencedArtifacts) {
        const existing = byId.get(referencedArtifact.id);
        if (!existing) {
          byId.set(referencedArtifact.id, referencedArtifact);
          changed = true;
          continue;
        }

        const merged: ArtifactContent = {
          ...referencedArtifact,
          ...existing,
          content: existing.content || referencedArtifact.content,
          title: existing.title || referencedArtifact.title,
          clientId: existing.clientId || referencedArtifact.clientId,
          runId: existing.runId || referencedArtifact.runId,
          sourceConversationId: existing.sourceConversationId || referencedArtifact.sourceConversationId,
          format: existing.format || referencedArtifact.format,
          rag: existing.rag || referencedArtifact.rag,
          researcher: existing.researcher || referencedArtifact.researcher,
          targets: existing.targets || referencedArtifact.targets,
          sourcesEnriched: existing.sourcesEnriched || referencedArtifact.sourcesEnriched,
          qaIssues: existing.qaIssues || referencedArtifact.qaIssues,
          validationIssues: existing.validationIssues || referencedArtifact.validationIssues,
          policyIssues: existing.policyIssues || referencedArtifact.policyIssues,
        };

        if (
          merged.content !== existing.content
          || merged.title !== existing.title
          || merged.clientId !== existing.clientId
          || merged.runId !== existing.runId
          || merged.sourceConversationId !== existing.sourceConversationId
          || merged.format !== existing.format
          || merged.rag !== existing.rag
          || merged.researcher !== existing.researcher
          || merged.targets !== existing.targets
          || merged.sourcesEnriched !== existing.sourcesEnriched
          || merged.qaIssues !== existing.qaIssues
          || merged.validationIssues !== existing.validationIssues
          || merged.policyIssues !== existing.policyIssues
        ) {
          byId.set(referencedArtifact.id, merged);
          changed = true;
        }
      }

      if (!changed) return prev;
      return Array.from(byId.values()).sort((a, b) => {
        const aTime = a.lastUpdated?.getTime() || a.timestamp?.getTime() || 0;
        const bTime = b.lastUpdated?.getTime() || b.timestamp?.getTime() || 0;
        return bTime - aTime;
      });
    });
  }, [
    activeClient?.id,
    currentConversation?.clientId,
    currentConversation?.id,
    messages,
    setArtifacts,
  ]);

  useEffect(() => {
    setArtifactFeedbackContext(null);
    setArtifactFeedbackBusyPromptId(null);
  }, [currentConversation?.id]);

  useEffect(() => {
    if (showAttachmentPicker) return;
    attachmentDocHydrationClientIdRef.current = null;
    setAttachmentDocsHydrating(false);
  }, [showAttachmentPicker]);

  useEffect(() => {
    if (!showAttachmentPicker) return;
    const clientId = attachmentClientId;
    if (!clientId) return;
    if (availableSourceDocuments.length > 0) {
      attachmentDocHydrationClientIdRef.current = null;
      return;
    }
    if (attachmentDocHydrationClientIdRef.current === clientId) return;

    let cancelled = false;
    attachmentDocHydrationClientIdRef.current = clientId;
    setAttachmentDocsHydrating(true);

    const hydrateDocs = async () => {
      try {
        const payload = await fetchAuthedJson<ClientDocumentsResponse>(
          `/api/client-documents?clientId=${encodeURIComponent(clientId)}`
        );
        if (cancelled) return;
        const docs = Array.isArray(payload?.documents)
          ? payload.documents
              .map((entry) => mapClientDocumentToAttachment(entry))
              .filter((entry): entry is SourceDocumentAttachment => !!entry)
          : [];
        setAttachmentSourceDocuments(docs);
      } catch (error) {
        console.error('Failed to hydrate client documents for attachment picker', error);
      } finally {
        if (!cancelled) {
          setAttachmentDocsHydrating(false);
        }
      }
    };

    void hydrateDocs();
    return () => {
      cancelled = true;
    };
  }, [
    showAttachmentPicker,
    attachmentClientId,
    availableSourceDocuments.length,
    fetchAuthedJson,
  ]);

  const hasClarifications = useCallback(() => {
    if (!currentConversation?.id) return false;
    const conversationClarifications = storedClarifications[currentConversation.id];
    return !!conversationClarifications && Object.keys(conversationClarifications).length > 0;
  }, [currentConversation?.id, storedClarifications]);

  const getClarificationTypes = useCallback((): ClarifyType[] => {
    if (!currentConversation?.id) return [];
    const conversationClarifications = storedClarifications[currentConversation.id];
    if (!conversationClarifications) return [];
    return Object.keys(conversationClarifications).filter(
      key => !conversationClarifications[key as ClarifyType]?.skipped
    ) as ClarifyType[];
  }, [currentConversation?.id, storedClarifications]);

  const clarificationCount = useMemo(() => {
    if (!currentConversation?.id) return 0;
    const conversationClarifications = storedClarifications[currentConversation.id] || {};
    return Object.keys(conversationClarifications).length;
  }, [currentConversation?.id, storedClarifications]);

  const activeClarificationTypes = useMemo(
    () => getClarificationTypes(),
    [getClarificationTypes],
  );

  const handleClearAllClarifications = useCallback(async () => {
    if (!currentConversation?.id) return;
    if (isClearingClarifications) return;
    if (!hasClarifications()) {
      alert('No clarifications to clear.');
      return;
    }
    const shouldClear = await confirm({
      title: 'Clear clarifications',
      message: 'Clear all saved clarifications for this conversation?',
      confirmText: 'Clear all',
      cancelText: 'Cancel',
      type: 'warning',
    });
    if (shouldClear) {
      setIsClearingClarifications(true);
      try {
        await clearClarifications(currentConversation.id, 'all');
      } finally {
        setIsClearingClarifications(false);
      }
    }
  }, [clearClarifications, currentConversation?.id, hasClarifications, isClearingClarifications]);

  const handleRequestChanges = useCallback((feedback: ReviewerFeedback) => {
    const bulletSection = feedback.bullets.length > 0
      ? `Key points:\n${feedback.bullets.slice(0, 3).map((bullet) => `- ${bullet}`).join('\n')}`
      : '';
    const templateParts = [
      'Please address the following reviewer feedback:',
      '',
      feedback.summary,
      ...(bulletSection ? ['', bulletSection] : []),
    ];
    setComposerText(templateParts.join('\n'));
    requestAnimationFrame(() => {
      const input = document.querySelector('textarea[name=\"message\"]');
      if (input instanceof HTMLTextAreaElement) input.focus();
    });
  }, [setComposerText]);

  const handleViewDetails = useCallback((feedback: ReviewerFeedback) => {
    setSelectedReviewerFeedback(feedback);
    setIsReviewerDrawerOpen(true);
  }, [setSelectedReviewerFeedback, setIsReviewerDrawerOpen]);

  // PHASE-2.03 NOTE: moved sidebar event + server artifacts effects below sidebarPanel initialization
  // Persist focus mode
  useEffect(() => {
    try {
      const saved = localStorage.getItem('chat.focusMode');
      if (saved) setFocusMode(saved === '1');
    } catch {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('chat.focusMode', focusMode ? '1' : '0'); } catch {}
    try {
      window.dispatchEvent(new CustomEvent('chat:focus-mode-changed', { detail: { focusMode } }));
    } catch {}
  }, [focusMode]);

  // Defer non-critical UI after first paint
  useEffect(() => {
    const t = setTimeout(() => setShowStats(true), 150);
    return () => clearTimeout(t);
  }, []);

  // PHASE-02 START
  useEffect(() => {
    autoSaveKeysRef.current = new Set();
    persistedChatArtifactKeysRef.current = new Set();
  }, [currentConversation?.id]);

  useEffect(() => {
    setLearningPrompt(null);
    learningPromptKeyRef.current = null;
    preferenceExtractionKeyRef.current = null;
    setSaveAsPreference(false);
    setPreferenceSuggestion(null);
  }, [currentConversation?.id]);

  // PHASE-03 START
  const handleLanguageChange = useCallback((value: 'en' | 'ar') => {
    if (value === lang) return;
    setLang(value);
    if (!currentConversation?.id) return;
    setConversationLanguage(currentConversation.id, value).catch((err: unknown) => {
      console.error('Failed to update conversation language', err);
      try {
        (toast as any)?.error?.('Unable to update language preference.');
      } catch {}
    });
  }, [currentConversation?.id, lang, setConversationLanguage]);
  // PHASE-03 END

  const openPreferencesDrawer = useCallback((field?: PreferenceField) => {
    setPreferencesDrawerFocus(field || null);
    setPreferencesDrawerOpen(true);
  }, []);

  const handleQuickActionSelect = useCallback(async (action: ChatQuickAction) => {
    if (action.disabled) return;
    if (quickActionRunningKind) return;

    const requestBrief = buildQuickActionComposerPrefill({
      action,
      clientName: activeClient?.name,
      clientIndustry: activeClient?.industry,
      artifact: attachedArtifact,
      sourceDocuments: attachedSourceDocuments,
      language: lang,
      strictArtifactScope: artifactStrictScope,
      clarificationTypes: activeClarificationTypes,
      preferenceSummary: activePreferenceSummary,
    });
    const clientId = activeClient?.id || currentConversation?.clientId || 'default';
    const sourceConversationId = currentConversation?.id;
    const contextAttachments = {
      ...(attachedArtifact
        ? {
            artifact: {
              id: attachedArtifact.id,
              title: sanitizeArtifactTitle(attachedArtifact.title, 'Untitled Artifact'),
              type: attachedArtifact.type,
              ...(attachedArtifact.content ? { content: attachedArtifact.content.slice(0, 10000) } : {}),
              ...(attachedArtifact.artifactKey ? { artifactKey: attachedArtifact.artifactKey } : {}),
              ...(attachedArtifact.lineageId ? { lineageId: attachedArtifact.lineageId } : {}),
            },
          }
        : {}),
      ...(attachedArtifact && artifactStrictScope ? { artifactStrict: true } : {}),
      ...(attachedSourceDocumentIds.length ? { sourceDocumentIds: attachedSourceDocumentIds } : {}),
      ...(attachedSourceDocuments.length
        ? {
            sourceDocuments: attachedSourceDocuments.map((doc) => ({
              id: doc.id,
              title: doc.title,
              ...(doc.type ? { type: doc.type } : {}),
              ...(doc.url ? { url: doc.url } : {}),
            })),
          }
        : {}),
    };

    setComposerText(requestBrief);
    setQuickActionRunningKind(action.kind);
    setQuickActionError(null);
    const targetChannel = inferRuntimeTargetChannel(requestBrief) || null;
    const targetLanguage = inferRuntimeTargetLanguage(requestBrief, lang) || null;
    const outputArtifactType: RuntimeToolArtifactType | null =
      action.toolId === 'media_pitch'
        ? 'media_pitch'
        : action.toolId === 'repurpose'
        ? (targetChannel === 'email' ? 'content' : 'social_media')
        : quickActionArtifactType;
    const checkpointId = getArtifactCheckpointId(attachedArtifact);

    try {
      const result = await runToolAsAgent(action.toolId, {
        surface: 'chat',
        plannerApproved: true,
        clientId,
        language: lang,
        brief: requestBrief,
        input: attachedArtifact?.content,
        previousContent: attachedArtifact?.content,
        sourceConversationId,
        artifactType: quickActionArtifactType || undefined,
        sourceArtifactType: quickActionArtifactType || undefined,
        outputArtifactType: outputArtifactType || undefined,
        ...(targetChannel ? { targetChannel } : {}),
        ...(targetLanguage ? { targetLanguage } : {}),
        artifactId: attachedArtifact?.id,
        sourceArtifactId: attachedArtifact?.id,
        parentArtifactId: attachedArtifact?.id,
        artifactKey: attachedArtifact?.artifactKey,
        lineageId: attachedArtifact?.lineageId,
      });

      if (!result.ok || !result.data) {
        throw new Error(result.error || `${action.manifest.label} failed.`);
      }

      const normalized = result.data.normalized;
      const runtimeToolState = result.data.runtimeToolState || buildFallbackRuntimeToolState({
        action,
        clientId,
        sourceConversationId,
        artifactType: quickActionArtifactType,
        sourceArtifactType: quickActionArtifactType,
        outputArtifactType,
        targetChannel,
        targetLanguage,
        artifactId: attachedArtifact?.id,
        sourceArtifactId: attachedArtifact?.id,
        parentArtifactId: attachedArtifact?.id,
        artifactKey: attachedArtifact?.artifactKey,
        lineageId: attachedArtifact?.lineageId,
        checkpointId,
        content: normalized.content,
        runId: normalized.runId,
        messageId: normalized.messageId,
        route: normalized.route,
      });
      const assistantContent =
        normalized.content
        || runtimeToolState.resultPayload?.content
        || runtimeToolState.summary
        || `${action.manifest.label} completed.`;

      await appendAssistantMessage({
        content: assistantContent,
        messageKind: 'ai_response',
        meta: {
          runId: runtimeToolState.runId || normalized.runId,
          contextAttachments,
          runtimeTool: {
            state: runtimeToolState,
            error: null,
          },
        },
      });
      toast.success(`${action.label} completed.`);
    } catch (error) {
      const message = normalizeQuickActionError(error);
      setQuickActionError(message);
      toast.error(message);
    } finally {
      setQuickActionRunningKind(null);
    }
  }, [
    activeClarificationTypes,
    activeClient?.id,
    activeClient?.industry,
    activeClient?.name,
    activePreferenceSummary,
    appendAssistantMessage,
    artifactStrictScope,
    attachedArtifact,
    attachedSourceDocumentIds,
    attachedSourceDocuments,
    currentConversation?.clientId,
    currentConversation?.id,
    getArtifactCheckpointId,
    lang,
    quickActionArtifactType,
    quickActionRunningKind,
  ]);

  const handleSavePreferences = useCallback(async (updates: Partial<ClientPreferences>) => {
    const clientId = currentConversation?.clientId || activeClient?.id;
    if (!clientId) return;
    const saved = await updateClientPreferences(clientId, updates);
    if (saved) {
      try { (toast as any)?.success?.('Client preferences updated.'); } catch {}
    } else {
      try { (toast as any)?.error?.('Unable to update client preferences.'); } catch {}
    }
  }, [activeClient?.id, currentConversation?.clientId, updateClientPreferences]);

  const latestRunMeta = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const candidate = messages[i] as any;
      if (candidate?.sender !== 'ai') continue;
      const meta = candidate?.meta || {};
      if (meta?.provenance && typeof meta.provenance === 'object') return meta.provenance as RunMeta;
      if (
        meta?.effectivePreferences ||
        meta?.preferenceProvenance ||
        meta?.rag?.sufficiency ||
        meta?.rag?.retrievalQuality ||
        Array.isArray(meta?.citationsV2)
      ) {
        return meta as RunMeta;
      }
    }
    return null;
  }, [messages]);

  const preferenceProvenance = useMemo(() => {
    const provenance = latestRunMeta?.preferenceProvenance;
    return provenance && typeof provenance === 'object' ? provenance : undefined;
  }, [latestRunMeta?.preferenceProvenance]);

  const currentStoredClarifications = useMemo(() => {
    if (!currentConversation?.id) return {};
    return storedClarifications[currentConversation.id] || {};
  }, [currentConversation?.id, storedClarifications]);

  const agentKnowledgeDisplay = useMemo(() => (
    buildAgentKnowledgeDisplay({
      clientName: activeClient?.name || null,
      preferences: clientPreferences,
      provenance: preferenceProvenance,
      storedClarifications: currentStoredClarifications,
      messages,
    })
  ), [
    activeClient?.name,
    clientPreferences,
    currentStoredClarifications,
    messages,
    preferenceProvenance,
  ]);

  const handlePreferenceDiffExtraction = useCallback(
    async (input: { beforeText: string; afterText: string; runId?: string; messageId?: string; source?: string }) => {
      const clientId = currentConversation?.clientId || activeClient?.id;
      if (!clientId || !currentUser) return;
      const payload = buildPreferenceDiffPayload({
        beforeText: input.beforeText,
        afterText: input.afterText,
        runId: input.runId,
        messageId: input.messageId,
        source: input.source || 'edit_diff_extractor',
      });
      if (!payload) return;

      try {
        const token = await currentUser.getIdToken();
        const result = await syncPreferenceExtractionToGraph({
          clientId,
          payload,
          idToken: token,
        });
        if (!result || result.skipped || !result.updates || !Object.keys(result.updates).length) return;

        setPreferenceSuggestion({
          updates: result.updates,
          source: typeof result.source === 'string' ? result.source : (input.source || 'edit_diff_extractor'),
          rawConfidence: typeof result.rawConfidence === 'number' ? result.rawConfidence : undefined,
          calibratedConfidence: typeof result.calibratedConfidence === 'number' ? result.calibratedConfidence : undefined,
          runId: input.runId,
          messageId: input.messageId,
          keys: Array.isArray(result.keys) ? result.keys : undefined,
          diffChars: typeof result.diffChars === 'number' ? result.diffChars : payload.diffChars,
          explanation: typeof result.explanation === 'string' ? result.explanation : undefined,
        });

        trackPreferenceMetric('preference_extract_suggestion_shown_count', {
          clientId,
          source: typeof result.source === 'string' ? result.source : (input.source || 'edit_diff_extractor'),
        });
      } catch (error) {
        console.warn('[preferences] diff_extraction_failed', (error as any)?.message || error);
      }
    },
    [activeClient?.id, currentConversation?.clientId, currentUser],
  );

  const handlePreferenceSuggestionConfirm = useCallback(async () => {
    if (!preferenceSuggestion) return;
    const clientId = currentConversation?.clientId || activeClient?.id;
    if (!clientId || !currentUser) return;

    const saved = await updateClientPreferences(clientId, preferenceSuggestion.updates, {
      source: 'preference_extract_confirmed',
    });
    try {
      const token = await currentUser.getIdToken();
      void syncPreferenceExtractionFeedbackToGraph({
        clientId,
        acceptedByUser: true,
        idToken: token,
        source: preferenceSuggestion.source,
        rawConfidence: preferenceSuggestion.rawConfidence,
        calibratedConfidence: preferenceSuggestion.calibratedConfidence,
        runId: preferenceSuggestion.runId,
        messageId: preferenceSuggestion.messageId,
      });
    } catch {}

    if (saved) {
      try { (toast as any)?.success?.('Preference saved and applied to future drafts.'); } catch {}
    } else {
      try { (toast as any)?.error?.('Unable to save extracted preference.'); } catch {}
    }
    setPreferenceSuggestion(null);
  }, [
    activeClient?.id,
    currentConversation?.clientId,
    currentUser,
    preferenceSuggestion,
    updateClientPreferences,
  ]);

  const handlePreferenceSuggestionReject = useCallback(async () => {
    if (!preferenceSuggestion) return;
    const clientId = currentConversation?.clientId || activeClient?.id;
    if (!clientId || !currentUser) {
      setPreferenceSuggestion(null);
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await syncPreferenceExtractionFeedbackToGraph({
        clientId,
        acceptedByUser: false,
        idToken: token,
        source: preferenceSuggestion.source,
        rawConfidence: preferenceSuggestion.rawConfidence,
        calibratedConfidence: preferenceSuggestion.calibratedConfidence,
        runId: preferenceSuggestion.runId,
        messageId: preferenceSuggestion.messageId,
      });
    } catch {}

    trackPreferenceMetric('preference_extract_suggestion_rejected_count', {
      clientId,
      source: preferenceSuggestion.source,
    });
    setPreferenceSuggestion(null);
  }, [activeClient?.id, currentConversation?.clientId, currentUser, preferenceSuggestion]);

  const handleSaveLearningEntry = useCallback(async () => {
    if (!learningPrompt || !currentUser) return;
    const entry = await createLearningEntry({
      clientId: learningPrompt.clientId,
      beforeText: learningPrompt.beforeText,
      afterText: learningPrompt.afterText,
      userFeedback: learningPrompt.userFeedback,
      taskType: learningPrompt.taskType,
      runId: learningPrompt.runId,
      locale: learningPrompt.locale,
      createdBy: currentUser.uid,
      source: 'chat',
    });
    if (entry) {
      try { (toast as any)?.success?.('Learning example saved.'); } catch {}
    } else {
      try { (toast as any)?.error?.('Unable to save learning example.'); } catch {}
    }
    setLearningPrompt(null);
  }, [currentUser, learningPrompt]);

  const handleSkipLearningEntry = useCallback(() => {
    setLearningPrompt(null);
  }, []);

  const autoSaveArtifact = useCallback(
    async (artifact: ArtifactContent, memories?: MemoryReference[], provenance?: RunMeta | null) => {
      if (!flags.autoSaveArtifacts || !autoSaveEnabled) return;
      const clientId = activeClient?.id || currentConversation?.clientId;
      const userId = currentUser?.uid;
      if (!clientId || !userId) return;

      const artifactKey = `${artifact.id}-v${artifact.version}`;
      if (autoSaveKeysRef.current.has(artifactKey)) return;
      autoSaveKeysRef.current.add(artifactKey);

      try {
        const artifactKeyForSave = artifact.artifactKey || artifact.id;
        const lineageIdForSave = artifact.lineageId || artifactKeyForSave;
        await contentManagement.saveContent(
          {
            title: artifact.title,
            content: artifact.content,
            type: artifact.type as any,
            metadata: {
              autoSaved: true,
              agentType: artifact.agentType,
              artifactId: artifact.id,
              ...(artifact.runId ? { runId: artifact.runId } : {}),
              artifactKey: artifactKeyForSave,
              lineageId: lineageIdForSave,
              version: artifact.version,
              ...(artifact.badges?.length ? { badges: artifact.badges } : {}),
              language: lang,
              ...(artifact.policyIssues?.length ? { policyIssues: artifact.policyIssues } : {}),
            },
            provenance: provenance || (memories && memories.length ? { memoriesUsed: memories } : undefined),
          },
          clientId,
          userId,
          currentConversation?.id
        );

        setArtifacts(prev => prev.map(a =>
          a.id === artifact.id
            ? { ...a, isSavedToLibrary: true, savedToLibraryAt: new Date() }
            : a
        ));

        setActiveArtifact(prev =>
          prev && prev.id === artifact.id
            ? { ...prev, isSavedToLibrary: true, savedToLibraryAt: new Date() }
            : prev
        );

        try { (toast as any)?.success?.('Auto-saved to Documents'); } catch {}
      } catch (error) {
        console.error('Auto-save failed', error);
        try { (toast as any)?.error?.('Auto-save failed. You can save manually from Artifacts.'); } catch {}
        autoSaveKeysRef.current.delete(artifactKey);
      }
    },
    [
      flags.autoSaveArtifacts,
      autoSaveEnabled,
      activeClient?.id,
      currentConversation?.id,
      currentUser?.uid,
      setArtifacts,
      setActiveArtifact,
      lang
    ]
  );
  // PHASE-02 END

  // Keyboard shortcuts: f (focus), j (jump latest), n (new chat)
  useEffect(() => {
    const handler = async (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'f') {
        setFocusMode(v => !v);
      } else if (e.key === 'j') {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else if (e.key === 'n') {
        console.log('🔍 N key pressed', {
          isCleaningUp: (chatContext as any).isCleaningUp,
          timestamp: Date.now(),
          activeElement: document.activeElement?.tagName,
          target: e.target?.tagName,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey
        });
        try {
          if ((chatContext as any).createNewConversation && (chatContext as any).loadConversation && !(chatContext as any).isCleaningUp) {
            console.log('✅ Creating new conversation via N key');
            const id = await (chatContext as any).createNewConversation();
            await (chatContext as any).loadConversation(id);
          } else if ((chatContext as any).isCleaningUp) {
            console.log('🚫 N key blocked - cleanup in progress');
          }
        } catch (err) {
          console.error('New chat failed', err);
        }
      } else if (e.key === '?') {
        setShowShortcutHelp(v => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [chatContext]);

  // Block ALL shortcuts during cleanup to prevent unwanted chat creation
  useEffect(() => {
    if (!(chatContext as any)?.isCleaningUp) return;
    
    const blockShortcuts = (e: KeyboardEvent) => {
      const isCreationShortcut = e.key === 'n' || (e.ctrlKey && e.key === 'n') || ((e.ctrlKey || e.metaKey) && e.key === 'n');
      if (isCreationShortcut) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('🚫 BLOCKED shortcut during cleanup:', e.key, e.ctrlKey);
      }
    };
    
    // Add with capture=true to intercept before other handlers
    document.addEventListener('keydown', blockShortcuts, true);
    document.addEventListener('keyup', blockShortcuts, true);
    
    return () => {
      document.removeEventListener('keydown', blockShortcuts, true);
      document.removeEventListener('keyup', blockShortcuts, true);
    };
  }, [(chatContext as any)?.isCleaningUp]);

  // Unread tracking based on last seen timestamp
  useEffect(() => {
    if (!currentConversation) return;
    const key = `chat.lastSeen.${currentConversation.id}`;
    const latest = messages[messages.length - 1];
    if (!latest) return setUnreadCount(0);
    let lastSeen = 0;
    try { lastSeen = parseInt(localStorage.getItem(key) || '0', 10); } catch {}
    const toMillis = (t: any) => t instanceof Date ? t.getTime() : (typeof t === 'number' ? t : (typeof t === 'object' && t?.toDate ? t.toDate().getTime() : 0));
    const count = messages.filter(m => toMillis((m as any).timestamp) > lastSeen).length;
    setUnreadCount(count > 0 ? count : 0);
  }, [messages.length, currentConversation?.id]);

  const markSeenNow = useCallback(() => {
    if (!currentConversation) return;
    const key = `chat.lastSeen.${currentConversation.id}`;
    const latest = messages[messages.length - 1];
    const toMillis = (t: any) => t instanceof Date ? t.getTime() : (typeof t === 'number' ? t : (typeof t === 'object' && t?.toDate ? t.toDate().getTime() : Date.now()));
    const ts = latest ? toMillis((latest as any).timestamp) : Date.now();
    try { localStorage.setItem(key, String(ts)); } catch {}
    setUnreadCount(0);
  }, [currentConversation?.id, messages.length]);
  const [visibleCount, setVisibleCount] = useState(60);
  // ArticleMessage is now imported from ./ArticleMessage
  
  // Unified sidebar state (trimmed panels: only artifacts or migration)
  const [sidebarPanel, setSidebarPanel] = useState<'artifacts' | 'migration' | 'closed'>('closed');
  
  // Computed sidebar state (respect disableRightSidebar prop)
  const showSidebar = !props.disableRightSidebar && sidebarPanel !== 'closed';
  const showArtifacts = !props.disableRightSidebar && sidebarPanel === 'artifacts';
  
  // Unified sidebar controls
  const openSidebarPanel = useCallback((panel: 'artifacts') => {
    setSidebarPanel(panel);
    setOpenedPanels(prev => ({ ...prev, [panel]: true }));
    
    // Auto-select the most recent artifact when opening artifacts panel
    if (panel === 'artifacts' && artifacts.length > 0 && !activeArtifact) {
      const mostRecentArtifact = artifacts[artifacts.length - 1];
      setActiveArtifact(mostRecentArtifact);
    }
  }, [artifacts, activeArtifact, setActiveArtifact]);

  const closeSidebar = useCallback(() => {
    setSidebarPanel('closed');
  }, []);
  
  const toggleArtifactsPanel = useCallback(() => {
    setSidebarPanel(prev => prev === 'artifacts' ? 'closed' : 'artifacts');
  }, []);

  // Restore last sidebar panel state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('chat.sidebarPanel');
      if (saved === 'artifacts' || saved === 'closed' || saved === 'migration') {
        setSidebarPanel(saved as 'artifacts' | 'migration' | 'closed');
      }
      // If nothing saved, default is 'closed' (from useState initial value)
    } catch {}
  }, []);

  // Persist sidebar panel state and sync with navbar whenever it changes
  useEffect(() => {
    try {
      // Always save the current state, including 'closed'
      localStorage.setItem('chat.sidebarPanel', sidebarPanel);
    } catch {}

    // Sync artifacts state with navbar
    const artifactsVisible = sidebarPanel === 'artifacts';
    window.dispatchEvent(new CustomEvent('chat:artifacts:state-changed', { detail: { open: artifactsVisible } }));
  }, [sidebarPanel]);

  // PHASE-2.03 START: Allow external toggle/open/close events from page header
  useEffect(() => {
    if (props.disableRightSidebar) return;
    const onToggle = () => setSidebarPanel((p) => (p === 'artifacts' ? 'closed' : 'artifacts'));
    const onOpen = () => setSidebarPanel('artifacts');
    const onClose = () => setSidebarPanel('closed');
    window.addEventListener('chat:artifacts:toggle', onToggle as any);
    window.addEventListener('chat:artifacts:open', onOpen as any);
    window.addEventListener('chat:artifacts:close', onClose as any);
    return () => {
      window.removeEventListener('chat:artifacts:toggle', onToggle as any);
      window.removeEventListener('chat:artifacts:open', onOpen as any);
      window.removeEventListener('chat:artifacts:close', onClose as any);
    };
  }, [props.disableRightSidebar]);

  // PHASE-2.03 START: Load server artifacts for the active client when artifacts UI is opened
  useEffect(() => {
    const shouldHydrateArtifacts = sidebarPanel === 'artifacts' || showAttachmentPicker || handoffActive;
    if (!shouldHydrateArtifacts) return;
    let cancelled = false;
    const hydrationKey = `${attachmentClientId || 'no-client'}:${currentUser?.uid || 'no-user'}`;
    setServerArtifactHydration({ key: hydrationKey, status: 'loading' });
    const load = async () => {
      try {
        if (!attachmentClientId || !currentUser?.uid) {
          if (!cancelled) {
            setServerArtifactHydration({ key: hydrationKey, status: 'complete' });
          }
          return;
        }
        const payload = await fetchAuthedJson<ContentArtifactsListResponse>(
          `/api/content/artifacts?clientId=${encodeURIComponent(attachmentClientId)}`
        );
        if (cancelled) return;
        const mapped: ArtifactContent[] = Array.isArray(payload?.artifacts)
          ? payload.artifacts.map((artifact) => mapServerArtifactToArtifactContent(artifact))
          : [];
        setArtifacts((prev) => {
          const byId = new Map<string, ArtifactContent>();
          for (const a of prev) byId.set(a.id, a);
          for (const m of mapped) byId.set(m.id, { ...(byId.get(m.id) || {} as any), ...m });
          return Array.from(byId.values()).sort((a, b) => (b.lastUpdated?.getTime() || 0) - (a.lastUpdated?.getTime() || 0));
        });
      } catch (err) {
        console.error('Failed to load server artifacts for client', err);
      } finally {
        if (!cancelled) {
          setServerArtifactHydration({ key: hydrationKey, status: 'complete' });
        }
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [sidebarPanel, showAttachmentPicker, handoffActive, attachmentClientId, currentUser?.uid, fetchAuthedJson, setArtifacts]);
  // PHASE-2.03 END

  useEffect(() => {
    if (!handoffActive) {
      handoffConsumedKeyRef.current = null;
      handoffFetchArtifactKeyRef.current = null;
    }
  }, [handoffActive, handoffArtifactId]);

  useEffect(() => {
    if (!handoffActive) return;
    let cancelled = false;

    const requestedArtifactId = handoffArtifactId?.trim();
    if (!requestedArtifactId) {
      if (handoffConsumedKeyRef.current === '__missing_artifact__') return;
      handoffConsumedKeyRef.current = '__missing_artifact__';
      toast.error('Artifact handoff is missing an artifact id.');
      onHandoffConsumed?.();
      return;
    }

    const handoffKey = `${attachmentClientId || 'no-client'}:${requestedArtifactId}`;
    if (handoffConsumedKeyRef.current === handoffKey) return;

    setSidebarPanel('artifacts');

    if (!attachmentClientId || !currentUser?.uid) {
      return;
    }

    const primeComposerForArtifact = (artifact: ArtifactContent) => {
      if (cancelled) return;
      setActiveArtifact(artifact);
      setAttachedArtifact(artifact);
      setComposerText((previousText) => {
        if (previousText.trim()) return previousText;
        return 'Please revise the attached artifact while preserving facts/citations. Requested changes:';
      });

      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(() => {
          const composer = document.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
          if (!composer) return;
          composer.focus();
          const cursor = composer.value.length;
          composer.setSelectionRange(cursor, cursor);
        });
      }

      handoffConsumedKeyRef.current = handoffKey;
      onHandoffConsumed?.();
    };

    const targetArtifact = artifacts.find((artifact) => artifact.id === requestedArtifactId);
    if (targetArtifact) {
      primeComposerForArtifact(targetArtifact);
      return;
    }

    const hydrationKey = `${attachmentClientId || 'no-client'}:${currentUser?.uid || 'no-user'}`;
    const hydrationCompletedForCurrentScope =
      serverArtifactHydration.key === hydrationKey && serverArtifactHydration.status === 'complete';

    if (!hydrationCompletedForCurrentScope) {
      return () => {
        cancelled = true;
      };
    }

    if (handoffFetchArtifactKeyRef.current === handoffKey) {
      return () => {
        cancelled = true;
      };
    }
    handoffFetchArtifactKeyRef.current = handoffKey;

    const hydrateHandoffArtifact = async () => {
      try {
        const payload = await fetchAuthedJson<ContentArtifactResponse>(
          `/api/content/artifacts/${encodeURIComponent(requestedArtifactId)}?clientId=${encodeURIComponent(attachmentClientId)}`
        );
        if (cancelled) return;
        if (!payload?.artifact) {
          throw new Error('request_failed_404');
        }
        const mappedArtifact = mapServerArtifactToArtifactContent(payload.artifact);
        setArtifacts((prev) => {
          const existingIndex = prev.findIndex((artifact) => artifact.id === mappedArtifact.id);
          if (existingIndex >= 0) {
            return prev.map((artifact) => (
              artifact.id === mappedArtifact.id
                ? { ...artifact, ...mappedArtifact }
                : artifact
            ));
          }
          return [...prev, mappedArtifact];
        });
        primeComposerForArtifact(mappedArtifact);
      } catch (error) {
        if (cancelled) return;
        console.error('Artifact handoff hydration failed', error);
        toast.error('Artifact not found for this client/user.');
        handoffConsumedKeyRef.current = handoffKey;
        onHandoffConsumed?.();
      }
    };

    void hydrateHandoffArtifact();
    return () => {
      cancelled = true;
    };
  }, [
    handoffActive,
    handoffArtifactId,
    onHandoffConsumed,
    attachmentClientId,
    artifacts,
    currentUser?.uid,
    serverArtifactHydration,
    fetchAuthedJson,
    setArtifacts,
    setComposerText,
    setActiveArtifact,
  ]);

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);

  // Handle resize functionality
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = window.innerWidth - e.clientX;
    // Apply constraints from useContentArtifacts
    const clampedWidth = Math.max(320, Math.min(800, newWidth));
    setSidebarWidth(clampedWidth);
  }, [isResizing, setSidebarWidth]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Add/remove resize event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Helper functions for artifacts
  const getArtifactIcon = (type: string) => {
    const icons = {
      press_release: DocumentTextIcon,
      social_media: ChatBubbleLeftRightIcon,
      email: LightBulbIcon,
      statement: XMarkIcon,
      content: DocumentTextIcon,
      media_pitch: SparklesIcon,
      outreach_plan: SparklesIcon,
      thought_leadership: LightBulbIcon
    };
    return icons[type as keyof typeof icons] || DocumentTextIcon;
  };

  const getArtifactColor = (type: string) => {
    const colors = {
      press_release: 'bg-primary/10 text-primary',
      social_media: 'bg-secondary/10 text-secondary',
      email: 'bg-success/10 text-success',
      statement: 'bg-destructive/10 text-destructive',
      content: 'bg-warning/10 text-warning',
      media_pitch: 'bg-primary/10 text-primary',
      outreach_plan: 'bg-primary/10 text-primary',
      thought_leadership: 'bg-info/10 text-info'
    };
    return colors[type as keyof typeof colors] || 'bg-muted text-muted-foreground';
  };
  
  // Handle writing assistant toggle and content changes
  const handleWritingAssistantToggle = useCallback((enabled: boolean, content: string) => {
    setShowWritingAssistant(enabled);
    setWritingContent(content);
    if (!enabled) {
      setWritingSuggestions([]); // Clear suggestions when disabled
    }
  }, []);

  // Handle writing suggestions
  const handleWritingSuggestion = useCallback((suggestion: WritingSuggestion) => {
    setWritingSuggestions(prev => [...prev, suggestion]);
  }, []);

  // Handle applying writing suggestions
  const handleApplyWritingSuggestion = useCallback((suggestionId: string, newContent: string) => {
    setWritingContent(newContent);
    // Update the actual chat input content
    if (updateChatContent) {
      updateChatContent(newContent);
    }
    // Remove the applied suggestion
    setWritingSuggestions(prev => prev.filter(s => s.id !== suggestionId));
  }, [updateChatContent]);

  // Handle content update function from ChatInput
  const handleContentUpdateRef = useCallback((updateFn: (content: string) => void) => {
    setUpdateChatContent(() => updateFn);
  }, []);

  // Handle clicking on artifact references
  const handleArtifactClick = useCallback((artifactId: string) => {
    // Always open the artifacts panel first
    setSidebarPanel('artifacts');

    // Find the artifact by ID and open it
    let artifact = artifacts.find(a => a.id === artifactId);

    // If not found by ID, use the most recent artifact as fallback
    // This handles cases where there might be ID mismatches due to timing issues
    if (!artifact && artifacts.length > 0) {
      artifact = artifacts[artifacts.length - 1]; // Get most recent
    }

    if (artifact) {
      setActiveArtifact(artifact);
    }
  }, [artifacts, setActiveArtifact]);

  const upsertArtifactFromServerRecord = useCallback((record: ServerContentArtifactRecord): ArtifactContent => {
    const mapped = mapServerArtifactToArtifactContent(record);
    setArtifacts((prev) => {
      const existingIndex = prev.findIndex((artifact) => artifact.id === mapped.id);
      if (existingIndex >= 0) {
        return prev.map((artifact) => (artifact.id === mapped.id ? { ...artifact, ...mapped } : artifact));
      }
      return [...prev, mapped];
    });
    setActiveArtifact((prev) => (prev && prev.id === mapped.id ? { ...prev, ...mapped } : prev));
    return mapped;
  }, [setActiveArtifact, setArtifacts]);

  const applyLocalArtifactReviewState = useCallback((
    artifact: ArtifactContent,
    status: NonNullable<ArtifactContent['approvalStatus']>,
    feedback?: string,
  ): ArtifactContent => {
    const reviewer = currentUser?.displayName || currentUser?.email || undefined;
    const updated: ArtifactContent = {
      ...artifact,
      approvalStatus: status,
      ...(feedback ? { feedback } : {}),
      ...(status === 'approved'
        ? {
            approvedBy: reviewer,
            approvedAt: new Date(),
          }
        : {}),
      lastUpdated: new Date(),
    };
    setArtifacts((prev) => prev.map((item) => (item.id === artifact.id ? updated : item)));
    setActiveArtifact((prev) => (prev && prev.id === artifact.id ? updated : prev));
    return updated;
  }, [currentUser?.displayName, currentUser?.email, setActiveArtifact, setArtifacts]);

  const persistChatArtifactToContent = useCallback(async (
    artifact: ArtifactContent | null | undefined,
    options: { force?: boolean } = {},
  ): Promise<ArtifactContent | null> => {
    if (!artifact) return null;
    const clientId = artifact.clientId || currentConversation?.clientId || activeClient?.id || '';
    const userId = currentUser?.uid || '';
    const content = typeof artifact.content === 'string' ? artifact.content.trim() : '';
    if (!clientId || !userId || !artifact.id || !content) return null;

    const dedupeKey = `${clientId}:${artifact.id}:v${artifact.version}:${content.length}`;
    if (!options.force && persistedChatArtifactKeysRef.current.has(dedupeKey)) return null;
    persistedChatArtifactKeysRef.current.add(dedupeKey);

    try {
      const metadata = {
        artifactType: artifact.type,
        artifactId: artifact.id,
        ...(artifact.artifactKey ? { artifactKey: artifact.artifactKey } : {}),
        ...(artifact.lineageId ? { lineageId: artifact.lineageId } : {}),
        ...(artifact.runId ? { runId: artifact.runId } : {}),
        ...(getArtifactCheckpointId(artifact) ? { checkpointId: getArtifactCheckpointId(artifact) } : {}),
        ...(artifact.citationPackV1 ? { citationPackV1: artifact.citationPackV1 } : {}),
        ...(artifact.approvalEnvelopes?.length ? { approvalEnvelopes: artifact.approvalEnvelopes } : {}),
        ...(artifact.pressRelease ? { pressReleaseV1: artifact.pressRelease } : {}),
        ...(artifact.reviewerFeedback ? { reviewerFeedback: artifact.reviewerFeedback } : {}),
        ...(artifact.badges?.length ? { badges: artifact.badges } : {}),
        ...(artifact.format ? { format: artifact.format } : {}),
        ...(artifact.rag ? { rag: artifact.rag } : {}),
        ...(artifact.researcher ? { researcher: artifact.researcher } : {}),
        ...(artifact.targets ? { targets: artifact.targets } : {}),
        ...(artifact.sourcesEnriched ? { sourcesEnriched: artifact.sourcesEnriched } : {}),
        ...(artifact.qaIssues ? { qaIssues: artifact.qaIssues } : {}),
        ...(artifact.validationIssues ? { validationIssues: artifact.validationIssues } : {}),
        ...(artifact.consistency ? { consistency: artifact.consistency } : {}),
        ...(artifact.autoFix ? { autoFix: artifact.autoFix } : {}),
        ...(artifact.pillars ? { pillars: artifact.pillars } : {}),
        ...(artifact.knowledgePack ? { knowledgePack: artifact.knowledgePack } : {}),
        ...(artifact.supervisorMeta ? { supervisor: artifact.supervisorMeta } : {}),
        language: lang,
      };
      const payload = await fetchAuthedJson<ContentArtifactResponse>('/api/content/artifacts', {
        method: 'POST',
        body: JSON.stringify({
          clientId,
          artifact: {
            id: artifact.id,
            clientId,
            title: sanitizeArtifactTitle(artifact.title, 'Artifact'),
            content: artifact.content,
            type: artifact.type,
            agentType: artifact.agentType,
            version: artifact.version,
            ...(artifact.sourceConversationId || currentConversation?.id
              ? { sourceConversationId: artifact.sourceConversationId || currentConversation?.id }
              : {}),
            ...(artifact.artifactKey ? { artifactKey: artifact.artifactKey } : {}),
            ...(artifact.lineageId ? { lineageId: artifact.lineageId } : {}),
            ...(artifact.runId ? { runId: artifact.runId } : {}),
            ...(getArtifactCheckpointId(artifact) ? { checkpointId: getArtifactCheckpointId(artifact) } : {}),
            ...(artifact.policyIssues?.length ? { policyIssues: artifact.policyIssues } : {}),
            metadata,
          },
        }),
      });
      if (payload?.artifact) {
        return upsertArtifactFromServerRecord(payload.artifact);
      }
      return null;
    } catch (error) {
      persistedChatArtifactKeysRef.current.delete(dedupeKey);
      console.warn('[chat] artifact_content_upsert_failed', {
        artifactId: artifact.id,
        clientId,
        error: (error as Error)?.message || error,
      });
      return null;
    }
  }, [
    activeClient?.id,
    currentConversation?.clientId,
    currentConversation?.id,
    currentUser?.uid,
    fetchAuthedJson,
    getArtifactCheckpointId,
    lang,
    upsertArtifactFromServerRecord,
  ]);

  const buildArtifactAttachmentContext = useCallback((artifact: ArtifactContent) => ({
    id: artifact.id,
    title: sanitizeArtifactTitle(artifact.title, 'Untitled Artifact'),
    type: artifact.type,
    ...(artifact.content ? { content: artifact.content.slice(0, 10000) } : {}),
    ...(artifact.artifactKey ? { artifactKey: artifact.artifactKey } : {}),
    ...(artifact.lineageId ? { lineageId: artifact.lineageId } : {}),
    ...(artifact.runId ? { runId: artifact.runId } : {}),
    ...(artifact.checkpointId ? { checkpointId: artifact.checkpointId } : {}),
  }), []);

  const appendArtifactFeedbackPrompt = useCallback(async (artifact: ArtifactContent): Promise<void> => {
    const existingPrompt = messages.some((message: any) => {
      if (message?.sender !== 'ai') return false;
      if (message?.messageKind !== 'artifact_feedback_prompt') return false;
      const linkage = parseArtifactFeedbackLinkage(message);
      if (!linkage) return false;
      if (linkage.artifactId !== artifact.id) return false;
      if (artifact.runId && linkage.runId) return linkage.runId === artifact.runId;
      return true;
    });
    if (existingPrompt) return;
    await appendAssistantMessage({
      content: 'Draft ready. What do you want to do next?',
      messageKind: 'artifact_feedback_prompt',
      meta: {
        artifactFeedback: {
          artifactId: artifact.id,
          ...(artifact.lineageId ? { lineageId: artifact.lineageId } : artifact.artifactKey ? { lineageId: artifact.artifactKey } : {}),
          ...(artifact.runId ? { runId: artifact.runId } : {}),
        },
      },
    });
  }, [appendAssistantMessage, messages]);

  const handleArtifactFeedbackPromptAction = useCallback(async (message: any, action: ArtifactFeedbackAction) => {
    const linkage = parseArtifactFeedbackLinkage(message);
    if (!linkage) {
      toast.error('This feedback prompt is missing artifact linkage.');
      return;
    }

    const promptMessageId = typeof message?.id === 'string' ? message.id : linkage.promptMessageId;
    if (
      action !== 'open'
      && artifactFeedbackBusyPromptId
      && (artifactFeedbackBusyPromptId === promptMessageId || artifactFeedbackBusyPromptId === linkage.artifactId)
    ) {
      return;
    }
    let linkedArtifact = artifacts.find((artifact) => artifact.id === linkage.artifactId) || null;
    if (!linkedArtifact) {
      linkedArtifact = findReferencedArtifactForLinkage(messages, linkage, {
        clientId: activeClient?.id || currentConversation?.clientId || undefined,
        conversationId: currentConversation?.id || undefined,
      });
      if (linkedArtifact) {
        const artifactToUpsert = linkedArtifact;
        setArtifacts((prev) => (
          prev.some((artifact) => artifact.id === artifactToUpsert.id)
            ? prev.map((artifact) => (artifact.id === artifactToUpsert.id ? { ...artifactToUpsert, ...artifact } : artifact))
            : [...prev, artifactToUpsert]
        ));
      }
    }
    if (!linkedArtifact) {
      toast.error('Could not find the linked artifact.');
      return;
    }

    const actionPayload = {
      artifactId: linkage.artifactId,
      ...(linkage.lineageId ? { lineageId: linkage.lineageId } : {}),
      ...(linkage.runId ? { runId: linkage.runId } : {}),
      ...(promptMessageId ? { promptMessageId } : {}),
      action,
    } as const;

    if (action === 'open') {
      handleArtifactClick(linkage.artifactId);
      void persistChatArtifactToContent(linkedArtifact);
      return;
    }

    if (action === 'discuss') {
      setArtifactFeedbackBusyPromptId(promptMessageId || linkage.artifactId);
      try {
        void persistChatArtifactToContent(linkedArtifact);
        setAttachedArtifact(linkedArtifact);
        setActiveArtifact(linkedArtifact);
        setArtifactFeedbackContext({
          artifactId: linkage.artifactId,
          ...(linkage.lineageId ? { lineageId: linkage.lineageId } : {}),
          ...(linkage.runId ? { runId: linkage.runId } : {}),
          ...(promptMessageId ? { promptMessageId } : {}),
        });
        setComposerText((prev) => (prev.trim() ? prev : 'Please revise the attached draft based on these changes:'));
        requestAnimationFrame(() => {
          const input = document.querySelector('textarea[name="message"]');
          if (input instanceof HTMLTextAreaElement) input.focus();
        });
        await sendMessage('Discussing this draft in chat.', undefined, {
          kind: 'artifact_feedback_response',
          artifactFeedback: actionPayload,
          skipAgentRun: true,
        });
      } finally {
        setArtifactFeedbackBusyPromptId(null);
      }
      return;
    }

    const clientId = linkedArtifact.clientId || currentConversation?.clientId || activeClient?.id || '';
    if (!clientId) {
      toast.error('A client context is required to update review status.');
      return;
    }

    const route = action === 'approve' ? 'approve' : 'request-changes';
    setArtifactFeedbackBusyPromptId(promptMessageId || linkage.artifactId);
    try {
      const persistedArtifact = await persistChatArtifactToContent(linkedArtifact, { force: true });
      let nextArtifact = persistedArtifact || linkedArtifact;
      let serverStatusUpdated = false;
      try {
        const checkpointId = action === 'approve' ? getArtifactCheckpointId(nextArtifact) : undefined;
        const payload = await fetchAuthedJson<ContentArtifactResponse>(
          `/api/content/artifacts/${encodeURIComponent(linkage.artifactId)}/${route}`,
          {
            method: 'POST',
            body: JSON.stringify({
              clientId,
              ...(checkpointId ? { checkpointId } : {}),
              ...(action === 'request_changes' ? { feedback: 'Changes requested from chat prompt.' } : {}),
            }),
          },
        );
        nextArtifact = payload?.artifact ? upsertArtifactFromServerRecord(payload.artifact) : linkedArtifact;
        serverStatusUpdated = true;
      } catch (error) {
        if (!isRequestFailedStatus(error, 404)) {
          throw error;
        }
        nextArtifact = applyLocalArtifactReviewState(
          linkedArtifact,
          action === 'approve' ? 'approved' : 'needs_revision',
          action === 'request_changes' ? 'Changes requested from chat prompt.' : undefined,
        );
      }

      if (action === 'approve') {
        await sendMessage('Approved this draft.', undefined, {
          kind: 'artifact_feedback_response',
          artifactFeedback: actionPayload,
          skipAgentRun: true,
        });
        setArtifactFeedbackContext(null);
        toast.success(serverStatusUpdated ? 'Draft approved.' : 'Draft approved locally.');
        return;
      }

      const artifactAttachment = buildArtifactAttachmentContext(nextArtifact);
      await sendMessage(
        'Please revise the attached draft based on the requested changes, while preserving factual accuracy and citations.',
        undefined,
        {
          kind: 'artifact_feedback_response',
          artifactFeedback: actionPayload,
          contextAttachments: {
            artifact: artifactAttachment,
            artifactStrict: true,
          },
          previousContent: nextArtifact.content,
        },
      );
      setArtifactFeedbackContext(null);
      toast.info(serverStatusUpdated ? 'Revision request sent.' : 'Revision request sent using the local draft.');
    } catch (error) {
      console.error('[chat] artifact_feedback_action_failed', error);
      toast.error('Unable to update draft review status right now.');
    } finally {
      setArtifactFeedbackBusyPromptId(null);
    }
  }, [
    activeClient?.id,
    applyLocalArtifactReviewState,
    artifactFeedbackBusyPromptId,
    artifacts,
    buildArtifactAttachmentContext,
    currentConversation?.clientId,
    currentConversation?.id,
    fetchAuthedJson,
    getArtifactCheckpointId,
    handleArtifactClick,
    messages,
    persistChatArtifactToContent,
    sendMessage,
    setActiveArtifact,
    setArtifacts,
    upsertArtifactFromServerRecord,
  ]);

  // Handle writing assistant content changes
  const handleWritingContentChange = useCallback((newContent: string) => {
    if (updateChatContent) {
      updateChatContent(newContent);
    }
  }, [updateChatContent]);

  // Determine content type based on content analysis
  const getContentType = useCallback((content: string): PRContentType => {
    const lowerContent = content.toLowerCase();
    if (lowerContent.includes('press release') || lowerContent.includes('for immediate release')) {
      return 'press_release';
    }
    if (lowerContent.includes('media pitch') || lowerContent.includes('story idea')) {
      return 'media_pitch';
    }
    if (lowerContent.includes('statement') || lowerContent.includes('responds to')) {
      return 'statement';
    }
    if (lowerContent.includes('blog') || lowerContent.includes('article')) {
      return 'blog_post';
    }
    if (content.length < 280) {
      return 'social_media';
    }
    return 'press_release'; // Default
  }, []);

  const handleFeedback = useCallback(async (messageId: string, feedback: InteractionFeedback) => {
    try {
      const rating: 'positive' | 'negative' = feedback.rating === 'positive' ? 'positive' : 'negative';
      const feedbackText = typeof feedback.details === 'string' ? feedback.details.trim() : '';

      // Store the feedback (convert for backward compatibility)
      await provideFeedback(messageId, rating, feedbackText || undefined);

      const aiMessage = messages.find((msg) => msg.id === messageId);
      const memoryRefs = Array.isArray((aiMessage as any)?.meta?.memories)
        ? ((aiMessage as any).meta.memories as any[])
        : [];
      const memoryIds = Array.from(
        new Set(
          memoryRefs
            .map((memory: any) => {
              if (typeof memory === 'string' && memory.trim()) return memory.trim();
              if (memory && typeof memory === 'object' && typeof memory.id === 'string' && memory.id.trim()) {
                return memory.id.trim();
              }
              return '';
            })
            .filter(Boolean)
        )
      );
      const runId = typeof (aiMessage as any)?.meta?.runId === 'string'
        ? (aiMessage as any).meta.runId
        : undefined;
      const thumbs = rating === 'positive' ? 'up' : 'down';
      const thumbKey = `${messageId}:${thumbs}`;
      // No-regression contract: thumbs path remains authoritative for memory ranking updates.
      const shouldSendThumbs = !thumbFeedbackSentRef.current.has(thumbKey);
      const feedbackTextDedupKey = feedbackText ? `${messageId}:${hashFeedbackText(feedbackText.toLowerCase())}` : null;
      const shouldSendFeedbackText = feedbackTextDedupKey ? !feedbackTextDedupRef.current.has(feedbackTextDedupKey) : false;
      if (feedbackTextDedupKey && shouldSendFeedbackText) {
        feedbackTextDedupRef.current.add(feedbackTextDedupKey);
      }

      void (async () => {
        try {
          const token = currentUser ? await currentUser.getIdToken() : null;
          if (shouldSendThumbs) {
            const response = await fetch('/api/graph/feedback/thumbs', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                messageId,
                runId,
                thumbs,
                memoryIds,
              }),
            });
            if (!response.ok) {
              const errorText = await response.text();
              console.warn('[feedback] graph thumbs failed', { status: response.status, error: errorText });
            } else {
              thumbFeedbackSentRef.current.add(thumbKey);
            }
          }

          if (feedbackText && shouldSendFeedbackText) {
            const textResult = await syncFeedbackTextToGraph({
              messageId,
              runId,
              feedbackText,
              idToken: token,
            });
            if (!textResult) {
              if (feedbackTextDedupKey) {
                feedbackTextDedupRef.current.delete(feedbackTextDedupKey);
              }
            } else if (textResult.applied) {
              try {
                (toast as any)?.success?.(
                  textResult.durable === false
                    ? 'Learning candidate queued for review.'
                    : 'Preference learned from your note.'
                );
              } catch {}
            } else if (textResult.ignored) {
              console.info('[feedback] text_feedback_ignored', {
                messageId,
                runId,
                ignored: textResult.ignored,
                reason: textResult.reason,
              });
            }
          }
        } catch (error) {
          if (feedbackTextDedupKey && shouldSendFeedbackText) {
            feedbackTextDedupRef.current.delete(feedbackTextDedupKey);
          }
          console.warn('[feedback] graph feedback request failed', (error as any)?.message || error);
        }
      })();
      
      // Record performance feedback for learning
      const { recordUserFeedback } = await import('@/lib/aiAgents/performanceMonitoring');
      await recordUserFeedback(messageId, rating, feedback.userModified || false);
      
      // Toast notification for thumbs feedback
      if (rating === 'positive') {
        toast.success('Thanks! I\'ll lean into this style.');
      } else {
        toast.warning('Got it. I\'ll adjust future responses.');
      }
      
    } catch (error) {
      console.error('Error providing feedback:', error);
      toast.error('Feedback failed to send. Please try again.');
    }
  }, [provideFeedback, currentUser, messages]);

  const renderResponseQualityBadges = useCallback((message: any) => {
    if (!SHOW_AGENT_DIAGNOSTICS) return null;
    if (!message || message.sender !== 'ai') return null;
    const meta = (message.meta?.provenance || message.meta || {}) as RunMeta;
    const sufficiency = meta?.rag?.sufficiency;
    const citations = Array.isArray(meta?.citationsV2) ? meta.citationsV2 : [];

    const qualityBadges: Array<{ label: string; className: string }> = [];
    if (sufficiency) {
      qualityBadges.push({
        label: sufficiency.sufficient ? 'Sufficiency: grounded' : 'Sufficiency: limited context',
        className: sufficiency.sufficient
          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          : 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
      });
    }

    if (citations.length) {
      const grounded = citations.filter((entry) => entry?.grounded && typeof entry?.confidence === 'number');
      const avgConfidence = grounded.length
        ? grounded.reduce((sum, entry) => sum + Number(entry?.confidence || 0), 0) / grounded.length
        : 0;
      const band = avgConfidence >= 0.9 ? 'high' : avgConfidence >= 0.78 ? 'medium' : 'low';
      qualityBadges.push({
        label: `Citation grounding: ${band}${grounded.length ? ` (${avgConfidence.toFixed(2)})` : ''}`,
        className:
          band === 'high'
            ? 'border-info/30 bg-info/10 text-info'
            : band === 'medium'
              ? 'border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300'
              : 'border-destructive/30 bg-destructive/10 text-destructive',
      });
    }

    if (!qualityBadges.length) return null;
    return (
      <div className="mt-2 flex flex-wrap gap-2">
        {qualityBadges.map((badge) => (
          <span key={badge.label} className={cn('text-[11px] px-2 py-1 rounded-full border', badge.className)}>
            {badge.label}
          </span>
        ))}
      </div>
    );
  }, []);

  const pendingInputItems = useMemo(() => (
    aggregatePendingInputItems({
      clarifyPending,
      clarifyQueue,
      messages,
      artifacts,
    })
  ), [artifacts, clarifyPending, clarifyQueue, messages]);

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => {
      const input = document.querySelector('textarea[name="message"]');
      if (input instanceof HTMLTextAreaElement) input.focus();
    });
  }, []);

  const handlePendingInputAction = useCallback((item: PendingInputItem, action: PendingInputAction) => {
    if (action === 'resume') {
      setClarifyUIHidden(false);
      setClarifySubmissionPending(null);
      setDismissedClarifyBannerId(null);
      if (item.kind === 'clarification') {
        setTimeout(() => {
          const anchor = document.getElementById('clarify-form-anchor');
          anchor?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 30);
      } else {
        setComposerText((prev) => prev.trim() ? prev : 'Please resume this run from the latest checkpoint.');
        focusComposer();
      }
      return;
    }

    if (action === 'view_details') {
      if (item.kind === 'clarification') {
        setShowClarificationsDrawer(true);
        return;
      }
      if (item.artifactId) {
        handleArtifactClick(item.artifactId);
        return;
      }
      setComposerText((prev) => prev.trim() ? prev : 'Show me the run details before continuing.');
      focusComposer();
      return;
    }

    if (action === 'open_artifact' && item.artifactId) {
      handleArtifactClick(item.artifactId);
      return;
    }

    if (action === 'approve' || action === 'request_changes' || action === 'discuss') {
      const sourceMessage = messages.find((message: any) => {
        if (item.messageId && message?.id === item.messageId) return true;
        if (message?.messageKind !== 'artifact_feedback_prompt') return false;
        const linkage = parseArtifactFeedbackLinkage(message);
        return !!linkage && !!item.artifactId && linkage.artifactId === item.artifactId;
      });

      if (sourceMessage) {
        const feedbackAction = action === 'request_changes' ? 'request_changes' : action;
        void handleArtifactFeedbackPromptAction(sourceMessage, feedbackAction);
        return;
      }
    }

    if (action === 'discuss') {
      setComposerText((prev) => prev.trim() ? prev : `Let's discuss the pending ${item.kind.replace(/_/g, ' ')} before continuing.`);
      focusComposer();
    }
  }, [
    focusComposer,
    handleArtifactClick,
    handleArtifactFeedbackPromptAction,
    messages,
  ]);

  useEffect(() => {
    if (clarifyPending?.id && clarifyPending.id !== lastClarifyIdRef.current) {
      lastClarifyIdRef.current = clarifyPending.id;
      setClarifyUIHidden(false);
      setDismissedClarifyBannerId(null);
      setClarifySubmissionPending(null);
    }
    if (!clarifyPending) {
      lastClarifyIdRef.current = null;
      setDismissedClarifyBannerId(null);
      setClarifySubmissionPending(null);
    }
  }, [clarifyPending?.id]);

  const clarificationResumeHealthy = useMemo(() => {
    if (!clarifySubmissionPending) return false;
    return messages.some((message) => {
      const messageTs = toMessageMillis((message as any)?.timestamp);
      if ((message as any)?.isLoading === true && message?.sender === 'ai' && messageTs >= clarifySubmissionPending.startedAt - 1000) {
        return true;
      }
      const status = String((message as any)?.meta?.pendingStatus || '').trim().toLowerCase();
      if (!status) return false;
      if (status !== 'in_progress' && status !== 'completed' && status !== 'needs_clarification') return false;
      return messageTs >= clarifySubmissionPending.startedAt - 1000;
    });
  }, [clarifySubmissionPending, messages]);

  useEffect(() => {
    if (!clarifySubmissionPending) return;
    if (clarificationResumeHealthy) {
      setClarifySubmissionPending(null);
      return;
    }
    const stillAwaitingSameRequest = clarifyPending?.id === clarifySubmissionPending.requestId;
    if (!stillAwaitingSameRequest) {
      setClarifySubmissionPending(null);
      return;
    }
  }, [clarificationResumeHealthy, clarifyPending?.id, clarifySubmissionPending]);

  useEffect(() => {
    if (!clarifySubmissionPending) return;
    if (clarificationResumeHealthy) return;
    if (clarifySubmissionPending.timedOut) return;
    if (clarifyPending?.id !== clarifySubmissionPending.requestId) return;

    const pendingRequestId = clarifySubmissionPending.requestId;
    const timeoutId = setTimeout(() => {
      setClarifySubmissionPending((current) => {
        if (!current) return current;
        if (current.requestId !== pendingRequestId) return current;
        if (current.timedOut) return current;
        return { ...current, timedOut: true };
      });
      setClarifyUIHidden(false);
      setDismissedClarifyBannerId(null);
      try {
        (toast as any)?.error?.('Resume timed out. Please retry your clarification.');
      } catch {}
    }, CLARIFY_PENDING_HEALTH_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [clarificationResumeHealthy, clarifyPending?.id, clarifySubmissionPending]);

  // Auto-scroll to bottom on new messages with smooth behavior
  useEffect(() => {
    if (clarifyPending && !clarifyUIHidden) {
      setTimeout(() => {
        try {
          (toast as any)?.info?.('I need a few details to draft accurately.');
        } catch {}
        const el = document.getElementById('clarify-form-anchor');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [clarifyPending, clarifyUIHidden]);

  useEffect(() => {
    if (clarifyLock) return;
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
    };

    // Small delay to ensure message is rendered
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [messages.length, clarifyLock]);

  const lastMessageCountRef = useRef(messages.length);
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current && clarifyLock) {
      setShowJumpToLatest(true);
    }
    lastMessageCountRef.current = messages.length;
  }, [messages.length, clarifyLock]);

  // Track processed messages to avoid re-processing
  const [processedMessageIds, setProcessedMessageIds] = useState<Set<string>>(new Set());

  // Detect artifacts in new AI messages with versioning support
  useEffect(() => {
    if (messages.length === 0) return;

    // Use setTimeout to ensure this runs after the component has fully updated
    const timeoutId = setTimeout(() => {
      const lastMessageIndex = (() => {
        for (let index = messages.length - 1; index >= 0; index -= 1) {
          const candidate = messages[index];
          if (
            candidate
            && candidate.id
            && (candidate.sender === 'ai' || candidate.isAI)
            && !candidate.isLoading
            && candidate.content
            && (candidate as any).messageKind !== 'artifact_feedback_prompt'
            && !(candidate as any).isArtifactReference
            && !processedMessageIds.has(candidate.id)
          ) {
            return index;
          }
        }
        return -1;
      })();
      const lastMessage = lastMessageIndex >= 0 ? messages[lastMessageIndex] : null;
    
      // Only process AI messages that aren't loading and haven't been processed before
      if (
        lastMessage &&
        lastMessage.id &&
        (lastMessage.sender === 'ai' || lastMessage.isAI) &&
        !lastMessage.isLoading &&
        lastMessage.content &&
        (lastMessage as any).messageKind !== 'artifact_feedback_prompt' &&
        !(lastMessage as any).isArtifactReference && // Don't process artifact references
        !processedMessageIds.has(lastMessage.id) // Don't reprocess the same message
      ) {
        // Normalize content: if agent returned structured JSON (e.g., pitch/social),
        // format it into readable text so detection can work and artifacts are clean.
        const formatStructured = (raw: string): string => {
          try {
            const obj = JSON.parse(raw);
            // Pitch shape
            if (obj && (Array.isArray(obj.subjects) || typeof obj.pitch === 'string')) {
              const subjects = Array.isArray(obj.subjects) ? obj.subjects : [];
              const subjBlock = subjects.length
                ? `Subject options:\n${subjects.map((s: string) => `- ${s}`).join('\n')}`
                : '';
              const angleBlock = Array.isArray(obj.angleReasons) && obj.angleReasons.length
                ? `\nReasons:\n${obj.angleReasons.map((r: string) => `- ${r}`).join('\n')}`
                : '';
              const follow = obj.followUp ? `\n\nFollow-up:\n${obj.followUp}` : '';
              const parts = [subjBlock, obj.pitch ? `\nPitch:\n${obj.pitch}` : '', angleBlock, follow].filter(Boolean);
              if (parts.length) return parts.join('\n');
            }
            // Social shape
            if (obj && (obj.xThread || obj.linkedin || obj.instagram)) {
              const xBlock = Array.isArray(obj.xThread) && obj.xThread.length
                ? `X Thread:\n${obj.xThread.map((t: string, i: number) => `${i + 1}. ${t}`).join('\n')}`
                : '';
              const liBlock = obj.linkedin ? `\n\nLinkedIn:\n${obj.linkedin}` : '';
              const igBlock = obj.instagram ? `\n\nInstagram:\n- Caption: ${obj.instagram.caption || ''}\n- Alt Text: ${obj.instagram.altText || ''}` : '';
              const combined = [xBlock, liBlock, igBlock].filter(Boolean).join('');
              if (combined) return combined;
            }
          } catch {}
          return raw;
        };
        const content = formatStructured(lastMessage.content || lastMessage.text || '');
        const metaFromMessage = (((lastMessage as any)?.meta ?? null) as Record<string, unknown> | null);
        const runMeta = ((metaFromMessage?.provenance || metaFromMessage || null) as RunMeta | null);
        const routingMeta = ((runMeta as any)?.routing || {}) as Record<string, unknown>;
        const messageArtifact = ((lastMessage as any)?.artifact || {}) as Record<string, unknown>;
        const routingReason = normalizeString(routingMeta.reason);
        const agentTypeFromMessage = normalizeString((lastMessage as any).agentMetadata?.agentType).toLowerCase();
        const routeLabelFromMeta =
          normalizeString((runMeta as any)?.routeLabel)
          || normalizeString(metaFromMessage?.routeLabel)
          || normalizeString((runMeta as any)?.fineRoute)
          || normalizeString(metaFromMessage?.fineRoute);
        const artifactTransformMeta = asRecord((runMeta as any)?.artifactTransform || metaFromMessage?.artifactTransform);
        const isArtifactTransformResult =
          Object.keys(artifactTransformMeta).length > 0
          || routeLabelFromMeta === 'social'
          || routeLabelFromMeta === 'translate'
          || routingReason.startsWith('artifact_transform_override')
          || agentTypeFromMessage === 'social'
          || agentTypeFromMessage === 'translate';
        // Authoritative artifact decision from the graph service (meta.artifactBehavior).
        // When present it overrides the local heuristics below; the heuristics only
        // apply to legacy messages that carry no flag.
        const artifactBehaviorFromMeta = normalizeString(metaFromMessage?.artifactBehavior).toLowerCase();
        const serviceSaysPersist = artifactBehaviorFromMeta === 'persist';
        const serviceSaysSuppress = artifactBehaviorFromMeta === 'suppress';
        const isQaChatAnswer =
          serviceSaysSuppress
          || (
            !serviceSaysPersist
            && !isArtifactTransformResult
            && (
              !!messageArtifact.qa
              || routeLabelFromMeta === 'qa'
              || routingMeta.intentKind === 'qa_request'
              || routingReason === 'request_alignment_verification_match'
            )
          );

        if (isQaChatAnswer) {
          setProcessedMessageIds(prev => new Set(prev).add(lastMessage.id));
          return;
        }
        
        // Check if this might be an update to existing content
        const userMessageIndex = (() => {
          for (let index = lastMessageIndex - 1; index >= 0; index -= 1) {
            const candidate = messages[index];
            if (candidate?.sender === 'user') return index;
          }
          return -1;
        })();
        const userMessage = userMessageIndex >= 0 ? messages[userMessageIndex] : null;
        const userMessageContent = userMessage?.content || userMessage?.text || '';
        const explicitAttachedArtifactId = (() => {
          const raw = (userMessage as any)?.meta?.contextAttachments?.artifact;
          if (!raw || typeof raw !== 'object') return undefined;
          const id = typeof raw.id === 'string' ? raw.id.trim() : '';
          return id || undefined;
        })();
        
        // Detect update intent from user message with conversation context
        const updateDetection = detectUpdateIntent(userMessageContent, messages.slice(-5).map(m => m.content));
        
        console.log('Update detection result:', {
          userMessage: userMessageContent,
          isUpdateIntent: updateDetection.isUpdateIntent,
          confidence: updateDetection.confidence,
          editReason: updateDetection.editReason,
          attachedArtifact: explicitAttachedArtifactId,
          existingArtifacts: artifacts.length,
          activeArtifact: activeArtifact?.id
        });
        
        // Detect if this should become an artifact
        let detection = detectArtifactContent(content, (lastMessage as any).agentMetadata, {
          skipQaHeuristics: serviceSaysPersist,
        });
        // Threaded extras from the last assistant message (if provided by LangGraph)
          const threadedExtras = (() => {
          const a: any = (lastMessage as any).artifact || {};
          const out: any = {};
          if (Array.isArray(a.targets)) out.targets = a.targets;
          if (Array.isArray(a.sourcesEnriched)) out.sourcesEnriched = a.sourcesEnriched;
          if (Array.isArray(a.qaIssues)) out.qaIssues = a.qaIssues;
          if (Array.isArray(a.validationIssues)) out.validationIssues = a.validationIssues;
          // PHASE-04 START
          if (a.researcher) out.researcher = a.researcher;
          // PHASE-04 END
          if (a.format) out.format = a.format;
          return out;
        })();
        const consistencyMeta = runMeta?.consistency;
        const autoFixMeta = runMeta?.autoFix;
        const pillarsMeta = runMeta?.pillars;
        const knowledgePackMeta = runMeta?.knowledgePack;
        const supervisorMeta = runMeta?.supervisor;
        const resolvedRunId =
          (typeof metaFromMessage?.runId === 'string' && metaFromMessage.runId.trim())
            ? metaFromMessage.runId.trim()
            : (typeof supervisorMeta?.runId === 'string' && supervisorMeta.runId.trim())
              ? supervisorMeta.runId.trim()
              : undefined;
        const policyIssues: PolicyIssue[] = Array.isArray(metaFromMessage?.policyIssues)
          ? (metaFromMessage?.policyIssues as PolicyIssue[])
          : [];

        const inferRequestedArtifactType = (): ArtifactContent['type'] | null => {
          const req = userMessageContent.toLowerCase();
          if (/press\s*release|for\s+immediate\s+release/.test(req)) return 'press_release';
          if (/media\s*pitch|journalist\s*pitch|\bstory\s*idea\b|\bpitch\b/.test(req)) return 'media_pitch';
          if (/\bsocial\b|tweet|linkedin|instagram|\bx\s*thread\b|post\s+for\s+social/.test(req)) return 'social_media';
          if (/\bemail\b|subject\s*:|outreach\s+email/.test(req)) return 'email';
          if (/\bstatement\b|holding\s+statement|official\s+response/.test(req)) return 'statement';
          return null;
        };
        const inferArtifactTypeFromAgent = (): ArtifactContent['type'] => {
          const agentType = ((lastMessage as any).agentMetadata?.agentType || '').toString().toLowerCase();
          if (agentType === 'social') return 'social_media';
          if (agentType === 'media') return 'media_pitch';
          if (agentType === 'crisis' || agentType === 'executive') return 'statement';
          return 'content';
        };
        const deriveFallbackTitle = (fallbackType: ArtifactContent['type']): string => {
          const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
          for (const line of lines) {
            const isHeader = /for\s+immediate\s+release/i.test(line);
            const isDate = /\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\b\s+\d{1,2},\s+\d{4}/i.test(line) || /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(line);
            if (!isHeader && !isDate && line.length > 10 && line.length < 150) return line.replace(/^\*+|\*+$/g, '');
          }
          const firstSentence = content.split(/[.!?]/)[0].trim();
          if (firstSentence.length > 10 && firstSentence.length < 150) return firstSentence;
          const defaults: Record<ArtifactContent['type'], string> = {
            press_release: 'Press Release',
            media_pitch: 'Media Pitch',
            social_media: 'Social Media Draft',
            email: 'Email Draft',
            statement: 'Statement',
            content: 'Content Draft',
            outreach_plan: 'Outreach Plan',
            thought_leadership: 'Thought Leadership Piece',
          };
          return defaults[fallbackType];
        };
        const requestedArtifactType = inferRequestedArtifactType();
        const transformOutputArtifactType =
          toArtifactContentType(artifactTransformMeta.outputArtifactType)
          || (routeLabelFromMeta === 'social' ? 'social_media' : null)
          || (agentTypeFromMessage === 'social' ? 'social_media' : null)
          || (routeLabelFromMeta === 'translate' && requestedArtifactType ? requestedArtifactType : null);
        const hasPolicySignals = policyIssues.length > 0;
        const shouldBypassSubstantialGate =
          content.trim().length >= 180 && (requestedArtifactType === 'press_release' || hasPolicySignals);
        const shouldPromoteTransformArtifact =
          isArtifactTransformResult
          && content.trim().length >= 40
          && requestedArtifactType !== 'content';
        const runtimeToolMeta = (((metaFromMessage?.runtimeTool || null) as Record<string, unknown> | null) || null);
        const runtimeToolState = runtimeToolMeta?.state && typeof runtimeToolMeta.state === 'object'
          ? (runtimeToolMeta.state as any)
          : null;
        const runtimeToolCreatedArtifactIds = Array.isArray(runtimeToolState?.createdArtifactIds)
          ? runtimeToolState.createdArtifactIds.filter((value: unknown): value is string => typeof value === 'string' && value.trim().length > 0)
          : [];
        const runtimeToolArtifactId = runtimeToolCreatedArtifactIds[0]
          || (typeof runtimeToolState?.artifactId === 'string' && runtimeToolState.artifactId.trim()
            ? runtimeToolState.artifactId.trim()
            : '');
        const runtimeToolContent = typeof runtimeToolState?.resultPayload?.content === 'string' && runtimeToolState.resultPayload.content.trim()
          ? runtimeToolState.resultPayload.content.trim()
          : '';
        const runtimeToolTitle = typeof runtimeToolState?.resultPayload?.title === 'string' && runtimeToolState.resultPayload.title.trim()
          ? runtimeToolState.resultPayload.title.trim()
          : '';

        if (runtimeToolState?.status === 'completed' && runtimeToolArtifactId && runtimeToolContent) {
          const runtimeArtifactType = getRuntimeOutputArtifactType(runtimeToolState)
            || requestedArtifactType
            || inferArtifactTypeFromAgent();
          const artifactTitle = runtimeToolTitle || deriveFallbackTitle(runtimeArtifactType);
          const existingArtifact = artifacts.find((artifact) => artifact.id === runtimeToolArtifactId) || null;
          const nextTimestamp = new Date();
          const runtimeCheckpointId = resolveArtifactCheckpointId({
            checkpointId: runtimeToolState.checkpointId || runtimeToolState.resultPayload?.checkpointId,
            runId: resolvedRunId || runtimeToolState.runId || runtimeToolState.resultPayload?.runId,
            requestId: runtimeToolState.requestId,
          });
          const nextArtifact: ArtifactContent = existingArtifact
            ? {
                ...existingArtifact,
                type: runtimeArtifactType,
                title: artifactTitle,
                content: runtimeToolContent,
                lastUpdated: nextTimestamp,
                version: existingArtifact.version + 1,
                history: [
                  ...existingArtifact.history,
                  {
                    content: existingArtifact.content,
                    title: existingArtifact.title,
                    timestamp: existingArtifact.lastUpdated,
                    editReason: `Updated via ${runtimeToolState.label || runtimeToolState.toolId || 'runtime tool'}`,
                    agentType: existingArtifact.agentType,
                  },
                ],
                ...(resolvedRunId ? { runId: resolvedRunId } : {}),
                ...(runtimeCheckpointId ? { checkpointId: runtimeCheckpointId } : {}),
                ...(existingArtifact.artifactKey ? { artifactKey: existingArtifact.artifactKey } : runtimeToolState.artifactKey ? { artifactKey: runtimeToolState.artifactKey } : {}),
                ...(existingArtifact.lineageId ? { lineageId: existingArtifact.lineageId } : runtimeToolState.lineageId ? { lineageId: runtimeToolState.lineageId } : {}),
                ...(threadedExtras.format ? { format: threadedExtras.format } : {}),
                ...(threadedExtras.researcher ? { researcher: threadedExtras.researcher } : {}),
                ...(threadedExtras.targets ? { targets: threadedExtras.targets } : {}),
                ...(threadedExtras.sourcesEnriched ? { sourcesEnriched: threadedExtras.sourcesEnriched } : {}),
                ...(threadedExtras.qaIssues ? { qaIssues: threadedExtras.qaIssues } : {}),
                ...(threadedExtras.validationIssues ? { validationIssues: threadedExtras.validationIssues } : {}),
                policyIssues,
                ...(consistencyMeta ? { consistency: consistencyMeta } : {}),
                ...(autoFixMeta ? { autoFix: autoFixMeta } : {}),
                ...(pillarsMeta ? { pillars: pillarsMeta } : {}),
                ...(knowledgePackMeta ? { knowledgePack: knowledgePackMeta } : {}),
                ...(supervisorMeta ? { supervisorMeta } : {}),
              }
            : {
                id: runtimeToolArtifactId,
                type: runtimeArtifactType,
                title: artifactTitle,
                content: runtimeToolContent,
                agentType: (lastMessage as any).agentMetadata?.agentType || 'content',
                timestamp: nextTimestamp,
                version: 1,
                history: [],
                lastUpdated: nextTimestamp,
                ...(currentConversation?.id ? { sourceConversationId: currentConversation.id } : {}),
                ...(resolvedRunId ? { runId: resolvedRunId } : {}),
                ...(runtimeCheckpointId ? { checkpointId: runtimeCheckpointId } : {}),
                ...(runtimeToolState.artifactKey ? { artifactKey: runtimeToolState.artifactKey } : {}),
                ...(runtimeToolState.lineageId ? { lineageId: runtimeToolState.lineageId } : {}),
                ...(threadedExtras.format ? { format: threadedExtras.format } : {}),
                ...(threadedExtras.researcher ? { researcher: threadedExtras.researcher } : {}),
                ...(threadedExtras.targets ? { targets: threadedExtras.targets } : {}),
                ...(threadedExtras.sourcesEnriched ? { sourcesEnriched: threadedExtras.sourcesEnriched } : {}),
                ...(threadedExtras.qaIssues ? { qaIssues: threadedExtras.qaIssues } : {}),
                ...(threadedExtras.validationIssues ? { validationIssues: threadedExtras.validationIssues } : {}),
                policyIssues,
                ...(consistencyMeta ? { consistency: consistencyMeta } : {}),
                ...(autoFixMeta ? { autoFix: autoFixMeta } : {}),
                ...(pillarsMeta ? { pillars: pillarsMeta } : {}),
                ...(knowledgePackMeta ? { knowledgePack: knowledgePackMeta } : {}),
                ...(supervisorMeta ? { supervisorMeta } : {}),
              };

          setArtifacts((prev) => {
            const existingIndex = prev.findIndex((artifact) => artifact.id === runtimeToolArtifactId);
            if (existingIndex >= 0) {
              return prev.map((artifact) => (artifact.id === runtimeToolArtifactId ? nextArtifact : artifact));
            }
            return [...prev, nextArtifact];
          });
          setActiveArtifact(nextArtifact);
          setAttachedArtifact(nextArtifact);

          const referenceMessage = createArtifactReferenceMessage({
            id: nextArtifact.id,
            type: nextArtifact.type,
            title: nextArtifact.title,
            timestamp: nextArtifact.timestamp || nextTimestamp,
            agentMetadata: (lastMessage as any).agentMetadata,
            content: nextArtifact.content,
            ...(nextArtifact.runId ? { runId: nextArtifact.runId } : {}),
            ...(nextArtifact.checkpointId ? { checkpointId: nextArtifact.checkpointId } : {}),
            ...(nextArtifact.artifactKey ? { artifactKey: nextArtifact.artifactKey } : {}),
            ...(nextArtifact.lineageId ? { lineageId: nextArtifact.lineageId } : {}),
            meta: (lastMessage as any).meta,
          });

          replaceMessage(lastMessage.id, referenceMessage);
          void persistChatArtifactToContent(nextArtifact);
          void appendArtifactFeedbackPrompt(nextArtifact);
          setProcessedMessageIds((prev) => new Set(prev).add(lastMessage.id));
          return;
        }

        if (
          runtimeToolState
          && (
            runtimeToolState.status === 'failed'
            || runtimeToolState.toolId === 'fact_checker'
            || runtimeToolState.toolId === 'insights_exec'
            || runtimeToolState.toolId === 'seo_optimize'
          )
        ) {
          setProcessedMessageIds((prev) => new Set(prev).add(lastMessage.id));
          return;
        }

        // For structured policy-reviewed outputs (especially press releases), allow artifact
        // promotion even when formatting fails general "substantial" heuristics.
        if (!isSubstantialContent(content) && !shouldBypassSubstantialGate && !shouldPromoteTransformArtifact && !serviceSaysPersist) {
          setProcessedMessageIds(prev => new Set(prev).add(lastMessage.id));
          return;
        }

        // If heuristics miss, force artifact creation when we have explicit intent or policy signals.
        if (!detection.shouldCreateArtifact) {
          const shouldForceArtifact = shouldBypassSubstantialGate || shouldPromoteTransformArtifact || serviceSaysPersist;
          if (shouldForceArtifact) {
            const forcedType = transformOutputArtifactType || inferArtifactTypeFromAgent() || requestedArtifactType || 'content';
            detection = {
              shouldCreateArtifact: true,
              artifactType: forcedType,
              title: deriveFallbackTitle(forcedType),
            } as any;
          }
        }

        const explicitAttachmentUnresolved =
          !!explicitAttachedArtifactId
          && !artifacts.some((candidate) => candidate.id === explicitAttachedArtifactId);
        if (explicitAttachmentUnresolved && !isArtifactTransformResult) {
          console.warn('Attached artifact could not be resolved in local state; skipping auto artifact creation', {
            attachedArtifactId: explicitAttachedArtifactId,
            runId: resolvedRunId,
          });
          setProcessedMessageIds((prev) => new Set(prev).add(lastMessage.id));
          return;
        }
        
        if (detection.shouldCreateArtifact && detection.artifactType && detection.title) {
          let createdArtifact: ArtifactContent;
          const currentConversationId = currentConversation?.id;
          const isScopedArtifact = (candidate: ArtifactContent | null | undefined): candidate is ArtifactContent => {
            if (!candidate) return false;
            if (!currentConversationId) return true;
            if (candidate.sourceConversationId && candidate.sourceConversationId !== currentConversationId) return false;
            if (!candidate.sourceConversationId && candidate.isSavedToLibrary) return false;
            return true;
          };
          const scopedArtifacts = artifacts.filter((candidate) => isScopedArtifact(candidate));
          
          // Check if this is an update to existing content
          if ((updateDetection.isUpdateIntent || !!explicitAttachedArtifactId) && scopedArtifacts.length > 0) {
          // Enhanced artifact finding logic for better context tracking
          let targetArtifact: ArtifactContent | null = null;
          
          // Priority 0: explicit attachment from the user message
          if (explicitAttachedArtifactId) {
            const attachedTarget =
              scopedArtifacts.find((candidate) => candidate.id === explicitAttachedArtifactId)
              || artifacts.find((candidate) => candidate.id === explicitAttachedArtifactId)
              || null;
            if (attachedTarget) {
              targetArtifact = attachedTarget;
              console.log('Using explicitly attached artifact for update:', attachedTarget.id);
            }
          }

          // Priority 1: If user specifically mentions existing artifact or we have an active one
          if (!targetArtifact && activeArtifact && isScopedArtifact(activeArtifact) && updateDetection.confidence > 0.3) {
            // Check if the update request could apply to the active artifact
            const activeSimilarity = calculateContentSimilarity(content, activeArtifact.content);
            const typeMatches = !detection.artifactType || activeArtifact.type === detection.artifactType;
            
            if (activeSimilarity > 0.2 || typeMatches) {
              targetArtifact = activeArtifact;
              console.log('Using active artifact for update:', activeArtifact.id, 'similarity:', activeSimilarity);
            }
          }
          
          // Priority 2: Find similar content by type and similarity
          if (!targetArtifact) {
            let similarArtifact: ArtifactContent | null = null;
            let bestSimilarity = 0;
            for (const candidate of scopedArtifacts) {
              if (detection.artifactType && candidate.type !== detection.artifactType) continue;
              const similarity = calculateContentSimilarity(content, candidate.content || '');
              if (similarity > 0.3 && similarity > bestSimilarity) {
                bestSimilarity = similarity;
                similarArtifact = candidate;
              }
            }
            if (similarArtifact && updateDetection.confidence > 0.3) {
              targetArtifact = similarArtifact;
              console.log('Found similar artifact for update:', similarArtifact.id);
            }
          }
          
          // Priority 3: Most recent artifact of the same type
          if (!targetArtifact) {
            const sameTypeArtifacts = scopedArtifacts.filter(a => a.type === detection.artifactType);
            if (sameTypeArtifacts.length > 0 && updateDetection.confidence > 0.4) {
              targetArtifact = sameTypeArtifacts[sameTypeArtifacts.length - 1];
              console.log('Using most recent same-type artifact:', targetArtifact.id);
            }
          }
          
          // Priority 4: Most recent artifact if high confidence update intent
          if (!targetArtifact && scopedArtifacts.length > 0 && updateDetection.confidence > 0.6) {
            targetArtifact = scopedArtifacts[scopedArtifacts.length - 1];
            console.log('Using most recent artifact with high confidence:', targetArtifact.id);
          }
          
          const multipass = (lastMessage as any).agentMetadata?.multiPass as { refined?: boolean; finalReview?: boolean } | undefined;
          const badges = [
            multipass?.refined ? 'Refined' : null,
            multipass?.finalReview ? 'Final review' : null,
          ].filter(Boolean) as string[];

          if (targetArtifact) {
            console.log('Updating artifact:', targetArtifact.id, 'with confidence:', updateDetection.confidence);
            const beforeArtifactText = targetArtifact.content || '';
            
            // Update existing artifact with version
            const updateReason = updateDetection.editReason
              || (explicitAttachedArtifactId
                ? 'Updated explicitly attached artifact based on user request.'
                : `Updated based on: "${userMessageContent.substring(0, 50)}..."`);
            updateArtifactWithVersion(
              targetArtifact.id,
              content,
              detection.title,
              updateReason
            );
            void handlePreferenceDiffExtraction({
              beforeText: beforeArtifactText,
              afterText: content,
              runId: (lastMessage as any)?.id,
              messageId: targetArtifact.id,
              source: 'chat_revision_update_intent',
            });
            
            // Use the updated artifact for reference
            createdArtifact = {
              ...targetArtifact,
              content,
              title: detection.title,
              version: targetArtifact.version + 1,
              lastUpdated: new Date(),
              ...(resolvedRunId ? { runId: resolvedRunId } : {}),
              ...(targetArtifact.artifactKey ? { artifactKey: targetArtifact.artifactKey } : {}),
              ...(targetArtifact.lineageId
                ? { lineageId: targetArtifact.lineageId }
                : targetArtifact.artifactKey
                  ? { lineageId: targetArtifact.artifactKey }
                  : {}),
              ...(threadedExtras.targets ? { targets: threadedExtras.targets } : {}),
              ...(threadedExtras.sourcesEnriched ? { sourcesEnriched: threadedExtras.sourcesEnriched } : {}),
              ...(threadedExtras.qaIssues ? { qaIssues: threadedExtras.qaIssues } : {}),
              ...(threadedExtras.validationIssues ? { validationIssues: threadedExtras.validationIssues } : {}),
              // PHASE-04 START
              ...(threadedExtras.researcher ? { researcher: threadedExtras.researcher } : {}),
              // PHASE-04 END
              ...(threadedExtras.format ? { format: threadedExtras.format } : {}),
              policyIssues,
              ...(consistencyMeta ? { consistency: consistencyMeta } : {}),
              ...(autoFixMeta ? { autoFix: autoFixMeta } : {}),
              ...(pillarsMeta ? { pillars: pillarsMeta } : {}),
              ...(knowledgePackMeta ? { knowledgePack: knowledgePackMeta } : {}),
              ...(supervisorMeta ? { supervisorMeta } : {}),
            };
            if (badges.length && !createdArtifact.badges) {
              (createdArtifact as any).badges = badges;
            }
            
            // Set as active artifact
            setActiveArtifact(createdArtifact);
            setAttachedArtifact(createdArtifact);
            // Also patch stored artifact extras so lists/renderers see the updates
            setArtifacts((prev) => prev.map((a) => (
              a.id === targetArtifact.id
                ? {
                    ...a,
                    ...(threadedExtras.targets ? { targets: threadedExtras.targets } : {}),
                    ...(threadedExtras.sourcesEnriched ? { sourcesEnriched: threadedExtras.sourcesEnriched } : {}),
                    ...(threadedExtras.qaIssues ? { qaIssues: threadedExtras.qaIssues } : {}),
                    ...(threadedExtras.validationIssues ? { validationIssues: threadedExtras.validationIssues } : {}),
                    // PHASE-04 START
                    ...(threadedExtras.researcher ? { researcher: threadedExtras.researcher } : {}),
                    // PHASE-04 END
                    ...(threadedExtras.format ? { format: threadedExtras.format } : {}),
                    ...(resolvedRunId ? { runId: resolvedRunId } : {}),
                    ...(a.artifactKey ? { artifactKey: a.artifactKey } : {}),
                    ...(a.lineageId
                      ? { lineageId: a.lineageId }
                      : a.artifactKey
                        ? { lineageId: a.artifactKey }
                        : {}),
                    policyIssues,
                    ...(consistencyMeta ? { consistency: consistencyMeta } : {}),
                    ...(autoFixMeta ? { autoFix: autoFixMeta } : {}),
                    ...(pillarsMeta ? { pillars: pillarsMeta } : {}),
                    ...(knowledgePackMeta ? { knowledgePack: knowledgePackMeta } : {}),
                    ...(supervisorMeta ? { supervisorMeta } : {}),
                  }
                : a
            )));
          } else {
            console.log('No suitable artifact found for update, creating new one');
            // Create new artifact (no suitable target found)
            const generatedLineageId = `${resolvedRunId || 'run'}:${detection.artifactType}:${Date.now()}`;
            createdArtifact = addArtifact({
              type: detection.artifactType,
              title: detection.title,
              content,
              agentType: (lastMessage as any).agentMetadata?.agentType || 'ai',
              ...(currentConversation?.id ? { sourceConversationId: currentConversation.id } : {}),
              ...(resolvedRunId ? { runId: resolvedRunId } : {}),
              artifactKey: generatedLineageId,
              lineageId: generatedLineageId,
              badges,
              ...(threadedExtras.targets ? { targets: threadedExtras.targets } : {}),
              ...(threadedExtras.sourcesEnriched ? { sourcesEnriched: threadedExtras.sourcesEnriched } : {}),
              ...(threadedExtras.qaIssues ? { qaIssues: threadedExtras.qaIssues } : {}),
              ...(threadedExtras.validationIssues ? { validationIssues: threadedExtras.validationIssues } : {}),
              // PHASE-04 START
              ...(threadedExtras.researcher ? { researcher: threadedExtras.researcher } : {}),
              // PHASE-04 END
              ...(threadedExtras.format ? { format: threadedExtras.format } : {}),
              policyIssues,
              ...(consistencyMeta ? { consistency: consistencyMeta } : {}),
              ...(autoFixMeta ? { autoFix: autoFixMeta } : {}),
              ...(pillarsMeta ? { pillars: pillarsMeta } : {}),
              ...(knowledgePackMeta ? { knowledgePack: knowledgePackMeta } : {}),
              ...(supervisorMeta ? { supervisorMeta } : {}),
            });
          }
        } else {
          // Create new artifact (not an update request)
          const generatedLineageId = `${resolvedRunId || 'run'}:${detection.artifactType}:${Date.now()}`;
          createdArtifact = addArtifact({
            type: detection.artifactType,
            title: detection.title,
            content,
            agentType: (lastMessage as any).agentMetadata?.agentType || 'ai',
            ...(currentConversation?.id ? { sourceConversationId: currentConversation.id } : {}),
            ...(resolvedRunId ? { runId: resolvedRunId } : {}),
            artifactKey: generatedLineageId,
            lineageId: generatedLineageId,
            badges: [
              ((lastMessage as any).agentMetadata?.multiPass?.refined ? 'Refined' : null),
              ((lastMessage as any).agentMetadata?.multiPass?.finalReview ? 'Final review' : null),
            ].filter(Boolean) as string[],
            ...(threadedExtras.targets ? { targets: threadedExtras.targets } : {}),
            ...(threadedExtras.sourcesEnriched ? { sourcesEnriched: threadedExtras.sourcesEnriched } : {}),
            ...(threadedExtras.qaIssues ? { qaIssues: threadedExtras.qaIssues } : {}),
            ...(threadedExtras.validationIssues ? { validationIssues: threadedExtras.validationIssues } : {}),
            // PHASE-04 START
            ...(threadedExtras.researcher ? { researcher: threadedExtras.researcher } : {}),
            // PHASE-04 END
            ...(threadedExtras.format ? { format: threadedExtras.format } : {}),
            policyIssues,
            ...(consistencyMeta ? { consistency: consistencyMeta } : {}),
            ...(autoFixMeta ? { autoFix: autoFixMeta } : {}),
            ...(pillarsMeta ? { pillars: pillarsMeta } : {}),
            ...(knowledgePackMeta ? { knowledgePack: knowledgePackMeta } : {}),
            ...(supervisorMeta ? { supervisorMeta } : {}),
          });
        }
        
        setAttachedArtifact(createdArtifact);
        // Don't auto-open artifacts panel - let users use floating action buttons
        // setSidebarPanel('artifacts');
        
        // Create artifact reference message using the artifact ID
        const referenceMessage = createArtifactReferenceMessage({
          id: createdArtifact.id,
          type: detection.artifactType,
          title: detection.title,
          timestamp: createdArtifact.timestamp || new Date(),
          agentMetadata: (lastMessage as any).agentMetadata,
          content: createdArtifact.content,
          ...(createdArtifact.runId ? { runId: createdArtifact.runId } : {}),
          ...(createdArtifact.checkpointId ? { checkpointId: createdArtifact.checkpointId } : {}),
          ...(createdArtifact.artifactKey ? { artifactKey: createdArtifact.artifactKey } : {}),
          ...(createdArtifact.lineageId ? { lineageId: createdArtifact.lineageId } : {}),
          // PHASE-02 START
          meta: (lastMessage as any).meta
          // PHASE-02 END
        });
        
        // Replace the original message with the artifact reference
        replaceMessage(lastMessage.id, referenceMessage);
        void persistChatArtifactToContent(createdArtifact);
        void appendArtifactFeedbackPrompt(createdArtifact);
        // PHASE-02 START
        const memoryReferences = Array.isArray((lastMessage as any)?.meta?.memories)
          ? ((lastMessage as any).meta.memories as MemoryReference[])
          : undefined;
        const provenance = ((lastMessage as any)?.meta?.provenance || null) as RunMeta | null;
        void autoSaveArtifact(createdArtifact, memoryReferences, provenance);
        // PHASE-02 END
        
        // Mark as processed to avoid re-processing
        setProcessedMessageIds(prev => new Set(prev).add(lastMessage.id));
        }
      } else {
        // Mark as processed even if no artifact created
        if (lastMessage?.id) {
          setProcessedMessageIds(prev => new Set(prev).add(lastMessage.id));
        }
      }
    }, 10); // Minimal delay for immediate artifact detection

    return () => clearTimeout(timeoutId);
  }, [
    messages.length,
    addArtifact,
    updateArtifactWithVersion,
    createArtifactReferenceMessage,
    replaceMessage,
    appendArtifactFeedbackPrompt,
    persistChatArtifactToContent,
    autoSaveArtifact,
    handlePreferenceDiffExtraction,
  ]);
  
  // Load client info if there's a clientId in the conversation
  useEffect(() => {
    let cancelled = false;

    const loadClientInfo = async () => {
      if (currentConversation?.clientId && (!activeClient || activeClient.id !== currentConversation.clientId)) {
        try {
          const client = await fetchClientById(currentConversation.clientId);
          if (!cancelled && client && client.id === currentConversation.clientId) {
            setActiveClient(client);
          }
        } catch (error) {
          console.error('Failed to load client:', error);
        }
      }
    };
    
    void loadClientInfo();

    return () => {
      cancelled = true;
    };
  }, [currentConversation?.clientId, activeClient?.id, fetchClientById, setActiveClient]);

  // Reset artifacts when switching conversations or client context
  useEffect(() => {
    // Clear ephemeral artifacts when the conversation or its client changes
    // so the sidebar reflects the current context immediately
    clearArtifacts();
    // Also reset virtualized counter for list rendering
    setArtifactVisibleCount(50);
  }, [currentConversation?.id, currentConversation?.clientId, activeClient?.id, clearArtifacts]);

  const lastAiMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i] as any;
      if ((message?.sender === 'ai' || message?.isAI) && message?.content && !message?.isLoading) {
        return message;
      }
    }
    return null;
  }, [messages]);

  const shouldShowPreferenceToggle = useMemo(() => {
    const hasClientContext = Boolean(currentConversation?.clientId || activeClient?.id);
    if (!hasClientContext || !composerText.trim()) return false;
    const updateIntent = detectUpdateIntent(composerText, messages.slice(-5).map((m) => m.content || m.text || ''));
    const similarity = lastAiMessage ? calculateContentSimilarity(composerText, lastAiMessage.content || '') : 0;
    const looksLikeEdit = composerText.trim().length > 120 && similarity > 0.3;
    return updateIntent.isUpdateIntent || looksLikeEdit;
  }, [activeClient?.id, composerText, currentConversation?.clientId, lastAiMessage, messages]);

  useEffect(() => {
    if (!shouldShowPreferenceToggle) {
      setSaveAsPreference(false);
    }
  }, [currentConversation?.id, shouldShowPreferenceToggle]);

  useEffect(() => {
    if (!currentConversation?.id || learningPrompt) return;
    const clientId = currentConversation?.clientId || activeClient?.id;
    if (!clientId) return;
    if (messages.length < 2) return;

    const getText = (message: any) => (message?.content || message?.text || '').toString();
    const isClarification = (message: any) =>
      message?.messageKind === 'clarification' || message?.messageKind === 'clarification_skip';
    const isSubstantial = (text: string) => isSubstantialContent(text) && text.trim().length > 160;

    const inferTaskType = (text: string) => {
      const detection = detectArtifactContent(text, undefined);
      if (!detection.shouldCreateArtifact) return undefined;
      switch (detection.artifactType) {
        case 'press_release':
          return 'press_release';
        case 'media_pitch':
          return 'media_pitch';
        case 'social_media':
          return 'social';
        case 'email':
          return 'email';
        case 'statement':
          return 'statement';
        case 'content':
          return 'content';
        default:
          return undefined;
      }
    };

    const last = messages[messages.length - 1] as any;
    const prev = messages[messages.length - 2] as any;
    const prev2 = messages.length > 2 ? (messages[messages.length - 3] as any) : null;

    if (last?.sender === 'user' && prev?.sender === 'ai' && !isClarification(last)) {
      const afterText = getText(last);
      const beforeText = getText(prev);
      if (isSubstantial(afterText) && isSubstantial(beforeText)) {
        const similarity = calculateContentSimilarity(afterText, beforeText);
        if (similarity > 0.35 && similarity < 0.95) {
          const updateIntent = detectUpdateIntent(afterText, messages.slice(-5).map((m) => m.content || m.text || ''));
          const promptKey = `${prev?.id || 'ai'}:${last?.id || 'user'}:user_edit`;
          if (learningPromptKeyRef.current !== promptKey) {
            learningPromptKeyRef.current = promptKey;
            setLearningPrompt({
              id: promptKey,
              clientId,
              beforeText,
              afterText,
              userFeedback: updateIntent.editReason || 'User provided edited draft.',
              locale: lang,
              taskType: inferTaskType(afterText) || inferTaskType(beforeText),
              runId: prev?.id,
            });
            if (preferenceExtractionKeyRef.current !== promptKey) {
              preferenceExtractionKeyRef.current = promptKey;
              void handlePreferenceDiffExtraction({
                beforeText,
                afterText,
                runId: prev?.id,
                messageId: prev?.id,
                source: 'chat_user_manual_edit',
              });
            }
          }
        }
      }
    }

    if (last?.sender === 'ai' && prev?.sender === 'user' && prev2?.sender === 'ai' && !isClarification(prev)) {
      const userText = getText(prev);
      const beforeText = getText(prev2);
      const afterText = getText(last);
      const updateIntent = detectUpdateIntent(userText, messages.slice(-5).map((m) => m.content || m.text || ''));
      if (updateIntent.isUpdateIntent && isSubstantial(beforeText) && isSubstantial(afterText)) {
        const similarity = calculateContentSimilarity(afterText, beforeText);
        if (similarity < 0.9) {
          const promptKey = `${prev2?.id || 'ai'}:${last?.id || 'ai'}:instruction_edit`;
          if (learningPromptKeyRef.current !== promptKey) {
            learningPromptKeyRef.current = promptKey;
            setLearningPrompt({
              id: promptKey,
              clientId,
              beforeText,
              afterText,
              userFeedback: updateIntent.editReason || userText.trim(),
              locale: lang,
              taskType: inferTaskType(afterText) || inferTaskType(beforeText),
              runId: last?.id,
            });
            if (preferenceExtractionKeyRef.current !== promptKey) {
              preferenceExtractionKeyRef.current = promptKey;
              void handlePreferenceDiffExtraction({
                beforeText,
                afterText,
                runId: last?.id,
                messageId: last?.id,
                source: 'chat_revision_response',
              });
            }
          }
        }
      }
    }
  }, [
    activeClient?.id,
    currentConversation?.clientId,
    currentConversation?.id,
    handlePreferenceDiffExtraction,
    lang,
    learningPrompt,
    messages,
  ]);

  const hasMessages = messages.length > 0;
  const activeClarifyIndex = clarifyPending ? Math.max(clarifyQueue.findIndex((req: any) => req.id === clarifyPending.id), 0) : -1;
  const clarifyStepLabel = clarifyPending ? `${activeClarifyIndex + 1} of ${clarifyQueue.length}` : null;
  const activeClarifyAgent = clarifyPending?.agentLabel || 'Writer';
  const waitingForClarifyResumeHealth = !!(clarifySubmissionPending && !clarifySubmissionPending.timedOut && !clarificationResumeHealthy);
  const showResumeBanner = !!(
    clarifyPending
    && clarifyUIHidden
    && dismissedClarifyBannerId !== clarifyPending.id
    && !waitingForClarifyResumeHealth
  );
  const persistentHandoffMessage = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const candidate = messages[index];
      if (isActiveHandoffMessage(candidate)) return candidate;
    }
    return null;
  }, [messages]);
  const persistentHandoffMessageId = persistentHandoffMessage?.id || null;
  const persistentHandoffRunKey = persistentHandoffMessage ? getMessageRunKey(persistentHandoffMessage) : '';
  const persistentHandoffData = persistentHandoffMessage?.agentHandoffData || null;
  const persistentHandoffInstanceRef = useRef<{
    instanceKey: string;
    messageId: string | null;
    runKey: string;
  } | null>(null);
  const persistentHandoffInstanceKey = useMemo(() => {
    if (!persistentHandoffData) {
      persistentHandoffInstanceRef.current = null;
      return '';
    }

    const previous = persistentHandoffInstanceRef.current;
    const sameKnownRun = !!(
      previous
      && persistentHandoffRunKey
      && previous.runKey
      && previous.runKey === persistentHandoffRunKey
    );
    const sameSourceMessage = !!(
      previous
      && persistentHandoffMessageId
      && previous.messageId === persistentHandoffMessageId
    );
    const promotedTempToPendingRun = !!(
      previous
      && previous.messageId?.startsWith('temp-')
      && persistentHandoffMessageId?.startsWith('pending-')
    );

    if (previous && (sameKnownRun || sameSourceMessage || promotedTempToPendingRun)) {
      persistentHandoffInstanceRef.current = {
        instanceKey: previous.instanceKey,
        messageId: persistentHandoffMessageId,
        runKey: persistentHandoffRunKey,
      };
      return previous.instanceKey;
    }

    const nextKey = persistentHandoffRunKey || persistentHandoffMessageId || 'active-handoff';
    persistentHandoffInstanceRef.current = {
      instanceKey: nextKey,
      messageId: persistentHandoffMessageId,
      runKey: persistentHandoffRunKey,
    };
    return nextKey;
  }, [persistentHandoffData, persistentHandoffMessageId, persistentHandoffRunKey]);

  return (
    <div className={cn("chat-window-container relative h-full flex", fullWidth && "w-full")}>
      {/* Focus mode: no vignettes — toolbar and stats are hidden inline below */}
      {/* Main Chat Area - flexible width */}
      <div
        className={cn(
          'flex flex-col flex-1 min-w-0 relative h-full transition-all duration-300 ease-in-out',
          focusMode && 'bg-transparent'
        )}
        style={{ marginRight: showSidebar && !isCompactViewport ? `${sidebarWidth}px` : '0px' }}
      >
        {/* Messages container with optimized spacing */}
        <div
          className="flex-1 min-h-0 overflow-y-auto pb-2"
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
          ref={listRef}
          onScroll={() => {
            const el = listRef.current;
            if (!el) return;
            // Load more above when near top
            if (el.scrollTop < 200 && visibleCount < messages.length) {
              setVisibleCount((v) => Math.min(messages.length, v + 50));
            }
            // Toggle jump-to-latest when away from bottom
            const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
            setShowJumpToLatest(distanceFromBottom > 300);
            setShowJumpToOldest(el.scrollTop > 300);
            if (distanceFromBottom <= 10) {
              markSeenNow();
            }
          }}
        >
          {loading && !hasMessages ? (
            <div className="p-4">
              <LiquidGlassCard variant="default" className="p-4" hoverable={false}>
                <div className={cn(fullWidth ? 'max-w-none' : 'max-w-4xl mx-auto')}>
                  <MessageSkeleton />
                </div>
              </LiquidGlassCard>
            </div>
          ) : !hasMessages ? (
            <EmptyState
              activeClient={activeClient}
              onQuickAction={(template) => {
                setComposerText(template);
                requestAnimationFrame(() => {
                  const input = document.querySelector('textarea[name="message"]');
                  if (input instanceof HTMLTextAreaElement) input.focus();
                });
              }}
            />
          ) : (
            <div className="p-4 pb-2">
              <div className={cn(fullWidth ? 'max-w-none' : 'max-w-4xl mx-auto', 'space-y-4')}>
                {!focusMode && (agentKnowledgeDisplay.hasKnowledge || preferencesLoading || preferenceSuggestion) && (
                  <div className="space-y-2">
                    {preferenceSuggestion && (
                      <PreferenceConfirmBanner
                        mode="suggestion"
                        preferences={preferenceSuggestion.updates}
                        provenance={preferenceProvenance}
                        confidence={preferenceSuggestion.calibratedConfidence}
                        explanation={preferenceSuggestion.explanation}
                        onConfirm={handlePreferenceSuggestionConfirm}
                        onChange={() => {
                          openPreferencesDrawer();
                          setPreferenceSuggestion(null);
                        }}
                        onReject={handlePreferenceSuggestionReject}
                      />
                    )}
                    <WhatAgentKnowsPanel
                      model={agentKnowledgeDisplay}
                      loading={preferencesLoading}
                      onEditPreference={(field) => openPreferencesDrawer(field)}
                      onOpenClarifications={() => setShowClarificationsDrawer(true)}
                      onClearClarification={(type) => {
                        if (!currentConversation?.id) return;
                        void clearClarifications(currentConversation.id, type);
                      }}
                    />
                  </div>
                )}
                {/* View controls — frosted glass toolbar */}
                <div className={cn(
                  'sticky top-0 z-20 mb-4 flex min-h-11 flex-col gap-2 rounded-xl border border-border/50 bg-background/95 p-2 shadow-sm backdrop-blur-sm',
                  showSidebar && !isCompactViewport
                    ? 'items-stretch'
                    : 'sm:flex-row sm:items-center sm:justify-between',
                  focusMode && 'p-1 mb-2'
                )}>
                  <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                    {/* Focus mode toggle */}
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setFocusMode((v) => !v)}
                      className={cn('h-10 w-10 shrink-0 hover:bg-muted sm:h-9 sm:w-9', focusMode && 'bg-primary/10 text-primary')}
                      title={focusMode ? 'Exit Focus Mode' : 'Focus Mode'}
                    >
                      {focusMode ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
                    </Button>
                    {/* Remaining toolbar controls — hidden in focus mode */}
                    {!focusMode && (
                      <>
                        {/* Auto-save toggle */}
                        {flags.autoSaveArtifacts && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => setAutoSaveEnabled((v) => !v)}
                            className={cn(
                              'relative h-10 w-10 shrink-0 hover:bg-muted sm:h-9 sm:w-9',
                              autoSaveEnabled ? 'text-primary' : 'text-muted-foreground'
                            )}
                            title={`Auto-save ${autoSaveEnabled ? 'On' : 'Off'}`}
                          >
                            <CloudArrowUpIcon className="h-4 w-4" />
                            {autoSaveEnabled && (
                              <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-success" />
                            )}
                          </Button>
                        )}
                        {/* Locale segmented control */}
                        <div className="flex items-center rounded-lg border border-border/40 overflow-hidden">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleLanguageChange('en')}
                            disabled={!currentConversation}
                            className={cn(
                              'h-10 min-h-10 rounded-none px-3 py-1 text-xs sm:h-7 sm:min-h-0',
                              lang === 'en' ? 'bg-primary/15 text-primary font-semibold' : 'text-muted-foreground'
                            )}
                          >
                            EN
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleLanguageChange('ar')}
                            disabled={!currentConversation}
                            className={cn(
                              'h-10 min-h-10 rounded-none px-3 py-1 text-xs sm:h-7 sm:min-h-0',
                              lang === 'ar' ? 'bg-primary/15 text-primary font-semibold' : 'text-muted-foreground'
                            )}
                          >
                            AR
                          </Button>
                        </div>
                        {/* Artifacts button with count badge */}
                        {!showSidebar && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => openSidebarPanel('artifacts')}
                            className="relative h-10 w-10 shrink-0 hover:bg-muted sm:h-9 sm:w-9"
                            title="Open Artifacts"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                            {artifacts.length > 0 && (
                              <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] flex items-center justify-center text-xs font-bold bg-primary text-primary-foreground rounded-full px-1">
                                {artifacts.length}
                              </span>
                            )}
                          </Button>
                        )}
                        {/* Three-dot menu */}
                        <Popover
                          asChild
                          align="right"
                          trigger={
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="h-10 w-10 shrink-0 hover:bg-muted sm:h-9 sm:w-9"
                              aria-label="Conversation options"
                              disabled={!currentConversation}
                            >
                              <EllipsisVerticalIcon className="h-4 w-4" />
                            </Button>
                          }
                        >
                          <DropdownItem
                            onSelect={() => {
                              if (isClearingClarifications) return;
                              void handleClearAllClarifications();
                            }}
                          >
                            <span
                              className={[
                                'flex items-center gap-2 text-sm',
                                isClearingClarifications ? 'text-warning' : 'text-warning'
                              ].join(' ')}
                            >
                              {isClearingClarifications ? (
                                <span className="h-4 w-4 animate-spin rounded-full border border-warning border-t-transparent" />
                              ) : (
                                <TrashIcon className="h-4 w-4" />
                              )}
                              {isClearingClarifications
                                ? 'Clearing...'
                                : clarificationCount > 0
                                  ? `Clear Clarifications (${clarificationCount})`
                                  : 'Clear Clarifications'}
                            </span>
                          </DropdownItem>
                        </Popover>
                      </>
                    )}
                  </div>
                  {!focusMode && !showSidebar && <span className="hidden truncate whitespace-nowrap text-xs text-muted-foreground/60 sm:block">Enter to send &middot; Shift+Enter newline</span>}
                </div>

                {/* Real-time learning stats (deferred) */}
                {!focusMode && showStats && <RealTimeLearningStats />}
                
                <div className="space-y-4">
                  {/* Basic virtualization: render from bottom with incremental load on scroll-up */}
                  {(() => {
                    const start = Math.max(0, messages.length - visibleCount);
                    const slice = messages.slice(start);
                    return slice.map((message, i) => {
                      const index = start + i;
                    // Date separator per day
                    const currentDate = (() => {
                      const t = (message as any).timestamp;
                      let d: Date | null = null;
                      if (!t) return null;
                      if (t instanceof Date) d = t; else if (typeof t === 'number') d = new Date(t); else if (typeof t === 'object' && (t as any).toDate) d = (t as any).toDate();
                      return d ? d.toDateString() : null;
                    })();
                    const prev = messages[index - 1];
                    const prevDate = (() => {
                      if (!prev) return null;
                      const t = (prev as any).timestamp;
                      let d: Date | null = null;
                      if (!t) return null;
                      if (t instanceof Date) d = t; else if (typeof t === 'number') d = new Date(t); else if (typeof t === 'object' && (t as any).toDate) d = (t as any).toDate();
                      return d ? d.toDateString() : null;
                    })();
                    const showDateChip = !!currentDate && currentDate !== prevDate;
                    // Grouping: same sender within 5 minutes
                    const sameSender = !!prev && (prev as any).sender === (message as any).sender;
                    const withinFiveMinutes = (() => {
                      try {
                        const currTs = (message as any).timestamp;
                        const prevTs = (prev as any)?.timestamp;
                        if (!currTs || !prevTs) return false;
                        const toMillis = (t: any) => t instanceof Date ? t.getTime() : (typeof t === 'number' ? t : (typeof t === 'object' && t.toDate ? t.toDate().getTime() : NaN));
                        const dt = Math.abs(toMillis(currTs) - toMillis(prevTs));
                        return isFinite(dt) && dt <= 5 * 60 * 1000;
                      } catch {
                        return false;
                      }
                    })();
                    const compact = sameSender && withinFiveMinutes;
                    // Handle artifact reference messages differently (shared card across both render modes)
                    if ((message as any).isArtifactReference) {
                      const artifactCard = (
                        <ArtifactReferenceCard
                          artifactId={(message as any).artifactReference?.artifactId}
                          artifactTitle={(message as any).artifactReference?.artifactTitle}
                          memories={(message as any).meta?.memories}
                          onArtifactClick={handleArtifactClick}
                        >
                          {showResumeBanner && clarifyPending && (
                            <div className="mt-2">
                              <ClarificationResumeBanner
                                agentLabel={activeClarifyAgent}
                                pendingCount={clarifyQueue.length}
                                onResume={() => {
                                  setClarifyUIHidden(false);
                                  setClarifySubmissionPending(null);
                                  setDismissedClarifyBannerId(null);
                                  setTimeout(() => {
                                    const anchor = document.getElementById('clarify-form-anchor');
                                    anchor?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }, 30);
                                }}
                                onDismiss={() => setDismissedClarifyBannerId(clarifyPending.id)}
                              />
                            </div>
                          )}
                        </ArtifactReferenceCard>
                      );
                      return (
                        <React.Fragment key={(message as any).id || `message-${index}`}>
                          {showDateChip && (
                            <div className="sticky top-2 z-10 flex justify-center py-2">
                              <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground/90 border border-border/60 shadow-sm">
                                {currentDate}
                              </span>
                            </div>
                          )}
                          {renderMode === 'article' ? (
                            <article className="group py-5 px-6 article-glass-ai">
                              <div className={cn(fullWidth ? 'max-w-none' : 'max-w-3xl mx-auto')}>
                                {artifactCard}
                              </div>
                            </article>
                          ) : (
                            artifactCard
                          )}
                        </React.Fragment>
                      );
                    }
                    
                    const messageKey = (message as any).id || `message-${index}`;
                    const isPersistentHandoffSource = !!(
                      persistentHandoffMessageId
                      && (message as any).id === persistentHandoffMessageId
                    );
                    const isSamePersistentHandoffRun = !!(
                      persistentHandoffRunKey
                      && isActiveHandoffMessage(message)
                      && getMessageRunKey(message) === persistentHandoffRunKey
                    );
                    const shouldHidePersistentSourceBubble =
                      (isPersistentHandoffSource || isSamePersistentHandoffRun)
                      && isHandoffStatusOnlyMessage(message);
                    const displayMessage = isPersistentHandoffSource || isSamePersistentHandoffRun
                      ? { ...(message as any), isLoading: false, agentHandoffData: undefined }
                      : message;
                    if (shouldHidePersistentSourceBubble) {
                      return showDateChip ? (
                        <React.Fragment key={messageKey}>
                          <div className="sticky top-2 z-10 flex justify-center py-2">
                            <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground/90 border border-border/60 shadow-sm">
                              {currentDate}
                            </span>
                          </div>
                        </React.Fragment>
                      ) : null;
                    }
                    const messageId = (message as any).id;
                    const feedbackForMessage = messageId ? reviewerFeedback[messageId] : undefined;
                    const agentRunDisplay = SHOW_AGENT_DIAGNOSTICS && (message as any).sender === 'ai'
                      ? buildAgentRunDisplayFromMessage(displayMessage)
                      : null;
                    const decisionTrace = SHOW_AGENT_DIAGNOSTICS && (message as any).sender === 'ai'
                      ? buildDecisionTraceFromMessage(displayMessage)
                      : null;
                    const runDetails = SHOW_AGENT_DIAGNOSTICS && (message as any).sender === 'ai'
                      ? buildRunDetailsFromMessage(displayMessage)
                      : null;

                    // Handle clarification history blocks (both article and bubble)
                    if (
                      ((message as any).messageKind === 'clarification' ||
                        (message as any).messageKind === 'clarification_skip') &&
                      (message as any).clarification
                    ) {
                      return (
                        <React.Fragment key={messageKey}>
                          {showDateChip && (
                            <div className="sticky top-2 z-10 flex justify-center py-2">
                              <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground/90 border border-border/60 shadow-sm">
                                {currentDate}
                              </span>
                            </div>
                          )}
                          <ClarificationHistoryBlock message={message as any} />
                        </React.Fragment>
                      );
                    }

                    if ((message as any).messageKind === 'artifact_feedback_prompt') {
                      const linkage = parseArtifactFeedbackLinkage(message);
                      const linkedArtifact = linkage
                        ? artifacts.find((artifact) => artifact.id === linkage.artifactId) || null
                        : null;
                      const busyPromptId = artifactFeedbackBusyPromptId;
                      const promptId = typeof (message as any).id === 'string' ? (message as any).id : linkage?.promptMessageId || linkage?.artifactId || '';
                      const isBusy = !!busyPromptId && busyPromptId === promptId;
                      return (
                        <React.Fragment key={messageKey}>
                          {showDateChip && (
                            <div className="sticky top-2 z-10 flex justify-center py-2">
                              <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground/90 border border-border/60 shadow-sm">
                                {currentDate}
                              </span>
                            </div>
                          )}
                          <LiquidGlassCard variant="default" className="p-4">
                            <div className="space-y-3">
                              <div>
                                <p className="text-sm font-medium text-foreground">Draft ready for review</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {linkedArtifact
                                    ? sanitizeArtifactTitle(linkedArtifact.title, 'Artifact')
                                    : 'Choose what to do with this draft.'}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="primary"
                                  disabled={!linkage || isBusy}
                                  onClick={() => void handleArtifactFeedbackPromptAction(message, 'approve')}
                                >
                                  {isBusy ? 'Working…' : 'Approve'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={!linkage || isBusy}
                                  onClick={() => void handleArtifactFeedbackPromptAction(message, 'request_changes')}
                                >
                                  Request changes
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={!linkage || isBusy}
                                  onClick={() => void handleArtifactFeedbackPromptAction(message, 'discuss')}
                                >
                                  Discuss in chat
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={!linkage || isBusy}
                                  onClick={() => void handleArtifactFeedbackPromptAction(message, 'open')}
                                >
                                  Open artifact
                                </Button>
                              </div>
                            </div>
                          </LiquidGlassCard>
                        </React.Fragment>
                      );
                    }

                    // Render article-mode messages with full feature set
                    if (renderMode === 'article') {
                      return (
                        <React.Fragment key={messageKey}>
                          {showDateChip && (
                            <div className="sticky top-2 z-10 flex justify-center py-2">
                              <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground/90 border border-border/60 shadow-sm">
                                {currentDate}
                              </span>
                            </div>
                          )}
                          <ArticleMessageComponent
                            message={displayMessage}
                            isUser={(displayMessage as any).sender === 'user'}
                            fullWidth={fullWidth}
                            compact={compact}
                            currentUser={currentUser}
                            activeClient={activeClient}
                            error={false}
                            onFeedback={handleFeedback}
                            onArtifactClick={handleArtifactClick}
                            reviewerFeedback={feedbackForMessage}
                            onRequestChanges={handleRequestChanges}
                            onViewDetails={handleViewDetails}
                          />
                          {SHOW_AGENT_DIAGNOSTICS && agentRunDisplay && (
                            <AgentRunCard run={agentRunDisplay} />
                          )}
                          {SHOW_AGENT_DIAGNOSTICS && (
                            <>
                              <DecisionTracePanel trace={decisionTrace} />
                              <RunDetailsPanel details={runDetails} />
                            </>
                          )}
                          {renderResponseQualityBadges(message)}
                        </React.Fragment>
                      );
                    }
                    return (
                      <React.Fragment key={messageKey}>
                        {showDateChip && (
                          <div className="sticky top-2 z-10 flex justify-center py-2">
                            <span className="text-xs px-3 py-1 rounded-full bg-muted text-muted-foreground/90 border border-border/60 shadow-sm">
                              {currentDate}
                            </span>
                          </div>
                        )}
                        <LiquidGlassMessage
                          role={(displayMessage as any).sender === 'user' ? 'user' : 'assistant'}
                          content={(displayMessage as any).content || (displayMessage as any).text || ""}
                          timestamp={(displayMessage as any).timestamp}
                          loading={(displayMessage as any).isLoading || false}
                          error={false}
                          compact={compact}
                          showTimestamp={!compact}
                          wide={focusMode}
                          agentHandoffData={(displayMessage as any).agentHandoffData}
                          // Optional feedback wiring (non-breaking)
                          onFeedback={handleFeedback}
                          messageId={(displayMessage as any).id}
                          agentMetadata={(displayMessage as any).agentMetadata}
                        >
                        {/* Additional message features */}
                        {SHOW_AGENT_DIAGNOSTICS && agentRunDisplay && (
                          <AgentRunCard run={agentRunDisplay} />
                        )}
                        {SHOW_AGENT_DIAGNOSTICS && (
                          <>
                            <DecisionTracePanel trace={decisionTrace} />
                            <RunDetailsPanel details={runDetails} />
                          </>
                        )}
                        {(message as any).meta?.clarificationAck && (
                          <ClarificationAckNotice ack={(message as any).meta.clarificationAck} />
                        )}
                        {(message as any).meta?.runtimeTool && (
                          <RuntimeToolResultPanel
                            state={(message as any).meta.runtimeTool.state || null}
                            error={(message as any).meta.runtimeTool.error || null}
                            connectionStatus={(message as any).meta.runtimeTool.state?.status || 'completed'}
                            selectedToolLabel={(message as any).meta.runtimeTool.state?.label || null}
                            onOpenArtifact={handleArtifactClick}
                          />
                        )}
                        {(message as any).isEdited && (
                          <div className="text-xs text-muted-foreground mt-2">
                            Edited • {(() => {
                              const editedAt = (message as any).editedAt;
                              if (!editedAt) return '';
                              try {
                                let date: Date;
                                if (editedAt instanceof Date) {
                                  date = editedAt;
                                } else if (typeof editedAt === 'object' && editedAt.toDate) {
                                  date = editedAt.toDate();
                                } else {
                                  date = new Date(editedAt);
                                }
                                return isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              } catch {
                                return '';
                              }
                            })()}
                          </div>
                        )}
                        {/* PHASE-02 START */}
                        {Array.isArray((message as any).meta?.memories) && (message as any).meta.memories.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(message as any).meta.memories.map((memory: any, idx: number) => (
                              <MemoryPill key={`${memory?.id || idx}`} memory={memory} index={idx} />
                            ))}
                          </div>
                        )}
                        {/* PHASE-02 END */}
                        {renderResponseQualityBadges(message)}
                        {message.sender === 'ai' && feedbackForMessage && (
                          <ReviewerCallout
                            feedback={feedbackForMessage}
                            onRequestChanges={() => handleRequestChanges(feedbackForMessage)}
                            onViewDetails={() => handleViewDetails(feedbackForMessage)}
                          />
                        )}
                        {(message as any).agentMetadata && (
                          <div className="text-xs text-muted-foreground mt-2">
                            Generated by {(message as any).agentMetadata.agentType} agent
                          </div>
                        )}
                        </LiquidGlassMessage>
                      </React.Fragment>
                    );
                    });
                  })()}

                {/* Clarification form (interactive) – pinned near bottom of messages */}
                  {clarifyPending && !clarifyUIHidden && (
                    <div id="clarify-form-anchor" className="mt-2">
                      <div className="rounded-2xl border border-info/40 bg-background/80 shadow-lg shadow-info/10 p-4 space-y-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-primary">
                              {activeClarifyAgent} needs details
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {clarifyStepLabel ? `Clarification ${clarifyStepLabel}` : 'Clarification'} · {clarifyPending.type || 'press'} draft
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {clarifyStepLabel && clarifyQueue.length > 1 && (
                              <span className="text-xs px-2 py-1 rounded-full border border-primary/40 text-muted-foreground">
                                Step {clarifyStepLabel}
                              </span>
                            )}
                            <span className="text-xs px-2 py-1 rounded-full border border-primary/40 text-primary bg-primary/10">
                              Clarification
                            </span>
                          </div>
                        </div>
                        <ClarifyForm
                          type={clarifyPending.type || 'press'}
                          questions={clarifyPending.questions}
                          requestId={clarifyPending.id}
                          originRunId={clarifyPending.originRunId}
                          conversationId={currentConversation?.id}
                          language={lang}
                          stepLabel={clarifyStepLabel}
                          agentLabel={activeClarifyAgent}
                          onDismiss={() => setClarifyUIHidden(true)}
                          onSubmit={async (payload) => {
                            const submittedAt = Date.now();
                            setClarifySubmissionPending({ requestId: payload.requestId, startedAt: submittedAt, timedOut: false });
                            try {
                              await sendMessage(payload.compiledText, undefined, {
                                kind: 'clarification',
                                clarification: payload,
                              });
                              try { (toast as any)?.info?.('Got it — drafting now with your clarifications.'); } catch {}
                              setClarifyUIHidden(true);
                            } catch (error) {
                              setClarifySubmissionPending(null);
                              setClarifyUIHidden(false);
                              throw error;
                            }
                          }}
                          onSkip={async (payload) => {
                            const submittedAt = Date.now();
                            setClarifySubmissionPending({ requestId: payload.requestId, startedAt: submittedAt, timedOut: false });
                            try {
                              await sendMessage(payload.compiledText, undefined, {
                                kind: 'clarification_skip',
                                clarification: payload,
                              });
                              try { (toast as any)?.info?.('Continuing with placeholders…'); } catch {}
                              setClarifyUIHidden(true);
                            } catch (error) {
                              setClarifySubmissionPending(null);
                              setClarifyUIHidden(false);
                              throw error;
                            }
                          }}
                        />
                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <LockClosedIcon className="h-3.5 w-3.5 text-muted-foreground/80" />
                            Used to complete this draft and saved in this conversation.
                          </span>
                          <div className="flex items-center gap-3">
                            <a
                              href="/privacy"
                              target="_blank"
                              rel="noreferrer"
                              className="underline-offset-4 hover:underline text-muted-foreground"
                            >
                              Data policy
                            </a>
                            {clarifyLock && (
                              <button
                                type="button"
                                className="text-xs font-medium text-primary hover:text-primary/80"
                                onClick={() => {
                                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                                  setShowJumpToLatest(false);
                                }}
                              >
                                Jump to latest
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {persistentHandoffData && (
                    <div
                      id="agent-handoff-run-anchor"
                      className={cn(
                        'mt-3',
                        clarifyPending && !clarifyUIHidden && 'pt-1'
                      )}
                    >
                      <AgentHandoffAnimation
                        key={persistentHandoffInstanceKey || 'active-handoff'}
                        selectedAgent={persistentHandoffData.selectedAgent}
                        phase={persistentHandoffData.phase}
                        processingSteps={persistentHandoffData.processingSteps}
                        statusText={persistentHandoffData.statusText}
                        lastHandoff={persistentHandoffData.lastHandoff}
                      />
                    </div>
                  )}
                
                  {/* Loading message removed - using agent handoff animations in temporary messages instead */}
                </div>

                {/* In-Chat Learning Suggestion */}
                {learningSuggestion && (
                  <LearningSuggestion
                    suggestion={learningSuggestion.humanReadableSuggestion}
                    onConfirm={handleConfirmLearning}
                    onDismiss={handleDismissLearning}
                  />
                )}
                
                {/* Scroll anchor with padding for input */}
                <div ref={messagesEndRef} className="h-4" />
                {/* Load earlier prompt */}
                {visibleCount < messages.length && (
                  <div className="flex justify-center py-2">
                    <Button variant="ghost" size="sm" onClick={() => setVisibleCount((v) => Math.min(messages.length, v + 100))}>
                      Load earlier messages
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      {/* Clean Liquid Glass Input Area - Fixed at bottom */}
      <div className="flex-shrink-0 border-t border-border/50 bg-background/95 input-area-glass">
        <NeedsInputTray
          items={pendingInputItems}
          onAction={handlePendingInputAction}
        />
        {/* PHASE-13 START: Promote ChatInputV2 sitewide (no flag) */}
        {learningPrompt && (
          <div className="px-3 pb-2">
            <LearningEntryPrompt onSave={handleSaveLearningEntry} onSkip={handleSkipLearningEntry} />
          </div>
        )}
        {shouldShowPreferenceToggle && (
          <div className="px-3 pb-2">
            <PreferenceToggle checked={saveAsPreference} onChange={setSaveAsPreference} />
          </div>
        )}
        {showAttachmentPicker && (
          <div className="px-3 pb-2">
            <LiquidGlassCard className="p-3 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Run Context</p>
                  <p className="text-sm text-foreground">Attach context, review scope, and start a manifest-backed action.</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowAttachmentPicker(false)}>
                  Done
                </Button>
              </div>

              <div className="grid gap-2 lg:grid-cols-2">
                <div className="rounded-lg border border-border/40 bg-background/30 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Selected client</p>
                  <p className="mt-1 truncate text-sm font-medium text-foreground">
                    {activeClient?.name || 'General mode'}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {activeClient?.industry || 'No client-specific context'}
                  </p>
                </div>
                <div className="rounded-lg border border-border/40 bg-background/30 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Language</p>
                  <div className="mt-2 flex items-center rounded-lg border border-border/40 overflow-hidden w-fit">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLanguageChange('en')}
                      disabled={!currentConversation}
                      className={cn(
                        'rounded-none px-3 py-1 text-xs h-7 min-h-0',
                        lang === 'en' ? 'bg-primary/15 text-primary font-semibold' : 'text-muted-foreground'
                      )}
                    >
                      EN
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLanguageChange('ar')}
                      disabled={!currentConversation}
                      className={cn(
                        'rounded-none px-3 py-1 text-xs h-7 min-h-0',
                        lang === 'ar' ? 'bg-primary/15 text-primary font-semibold' : 'text-muted-foreground'
                      )}
                    >
                      AR
                    </Button>
                  </div>
                </div>
                <div className="rounded-lg border border-border/40 bg-background/30 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Attached artifact</p>
                  <p className="mt-1 truncate text-sm font-medium text-foreground">
                    {attachedArtifact ? sanitizeArtifactTitle(attachedArtifact.title, 'Untitled Artifact') : 'None'}
                  </p>
                  <label className={cn(
                    'mt-2 flex items-center gap-2 text-xs',
                    attachedArtifact ? 'text-foreground' : 'text-muted-foreground'
                  )}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border"
                      checked={artifactStrictScope}
                      disabled={!attachedArtifact}
                      onChange={(event) => setArtifactStrictScope(event.target.checked)}
                    />
                    Strict artifact scope
                  </label>
                </div>
                <div className="rounded-lg border border-border/40 bg-background/30 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Saved clarifications</p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-medium text-foreground">
                      {activeClarificationTypes.length
                        ? activeClarificationTypes.map(formatClarificationTypeLabel).join(', ')
                        : 'None'}
                    </p>
                    {clarificationCount > 0 && (
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowClarificationsDrawer(true)}>
                        View
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-border/40 bg-background/30 p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Selected source documents</p>
                <p className="mt-1 text-sm text-foreground">
                  {attachedSourceDocuments.length
                    ? attachedSourceDocuments.map((doc) => doc.title).join(', ')
                    : 'None'}
                </p>
              </div>

              <div className="rounded-lg border border-border/40 bg-background/30 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Active preferences</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {activePreferenceSummary.length
                        ? activePreferenceSummary.slice(0, 4).join(' · ')
                        : preferencesLoading
                          ? 'Loading preferences...'
                          : 'No active preference summary'}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openPreferencesDrawer()}>
                    Edit
                  </Button>
                </div>
                <PreferenceChips
                  preferences={clientPreferences}
                  provenance={preferenceProvenance}
                  onOpen={(field) => openPreferencesDrawer(field)}
                  className="mt-2"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="h-4 w-4 text-primary" />
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Quick actions</p>
                </div>
                {(quickActionRunningKind || quickActionError) && (
                  <div className={cn(
                    'rounded-lg border px-3 py-2 text-xs',
                    quickActionError
                      ? 'border-destructive/30 bg-destructive/10 text-destructive'
                      : 'border-primary/25 bg-primary/10 text-primary'
                  )}>
                    {quickActionError || 'Running graph-backed runtime tool in chat context...'}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {chatQuickActions.map((action) => (
                    <button
                      key={action.kind}
                      type="button"
                      disabled={action.disabled || !!quickActionRunningKind}
                      title={action.missingReason || action.manifest.description}
                      onClick={() => void handleQuickActionSelect(action)}
                      className={cn(
                        'inline-flex min-h-9 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                        action.disabled || quickActionRunningKind
                          ? 'cursor-not-allowed border-border/50 bg-muted/20 text-muted-foreground'
                          : 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15'
                      )}
                    >
                      <span>{quickActionRunningKind === action.kind ? `Running ${action.label}` : action.label}</span>
                      {action.disabled && action.missingReason && (
                        <span className="max-w-[140px] truncate text-[10px] font-normal">
                          {action.missingReason}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">Artifacts</p>
                {recentArtifactsForAttachment.length > 0 ? (
                  <div className="max-h-36 space-y-1 overflow-y-auto">
                    {recentArtifactsForAttachment.map((artifact) => {
                      const isAttached = attachedArtifact?.id === artifact.id;
                      return (
                        <button
                          key={artifact.id}
                          type="button"
                          onClick={() => {
                            const shouldDetach = attachedArtifact?.id === artifact.id;
                            setAttachedArtifact(shouldDetach ? null : artifact);
                            if (!shouldDetach) {
                              setActiveArtifact(artifact);
                            }
                          }}
                          className={cn(
                            'w-full rounded-lg border px-2.5 py-2 text-left text-sm transition-colors',
                            isAttached
                              ? 'border-primary/40 bg-primary/10 text-foreground'
                              : 'border-border/50 bg-background/30 text-muted-foreground hover:border-primary/30 hover:text-foreground',
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate">{sanitizeArtifactTitle(artifact.title, 'Untitled Artifact')}</span>
                            <span className="text-[11px] uppercase tracking-wide">
                              {isAttached ? 'Attached' : 'Attach'}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {attachmentArtifactsHydrating
                      ? 'Loading artifacts...'
                      : 'No artifacts available for this client yet.'}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">Client Documents</p>
                {availableSourceDocuments.length > 0 ? (
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border/40 bg-background/20 p-2">
                    {availableSourceDocuments.map((doc) => {
                      const checked = attachedSourceDocumentIds.includes(doc.id);
                      const statusLabel = doc.status && doc.status !== 'succeeded'
                        ? ` (${doc.status})`
                        : '';
                      return (
                        <label key={doc.id} className="flex items-start gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/30">
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 rounded border-border"
                            checked={checked}
                            onChange={() => toggleSourceDocumentAttachment(doc.id)}
                          />
                          <span className="min-w-0 truncate text-foreground">
                            {doc.title}
                            <span className="text-muted-foreground">{statusLabel}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {attachmentDocsHydrating
                      ? 'Loading client documents...'
                      : 'This client has no uploaded documents yet.'}
                  </p>
                )}
              </div>
            </LiquidGlassCard>
          </div>
        )}
        <ChatInputV2
          name="message"
          value={composerText}
          onValueChange={setComposerText}
          attachments={inputAttachments}
          onAttachmentClick={() => setShowAttachmentPicker((prev) => !prev)}
          attachmentButtonLabel="Manage run context"
          onSend={(text) => {
            try { (toast as any)?.info?.('Sending…'); } catch {}
            const transformPrompt = isArtifactTransformPrompt(text);
            const linkedFeedbackArtifact = artifactFeedbackContext
              ? artifacts.find((artifact) => artifact.id === artifactFeedbackContext.artifactId) || null
              : null;
            const conversationArtifactCandidates = transformPrompt
              ? filterArtifactsByReferencedSourceType(
                  messages
                    .map((message: any) => buildArtifactFromReferenceMessage(message, {
                      clientId: activeClient?.id || currentConversation?.clientId || undefined,
                      conversationId: currentConversation?.id || undefined,
                    }))
                    .filter((artifact): artifact is ArtifactContent => !!artifact)
                    .sort((a, b) => {
                      const aTime = a.lastUpdated?.getTime() || a.timestamp?.getTime() || 0;
                      const bTime = b.lastUpdated?.getTime() || b.timestamp?.getTime() || 0;
                      return bTime - aTime;
                    }),
                  text,
                )
              : [];
            const conversationFallbackArtifact = transformPrompt
              ? (
                  conversationArtifactCandidates[0]
                  || (activeArtifact?.sourceConversationId && activeArtifact.sourceConversationId === currentConversation?.id
                    ? activeArtifact
                    : null)
                  || null
                )
              : null;
            const transformFallbackArtifact = transformPrompt
              ? (
                  conversationFallbackArtifact
                    || activeArtifact
                    || (referencesLatestArtifact(text) ? recentArtifactsForAttachment[0] : null)
                    || (recentArtifactsForAttachment.length === 1 ? recentArtifactsForAttachment[0] : null)
                  || null
              )
              : null;
            const referencedSourceType = transformPrompt ? inferReferencedArtifactSourceType(text) : null;
            const promptMatchedFallback =
              !!transformFallbackArtifact
              && !!referencedSourceType
              && transformFallbackArtifact.type === referencedSourceType;
            const shouldPreferTransformFallback =
              !!transformFallbackArtifact
              && (
                referencesLatestArtifact(text)
                || (!!attachedArtifact && promptMatchedFallback && attachedArtifact.type !== referencedSourceType)
              );
            const effectiveArtifact = transformPrompt
              ? (
                  shouldPreferTransformFallback
                    ? (transformFallbackArtifact || attachedArtifact || linkedFeedbackArtifact || null)
                    : (attachedArtifact || transformFallbackArtifact || linkedFeedbackArtifact || null)
                )
              : (attachedArtifact || linkedFeedbackArtifact || transformFallbackArtifact || null);
            if (effectiveArtifact) {
              setActiveArtifact(effectiveArtifact);
            }

            const artifactAttachment = effectiveArtifact
              ? buildArtifactAttachmentContext(effectiveArtifact)
              : undefined;
            const contextAttachments = artifactAttachment || attachedSourceDocumentIds.length
              ? {
                  ...(artifactAttachment ? { artifact: artifactAttachment } : {}),
                  ...(artifactAttachment && (artifactStrictScope || transformPrompt) ? { artifactStrict: true } : {}),
                  ...(attachedSourceDocumentIds.length ? { sourceDocumentIds: attachedSourceDocumentIds } : {}),
                  ...(attachedSourceDocuments.length
                    ? {
                        sourceDocuments: attachedSourceDocuments.map((doc) => ({
                          id: doc.id,
                          title: doc.title,
                          ...(doc.type ? { type: doc.type } : {}),
                          ...(doc.url ? { url: doc.url } : {}),
                        })),
                      }
                    : {}),
                }
              : undefined;

            if (artifactFeedbackContext && linkedFeedbackArtifact && isFreeTextApproval(text)) {
              const clientId = linkedFeedbackArtifact.clientId || currentConversation?.clientId || activeClient?.id || '';
              if (!clientId) {
                toast.error('A client context is required to approve this draft.');
                return;
              }
              void (async () => {
                try {
                  let serverStatusUpdated = false;
                  try {
                    const persistedArtifact = await persistChatArtifactToContent(linkedFeedbackArtifact, { force: true });
                    const checkpointId = getArtifactCheckpointId(persistedArtifact || linkedFeedbackArtifact);
                    const payload = await fetchAuthedJson<ContentArtifactResponse>(
                      `/api/content/artifacts/${encodeURIComponent(linkedFeedbackArtifact.id)}/approve`,
                      {
                        method: 'POST',
                        body: JSON.stringify({
                          clientId,
                          ...(checkpointId ? { checkpointId } : {}),
                        }),
                      },
                    );
                    if (payload?.artifact) {
                      upsertArtifactFromServerRecord(payload.artifact);
                    }
                    serverStatusUpdated = true;
                  } catch (error) {
                    if (!isRequestFailedStatus(error, 404)) {
                      throw error;
                    }
                    applyLocalArtifactReviewState(linkedFeedbackArtifact, 'approved');
                  }
                  await sendMessage(text, undefined, {
                    kind: 'artifact_feedback_response',
                    artifactFeedback: {
                      artifactId: artifactFeedbackContext.artifactId,
                      ...(artifactFeedbackContext.lineageId ? { lineageId: artifactFeedbackContext.lineageId } : {}),
                      ...(artifactFeedbackContext.runId ? { runId: artifactFeedbackContext.runId } : {}),
                      ...(artifactFeedbackContext.promptMessageId ? { promptMessageId: artifactFeedbackContext.promptMessageId } : {}),
                      action: 'approve',
                    },
                    skipAgentRun: true,
                  });
                  setArtifactFeedbackContext(null);
                  setSaveAsPreference(false);
                  toast.success(serverStatusUpdated ? 'Draft approved.' : 'Draft approved locally.');
                } catch (error) {
                  console.error('[chat] free_text_approval_failed', error);
                  toast.error('Unable to approve this draft right now.');
                }
              })();
              return;
            }

            const attachedArtifactNeedsInlineContent = Boolean(
              effectiveArtifact
              && effectiveArtifact.content
              && effectiveArtifact.isSavedToLibrary !== true
            );
            if (effectiveArtifact) {
              void persistChatArtifactToContent(effectiveArtifact);
            }
            void sendMessage(text, undefined, {
              saveAsPreference,
              ...(artifactFeedbackContext && !transformPrompt
                ? {
                    kind: 'artifact_feedback_response',
                    artifactFeedback: {
                      artifactId: artifactFeedbackContext.artifactId,
                      ...(artifactFeedbackContext.lineageId ? { lineageId: artifactFeedbackContext.lineageId } : {}),
                      ...(artifactFeedbackContext.runId ? { runId: artifactFeedbackContext.runId } : {}),
                      ...(artifactFeedbackContext.promptMessageId ? { promptMessageId: artifactFeedbackContext.promptMessageId } : {}),
                      action: 'free_text' as const,
                    },
                  }
                : {}),
              ...(attachedArtifactNeedsInlineContent ? { previousContent: effectiveArtifact?.content } : {}),
              ...(contextAttachments ? { contextAttachments } : {}),
            });
            setArtifactFeedbackContext(null);
            setSaveAsPreference(false);
          }}
        />
        {/* PHASE-13 END */}
      </div>
      <ClarificationsDrawer
        isOpen={showClarificationsDrawer}
        onClose={() => setShowClarificationsDrawer(false)}
        conversationId={currentConversation?.id || ''}
        clarifications={currentConversation?.id ? storedClarifications[currentConversation.id] || {} : {}}
        onClearType={async (type) => {
          if (!currentConversation?.id) return;
          await clearClarifications(currentConversation.id, type);
        }}
        onClearAll={async () => {
          if (!currentConversation?.id) return;
          await clearClarifications(currentConversation.id, 'all');
        }}
      />
      <PreferencesDrawer
        isOpen={preferencesDrawerOpen}
        onClose={() => setPreferencesDrawerOpen(false)}
        preferences={clientPreferences}
        provenance={preferenceProvenance}
        onSave={handleSavePreferences}
        focusField={preferencesDrawerFocus}
      />
      <ReviewerDetailsDrawer
        feedback={selectedReviewerFeedback}
        isOpen={isReviewerDrawerOpen}
        onClose={() => setIsReviewerDrawerOpen(false)}
        onRequestChanges={
          selectedReviewerFeedback
            ? () => handleRequestChanges(selectedReviewerFeedback)
            : undefined
        }
      />
      {/* Mobile bottom nav spacer removed; compensate with padding on scroll area if needed */}

      {/* Jump to latest — compact arrow-down pill (positioned within chat area) */}
      <AnimatePresence>
        {showJumpToLatest && (
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.9 }}
            className="absolute right-4 bottom-24 sm:bottom-28 z-20"
          >
            <Button
              variant="ghost"
              size="icon-sm"
              className="bg-background/80 backdrop-blur-sm border border-border/40 shadow-lg hover:bg-background relative"
              onClick={() => {
                messagesEndRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'end' });
                setShowJumpToLatest(false);
                markSeenNow();
              }}
              title={unreadCount > 0 ? `${unreadCount} new messages` : 'Jump to latest'}
            >
              <ChevronDownIcon className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Jump to oldest — compact arrow-up pill (positioned within chat area) */}
      <AnimatePresence>
        {showJumpToOldest && (
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.9 }}
            className="absolute right-4 top-24 z-20"
          >
            <Button
              variant="ghost"
              size="icon-sm"
              className="bg-background/80 backdrop-blur-sm border border-border/40 shadow-lg hover:bg-background"
              onClick={() => {
                setVisibleCount(messages.length);
                listRef.current?.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
              }}
              title="Jump to oldest"
            >
              <ChevronUpIcon className="h-4 w-4" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      </div>

      {/* Keyboard shortcuts modal */}
      <AnimatePresence>
        {showShortcutHelp && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center"
            initial={prefersReducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowShortcutHelp(false)} />
            <div className="relative z-10 w-[90vw] max-w-md rounded-xl bg-background text-foreground border border-border shadow-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Keyboard Shortcuts</h3>
                <Button variant="ghost" size="icon-sm" onClick={() => setShowShortcutHelp(false)}>
                  <XMarkIcon className="h-4 w-4" />
                </Button>
              </div>
              <ul className="text-sm space-y-1">
                <li><span className="font-medium">f</span>: Toggle Focus Mode</li>
                <li><span className="font-medium">j</span>: Jump to latest</li>
                <li><span className="font-medium">n</span>: New chat</li>
                <li><span className="font-medium">?</span>: Toggle this help</li>
                <li><span className="font-medium">Enter</span>: Send • <span className="font-medium">Shift+Enter</span>: Newline</li>
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Writing Assistant Panel - Floating sidebar (avoid header overlap) */}
      {showWritingAssistant && writingContent.trim().length > 10 && (
        <div className="fixed right-4 w-80 max-w-[90vw] max-h-[60vh] z-30 top-20 sm:top-24 md:top-28 lg:right-6 lg:w-96">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowWritingAssistant(false)}
              className="absolute -top-2 -right-2 z-10 w-6 h-6 p-0 bg-background border border-border shadow-lg hover:bg-muted"
            >
              <XMarkIcon className="h-3 w-3" />
            </Button>
            <WritingAssistant
              content={writingContent}
              contentType={getContentType(writingContent)}
              clientId={activeClient?.id}
              onSuggestion={handleWritingSuggestion}
              onApplySuggestion={handleApplyWritingSuggestion}
              onContentChange={handleWritingContentChange}
              disabled={!showWritingAssistant}
              className="shadow-2xl border-2 border-border"
            />
          </div>
        </div>
      )}
      
      {/* Right Sidebar */}
      <AnimatePresence>
        {!props.disableRightSidebar && showSidebar && (
          <motion.div
            className="unified-sidebar absolute top-0 right-0 z-30 h-full max-w-full"
            style={{ width: isCompactViewport ? 'min(100vw, 420px)' : sidebarWidth }}
            initial={prefersReducedMotion ? false : { x: "100%" }}
            animate={{ x: 0 }}
            exit={prefersReducedMotion ? undefined : { x: "100%" }}
            transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 30 }}
          >
          {/* Resize Handle - Desktop only */}
          <div
            className="absolute left-0 top-0 bottom-0 w-1 bg-border/50 hover:bg-primary/40 cursor-col-resize hidden lg:block transition-colors"
            onMouseDown={handleResizeStart}
            ref={resizeRef}
          >
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-8 bg-muted-foreground/30 rounded-full" />
          </div>

          <motion.div
            className="h-full lg:pl-1 flex flex-col"
            initial={prefersReducedMotion ? false : { opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={prefersReducedMotion ? undefined : { opacity: 0, x: 20 }}
            transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.1, duration: 0.2 }}
          >
            <div className="flex h-full flex-col border-l border-border bg-background shadow-xl">
            {/* Panel Header */}
            <motion.div
              className="flex-shrink-0 border-b border-border/60 bg-background"
              initial={prefersReducedMotion ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, y: -10 }}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.15, duration: 0.2 }}
            >
              <div className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2">
                  <DocumentTextIcon className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">Artifacts</span>
                  {artifacts.length > 0 && (
                    <span className="px-2 py-1 text-xs font-bold bg-primary/20 text-primary rounded-full min-w-[20px] text-center">
                      {artifacts.length}
                    </span>
                  )}
                </div>
                <button
                  onClick={closeSidebar}
                  className="flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:h-9 sm:w-9"
                  aria-label="Close artifacts panel"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </motion.div>

            {/* Panel Content */}
            <motion.div 
              className="flex-1 overflow-hidden"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, y: 10 }}
              transition={prefersReducedMotion ? { duration: 0 } : { delay: 0.2, duration: 0.2 }}
            >
              {sidebarPanel === 'artifacts' && (
                <>
                  {activeArtifact ? (
                    <div className="h-full min-h-0 overflow-hidden flex flex-col">
                      <div className="max-h-[45%] flex-shrink-0 overflow-y-auto border-b border-border/50 px-3 py-3">
                        <ArtifactGraphCompanionPanel
                          artifact={activeArtifact}
                          onOpenHistory={() => setArtifactHistoryOpenSignal((value) => value + 1)}
                        />
                      </div>
                      <ContentArtifact
                        artifact={activeArtifact}
                        openVersionHistorySignal={artifactHistoryOpenSignal}
                        onClose={() => setActiveArtifact(null)}
                        onUpdate={(content) => {
                          const beforeText = activeArtifact.content || '';
                          updateArtifact(activeArtifact.id, content);
                          void handlePreferenceDiffExtraction({
                            beforeText,
                            afterText: content,
                            runId: activeArtifact.runId || activeArtifact.supervisorMeta?.runId,
                            messageId: activeArtifact.id,
                            source: 'content_artifact_edit',
                          });
                        }}
                        onPolicyIssuesUpdate={(issues, updatedContent) => {
                          setArtifacts(prev => prev.map(a => (
                            a.id === activeArtifact.id
                              ? {
                                  ...a,
                                  policyIssues: issues,
                                  ...(updatedContent ? { content: updatedContent, lastUpdated: new Date() } : {}),
                                }
                              : a
                          )));
                          setActiveArtifact({
                            ...activeArtifact,
                            policyIssues: issues,
                            ...(updatedContent ? { content: updatedContent, lastUpdated: new Date() } : {}),
                          });
                        }}
                        onApprove={(id, status, feedback) => {
                          const reviewer = currentUser?.displayName || currentUser?.email || undefined;
                          // Handle approval status update
                          setArtifacts(prev => prev.map(a =>
                            a.id === id
                              ? {
                                  ...a,
                                  approvalStatus: status,
                                  feedback,
                                  approvedBy: reviewer,
                                  approvedAt: new Date()
                                }
                              : a
                          ));

                          // Update active artifact if it's the one being approved
                          if (activeArtifact.id === id) {
                            setActiveArtifact({
                                  ...activeArtifact,
                                  approvalStatus: status,
                                  feedback,
                                  approvedBy: reviewer,
                                  approvedAt: new Date()
                                });
                          }
                        }}
                        onSaveToLibrary={(id) => {
                          // Mark artifact as saved
                          setArtifacts(prev => prev.map(a =>
                            a.id === id
                              ? {
                                  ...a,
                                  isSavedToLibrary: true,
                                  savedToLibraryAt: new Date()
                              }
                            : a
                          ));

                          // Update active artifact if it's the one being saved
                          if (activeArtifact.id === id) {
                            setActiveArtifact({
                                  ...activeArtifact,
                                  isSavedToLibrary: true,
                                  savedToLibraryAt: new Date()
                                });
                          }
                        }}
                        activeClient={activeClient}
                        user={currentUser}
                        className="min-h-0 flex-1"
                      />
                    </div>
                  ) : artifacts.length > 0 ? (
                    <div className="h-full flex flex-col">
                      {/* Artifacts List */}
                      <div
                        className="flex-1 overflow-y-auto p-3 space-y-2"
                        ref={artifactsListRef}
                        onScroll={() => {
                          const el = artifactsListRef.current;
                          if (!el) return;
                          if (el.scrollTop < 100 && artifactVisibleCount < artifacts.length) {
                            setArtifactVisibleCount((v) => Math.min(artifacts.length, v + 50));
                          }
                        }}
                      >
                        <p className="text-xs text-muted-foreground/60 px-1 mb-1">
                          {artifacts.length} draft{artifacts.length !== 1 ? 's' : ''} created
                        </p>
                        {(() => {
                          const start = Math.max(0, artifacts.length - artifactVisibleCount);
                          const slice = artifacts.slice(start);
                          const getArtifactEdgeColor = (type: string) => {
                            const colors: Record<string, string> = {
                              press_release: 'border-l-primary',
                              social_media: 'border-l-secondary',
                              email: 'border-l-success',
                              statement: 'border-l-destructive',
                              content: 'border-l-warning',
                              media_pitch: 'border-l-primary',
                              outreach_plan: 'border-l-primary',
                              thought_leadership: 'border-l-info',
                            };
                            return colors[type] || 'border-l-muted-foreground';
                          };
                          const getApprovalDot = (status?: string) => {
                            if (!status || status === 'pending') return null;
                            const dotColor = status === 'approved' ? 'bg-success' : status === 'needs_revision' ? 'bg-warning' : 'bg-destructive';
                            return <span className={cn('w-2 h-2 rounded-full flex-shrink-0', dotColor)} />;
                          };
                          return slice.map((artifact, idx) => (
                            <motion.button
                              key={artifact.id}
                              onClick={() => setActiveArtifact(artifact)}
                              initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={prefersReducedMotion ? { duration: 0 } : { delay: idx * 0.03, duration: 0.15 }}
                              className={cn(
                                'group/card min-h-[88px] w-full text-left p-3 artifact-list-card border-l-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                getArtifactEdgeColor(artifact.type)
                              )}
                            >
                              <div className="flex items-start gap-3">
                                <div className={cn(
                                  'flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-sm',
                                  getArtifactColor(artifact.type)
                                )}>
                                  {React.createElement(getArtifactIcon(artifact.type), { className: 'h-4 w-4' })}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-sm text-foreground leading-tight break-words flex-1">
                                      {sanitizeArtifactTitle(artifact.title, 'Untitled Artifact')}
                                    </h4>
                                    {getApprovalDot(artifact.approvalStatus)}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                    {stripMarkdown(artifact.content).substring(0, 100)}...
                                  </p>
                                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                    <span>{new Intl.DateTimeFormat('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }).format(artifact.timestamp)}</span>
                                    <span>·</span>
                                    <span>{artifact.content.length} chars</span>
                                    {artifact.version > 1 && (
                                      <>
                                        <span>·</span>
                                        <span className="text-primary">v{artifact.version}</span>
                                      </>
                                    )}
                                    {artifact.isEdited && (
                                      <>
                                        <span>·</span>
                                        <span className="text-warning">Edited</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.button>
                          ));
                        })()}
                        {artifactVisibleCount < artifacts.length && (
                          <div className="flex justify-center py-2">
                            <Button variant="ghost" size="sm" onClick={() => setArtifactVisibleCount((v) => Math.min(artifacts.length, v + 100))}>
                              Load earlier drafts
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center p-8">
                      <div className="rounded-xl border border-border/60 bg-background p-6 text-center">
                        <DocumentTextIcon className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                        <h3 className="font-medium text-foreground mb-1">No artifacts yet</h3>
                        <p className="text-sm text-muted-foreground">
                          AI-generated content will appear here as interactive artifacts you can edit, approve, and publish.
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Removed SEO, Insights, and Analytics panels */}

              {sidebarPanel === 'migration' && (
                <div className="h-full overflow-y-auto p-4">
                  <MigrationTrigger
                    onComplete={() => {
                      // After migration completes, refresh the current conversation
                      // Return to artifacts after migration
                      setSidebarPanel('artifacts');
                      // Optionally reload the conversation to see the enhancement
                      if (currentConversation?.id) {
                        loadConversation(currentConversation.id);
                      }
                    }}
                    className="h-full"
                  />
                </div>
              )}
            </motion.div>
            </div>
          </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default ChatWindow;
